/** Pânza editorului: desenul comun, plus apucătoarele și gesturile de editare.
 *
 *  DESENUL NU E AL EI. Îl face `tronson/Desen.tsx`, același pe care îl arată și
 *  vizualizatorul — planșă tehnică, hașură după rolul zidului, goluri ca
 *  ștersături. Aici se adaugă doar ce ține de EDITAT: apucătoarele, cifra care
 *  se vede cât ține gestul, cotele golurilor de pe peretele ales.
 *
 *  CE POATE FACE MÂNA:
 *    * click pe perete / cameră / gol -> selectează (o ușă NU se închide la
 *      click — sensul ei se întoarce din bara de context, starea nu există);
 *    * tragi de CORPUL unui perete, ales sau nu -> se MUTĂ întreg, singur,
 *      liber în plan, și se lipește de vecinul de pe aceeași direcție; nu-și
 *      cară vecinii de colț (v. `model.mutaPereteleSingur`, `model.lipirea`);
 *    * peretele ALES are un CADRU: laturile lungi trase schimbă grosimea,
 *      cele scurte lungimea, colțurile amândouă; capătul se lipește de FAȚA
 *      vecinului, iar grosimea de GROSIMEA lui (v. `model.redimPeretele`,
 *      `model.grosimeLipita`);
 *    * unealta «perete»: DOUĂ FELURI, amândouă îngăduite — apeși și tragi și
 *      ridici degetul, sau apeși o dată ca să începi și încă o dată ca să
 *      termini. Cursorul e un semn de perete și atât: nu se lipește de nimic
 *      înainte de prima apăsare. Zidul desenat se lipește însă de fețele
 *      zidurilor din stânga și din dreapta și iese perpendicular pe ele (v.
 *      `model.pereteNouLipit`); la sfârșit e ales, iar unealta se întoarce la
 *      săgeată;
 *    * CAMERA NU SE MUTĂ ȘI NU SE ROTEȘTE. Corpul ei se apasă și atât — se
 *      alege, se vopsește, se mobilează. Singurul gest care o schimbă e tras de
 *      una dintre MARGINILE ei, iar acela împinge zidul de pe latura aia (v.
 *      `model.mutaLaturaCamerei`). De ce: o cameră e golul dintre ziduri (v.
 *      antetul lui `camere.ts`), deci ce se mișcă sunt zidurile. O cameră
 *      «trasă» întreagă, sau întoarsă, cară cu ea zidurile din jur peste
 *      vecinele ei și lasă un plan pe care nu-l mai vrea nimeni. Cerut explicit,
 *      17.09.2026;
 *    * uneltele «ușă» / «fereastră»: apeși pe un zid -> un gol în el;
 *    * mijlocul mouse-ului, dreapta sau SPAȚIU + tragere -> panoramare;
 *      rotița mărește, W/A/S/D și săgețile mută, `0` reîncadrează — exact ca în
 *      vizualizatorul de arhitectură.
 *
 *  CÂT ȚINE GESTUL SE VEDE CIFRA. Fără ea tragi până arată bine, ceea ce pe un
 *  plan de arhitectură nu e o măsură.
 *
 *  ȘI CAMERELE SE REFAC LIVE. Un gest care mută ziduri cere `laSchimbare(d,
 *  true)`: gazda rederivă camerele din pereți la fiecare mișcare (10–14 ms pe
 *  M5, măsurat), deci ariile și cotele de pe planșă se rescriu SUB MÂNĂ, nu la
 *  ridicarea degetului. Gesturile care nu ating ziduri — mobila, aparatele —
 *  nu plătesc derivarea.
 *
 *  ORICE OBIECT ALES ÎȘI ARATĂ DISTANȚELE: cât e până la primul obstacol în
 *  cele patru direcții, cu o cotă pe fiecare porțiune cu alt vecin (v.
 *  `cote::coteleObiectului`) — un perete, o ușă, o priză și o piesă de
 *  mobilier răspund la fel. În culoarea selecției, fiindcă sunt măsuri de
 *  lucru, nu ale planșei.
 *
 *  ȘI SE POT SCRIE: apeși pe cifră, tastezi alta, iar obiectul pleacă exact cât
 *  să lase golul cerut. Mutarea e chiar cea a mâinii (`model::mutaObiectul`),
 *  deci un zid dus din cifră își duce mobila, ușile și prizele întocmai ca unul
 *  tras de corp. E singurul fel în care se poate spune «ușa la 15 cm de colț»
 *  pe o planșă la 37 px/m, unde un centimetru e o treime de pixel.
 */
import { useCallback, useEffect, useMemo, useRef, useState,
         type PointerEvent as PointerEventReact,
         type ReactNode } from "react";
import { fitBbox, type View } from "../../view";
import { deriva, TOC_IMPLICIT, type Gol, type ModelUsa, type Pt,
         type PlanTronson, type TronsonDoc, type VarfAfin } from "../doc";
import Desen, { HARTIE, SEL, SEL_MOALE, simbolUsa, TUS_CULOARE }
  from "../Desen";


import { COTA_PX, capeteleFetei, cheiaCotei, coteleObiectului, cutiaAlesului,
         gazdaDozei, locPeFata, punctulCifrei, type Cota, type LantDoza }
  from "../cote";
import { Cote, CotaDesen } from "../CoteDesen";
import Arata from "./Arata";
import Calcul from "../Calcul";
import type { Calc } from "../calc";
import { cuDeplasari, useTranzitie, type Aratare } from "./aratare";

/** Culoarea axelor de trasare: cărămiziul planșei, stins. Se citesc când le
 *  cauți și se pierd în fundal când nu. */
/** Cât de departe de un perete se mai prinde o piesă PUSĂ cu mâna, în metri.
 *  Mult peste raza de la tragere — v. `piesaLipita`. */
const RAZA_PUNERE = 0.5;

const AX_CULOARE = "#c2554a";

/** CÂT IESE APARATUL AXELOR ÎN AFARA CONTURULUI — și de ce sunt două unități.
 *
 *  `AX_IESIRE` e în METRI: atâta trec liniile de trasare dincolo de contur, și
 *  pe capătul lor stau discurile cu litera sau cifra axei. Se scalează cu
 *  desenul, ca orice altă măsură a lui.
 *
 *  `AX_COTA_PX` e în PIXELI: atâta stă cifra dintre două axe mai departe decât
 *  discurile, la fel ca orice cotă, care se scrie la o depărtare de ECRAN, nu
 *  de clădire. `AX_MARGINE_PX` o adună cu jumătatea ei de înălțime (9 px), ca
 *  să dea rezerva de care are nevoie încadrarea.
 *
 *  Stau aici, împreună, fiindcă le citesc DOUĂ locuri care nu se pot certa:
 *  desenul, care le așază, și încadrarea, care trebuie să le cuprindă. */
const AX_IESIRE = 0.9;
const AX_COTA_PX = 26;
const AX_MARGINE_PX = AX_COTA_PX + 9;

/** TĂIETURILE UNUI ZID ALES, în lungul lui: de la capăt la gol, golul, cât
 *  rămâne — cotele albastre de pe peretele selectat, ca în Homestyler.
 *
 *  GOLUL E AL ZIDULUI DACĂ STĂ PE EL, nu dacă îl numește. Într-o planșă
 *  importată zidurile vin deja TĂIATE la goluri, iar `wall` numește un ciot de
 *  alături: pe M1, 14 din cele 16 goluri au mijlocul în afara zidului al cărui
 *  nume îl poartă, iar peretele W32 (1,60 m, x 3,20–4,80) poartă numele a două
 *  uși de 0,90 m aflate la 2,85 și la 5,35. Luate ca atare, tăieturile ieșeau
 *  din zid în amândouă părțile și scriau «0,90 — 1,60 — 0,90» peste vecini:
 *  aceleași uși cotate a doua oară, pe un zid prin care nu trec.
 *
 *  FĂRĂ NICIUN GOL NU SE SCRIE NIMIC. Tăietura ar fi una singură — zidul
 *  întreg —, iar cifra ei e chiar lungimea pe care planșa o scrie deja pe fața
 *  aceluiași zid, la zece pixeli mai încolo: aceeași măsură de două ori, o dată
 *  albastră și o dată neagră. Ce n-are ce despărți nu se desparte.
 */
export function taieturileZidului(
  goluri: readonly Gol[], zid: string,
  osia: ReturnType<typeof osiaPeretelui>,
): [number, number][] {
  const ax = osia.lungPeX ? 0 : 1;
  const [s0, s1] = osia.span;
  const pe = goluri
    // capetele intră: unealta așază golul chiar pe capătul zidului când se
    // apasă dincolo de el (v. `model::pozitiaGolului`)
    .filter((g) => g.perete === zid
                && g.c[ax] >= s0 - 1e-6 && g.c[ax] <= s1 + 1e-6)
    .map((g) => ({ t0: Math.max(s0, Math.min(g.a[ax], g.b[ax])),
                   t1: Math.min(s1, Math.max(g.a[ax], g.b[ax])) }))
    .sort((a, b) => a.t0 - b.t0);
  if (pe.length === 0) return [];
  const out: [number, number][] = [];
  let t = s0;
  for (const g of pe) {
    out.push([t, g.t0]);
    out.push([g.t0, g.t1]);
    t = g.t1;
  }
  out.push([t, s1]);
  // ce e mai scurt decât un centimetru nu e o măsură, e un rest de rotunjire
  return out.filter(([a, b]) => b - a > 0.02);
}
import { K_MAX_TRONSON, ReperXY, laLume, useGesturi, useIncadrare } from "../Panza2D";
import { felulPiesei, piesaSingura } from "../../plan/arhMobilierPlan";
import { scaraDinRampe, contratrepteDinLungime, lungimeDinContratrepte,
         lungimileScarii, rampaPeTrepte, treaptaNivelului, verificaScara,
         felulScarii, pasulScarii, laturaSpre, laturaDinVector, departareaPe,
         rampaUrmatoare, sensulImplicit, VERSOR, TREAPTA as TREAPTA_SCARA,
         DIST_IMPLICITA, RAMPA as LATIME_RAMPA, RAMPA_MIN, TREPTE_MIN,
         type StareScara } from "../../plan/scara";
import { cutiaScarii, ScaraDinRampe } from "../../plan/ScaraDesen";
import {
  axeleZidurilor, axulPeretelui, balconulSePrinde, curbeazaPeretele,
  lipitDeZid,
  distanteleAxelor, indreaptaPeretele, lipesteDoza, LUNGIME_MINIMA_BANDA, mutaAparat,
  mutaCapatBanda, mutaCapatulSingur,
  unghiulPeretelui, conturCamerei, mutaMobilaMulte, mutaScaraIntreaga,
  adaugaVarfPodest, conturulPodestului, lungimeaAxei,
  tragePodestul,
  scoateVarfPodest, ceCuprinde, mutaCuprinsul, stergeCuprinsul,
  type Fereastra,
  imparteZidulLaCamera,
  mutaPeretele, mutaPereteleSingur, lipirea, lipiteleLui, totPeretele, pereteNouLipit, cadrulPartii,
  mutaNucleul,
  redimPeretele, cadrulPeretelui, rotestePeretele, type Prindere,
  mutaParteaNucleului, parteaSeCroieste, redimGolul, redimMobila,
  redimNucleul, redimParteaNucleului,
  mutaColtulConturului, mutaLaturaConturului, taieLaturaConturului,
  axele, mutaAxul, normalaAxei, punctulAxei, peAxa, intindereaAxei,
  copiazaCuprinsul, demoleazaBucata, lipesteBucata, mutaGolulLiber, r3,
  oglindesteBucata,
  rotesteBucata,
  type Bucata,
  cutiaDesenului,
  type Ax,
  osiaPeretelui, pozitiaGolului, schimbaAparat, 
  schimbaMobila, mutaObiectul, rampelePresetului,
  type Ales, type FormaCamera, type Apucare,
  COLTURI, LATURI,
  laturileCamerei, lipestePeFata, mutaLaturaCamerei, type LaturaCamerei,
} from "./model";
import type { FiltruFereastra } from "./model";
import { editable, layerOfPiece, layersOfItem, toateEditabile, type Layere } from "./layers";
import {
  cutiaPiesei, lipesteCutia, muchiileDinJur,
  lipesteUnghiul, unghiurileDinJur, razaSnap, lipesteZidul, SNAP_UNGHI, type Cutie,
  type Muchie, type FataPornirii, fetelePornirii, directiaScarii, pornireaScarii,
  LIPIRE_SCARA,
} from "./aliniere";
import { aparatul, ePePerete, marimeaSimbolului } from "../design/electric";
import { trasee as calculeazaTrasee } from "../design/electric";
import { drawnDiameter, simbolElectric } from "../design/ElectricDesen";
import { finisajul } from "../design/finisaje";
import { inInel as inCamera } from "../doc";
import { FEL_IMPLICIT } from "../feluri";
import { useT } from "../../../i18n";

export type Unealta = "sageata" | "perete" | "arc" | "parapet" | "balcon"
  | "usa" | "usaDubla" | "usaGlisanta" | "fereastra"
  | "demoleaza"
  | "camera" | "cameraL" | "cameraT" | "cameraLibera" | "zonaDesfumare"
  /** CAMERA FĂRĂ PEREȚI: conturul tras devine cameră pe loc, fără să zidească
   *  nimic în jurul ei, iar felul se alege pe urmă din panou. Cerut de arhitect
   *  (22.10.2026): «să trasez camera și să-mi apară perimetrul, și să setez
   *  tipul camerei FĂRĂ să am mereu pereți acolo». Celelalte patru feluri de
   *  cameră zidesc conturul (v. `model::adaugaCameraContur`); zona de desfumare
   *  nu zidea nici ea, dar își scria felul singură. */
  | "cameraFaraPereti"
  /** ÎNTINDEREA (tasta `S`): o fereastră peste plan, apoi tragi. V.
   *  `model::mutaCuprinsul`. */
  | "intinde"
  /** COTA PUSĂ DE MÂNĂ: două apăsări, de la un punct la altul. */
  | "cota"
  | "scara" | "lift" | "ghena" | "mobila"
  /** SCĂRILE DE-A GATA. Nu sunt un al doilea fel de scară: pun exact rampele pe
   *  care le-ar fi desenat mâna, deci ce rămâne e editabil ca orice scară trasă
   *  (v. `model::PRESETURI_SCARA`). */
  | "scaraGata_dreapta" | "scaraGata_dreapta_podest"
  | "scaraGata_u" | "scaraGata_u_doua"
  /** Modul Design: pune un aparat electric, sau vopsește o suprafață. */
  | "aparat" | "pensula";

/** Textul laturii alese. Chei LITERALE, nu compuse din bucăți: compuse, nu se
 *  mai pot număra la extragerea traducerilor și se pierd tăcut. */
/** Cât trebuie tras de pe podest ca rampa să-și schimbe sensul. O jumătate de
 *  metru: cursorul stă chiar în colț când se apasă podestul, iar acolo cele
 *  patru sferturi se ating — măsurat la încadrarea obișnuită a editorului
 *  (29,6 px/m) o tremurătură de un pixel sărea dintr-un sfert în altul. */
const SCHIMBA_SENS = 0.5;

/** A strip's length under the cursor: along its face from its start, inside
 *  the face's stops. */
function lungimeaBenzii(g: { a: Pt; rot: number; min: number; max: number }, p: Pt): number {
  const rad = (g.rot * Math.PI) / 180;
  const L = (p[0] - g.a[0]) * Math.sin(rad) - (p[1] - g.a[1]) * Math.cos(rad);
  return Math.max(g.min, Math.min(g.max, L));
}

/** Încotro urcă rampa care se așază acum — scris sub cursor, ca omul să vadă
 *  ce alege înainte s-o pună. */
const SENS_TEXT = {
  sus: "panza.scara_sens_sus", jos: "panza.scara_sens_jos",
  stanga: "panza.scara_sens_stanga", dreapta: "panza.scara_sens_dreapta",
} as const;

const LATURA_TEXT = {
  sus: (t: (k: "panza.scara_latura_sus") => string) => t("panza.scara_latura_sus"),
  jos: (t: (k: "panza.scara_latura_jos") => string) => t("panza.scara_latura_jos"),
  stanga: (t: (k: "panza.scara_latura_stanga") => string) =>
    t("panza.scara_latura_stanga"),
  dreapta: (t: (k: "panza.scara_latura_dreapta") => string) =>
    t("panza.scara_latura_dreapta"),
} as const;

/** Uneltele care trag o cameră întreagă dintr-o mișcare. */
const FORME: Unealta[] = ["camera", "cameraL", "cameraT"];

/** FELUL CU CARE SE NAȘTE O CAMERĂ FĂRĂ PEREȚI, pe unealtă — sau `null` când
 *  conturul tras se zidește, ca la celelalte feluri de cameră.
 *
 *  `FEL_IMPLICIT` («room») pentru camera oarecare, fiindcă din formă nu se
 *  vede ce e, iar un fel ghicit ar fi o afirmație pe care n-o face nimeni:
 *  omul îl alege din panou, unde oricum stă lista (v. `Proprietati`,
 *  `editor.camera_fel`). Desfumarea și-l știe pe-al ei. */
const felulZonei = (u: Unealta): string | null =>
  u === "zonaDesfumare" ? "desfumare"
    : u === "cameraFaraPereti" ? FEL_IMPLICIT : null;

/** UNELTELE DE UȘĂ, cu modelul pe care îl scriu și lățimea cu care pornesc:
 *  0,90 e lumina obișnuită a unei uși de cameră, 1,40 a unei uși duble. Trei
 *  unelte și nu una cu un selector, ca modelul să se vadă în fantomă înainte
 *  de a apăsa. */
const USI: Partial<Record<Unealta, { model: ModelUsa; latime: number }>> = {
  usa: { model: "single", latime: 0.9 },
  usaDubla: { model: "double", latime: 1.4 },
  usaGlisanta: { model: "sliding", latime: 0.9 },
};

/** Pasul la care se lipesc mutările. 5 cm: cât să nu se simtă la scara
 *  ecranului și cât să lase cifrele citibile — un plan tras din mână iese cu
 *  pereți la 2.847 m, iar apoi cineva citește 2.85 pe desen și 2.847 în
 *  document. */
const PAS = 0.05;

/** CÂT POATE FI STRÂMB UN ZID ȘI SĂ FIE TOT «DREPT», ca săgeată a capătului.
 *  V. nota lungă din `trageCorpulZidului`. */
const DREPT_MM = 0.01;
const rotunj = (v: number) => Math.round(v / PAS) * PAS;

/** CURSORUL DE REDIMENSIONARE potrivit unei direcții, în octante.
 *
 *  Pe capătul unui gol, cursorul era «move» — semnul că obiectul PLEACĂ, în
 *  timp ce apucătoarea aia îl LUNGEȘTE. Cerut de arhitect (21.10.2026): «nu aș
 *  vrea să-mi apară cursorul de move ci cel cu 2 săgeți sus-jos sau
 *  stânga-dreapta, în funcție de orientare».
 *
 *  Opt direcții, patru cursoare, ca la mânerele mobilei: un gol pe o fațadă
 *  oblică nu e nici orizontal, nici vertical, iar săgeata dreaptă pe el ar
 *  minți la fel ca «move». `y` crește în JOS în SVG, deci un unghi pozitiv
 *  coboară spre dreapta — adică `nwse`. */
const cursorulPeDirectie = (ux: number, uy: number): string => {
  const g = (((Math.atan2(uy, ux) * 180) / Math.PI) % 180 + 180) % 180;
  if (g < 22.5 || g >= 157.5) return "ew-resize";
  if (g < 67.5) return "nwse-resize";
  if (g < 112.5) return "ns-resize";
  return "nesw-resize";
};

/** CURSORUL UNUI MÂNER DE REDIMENSIONARE, întors odată cu piesa.
 *
 *  `sx`/`sy` spun încotro trage mânerul în cadrul PIESEI; mânerele se
 *  desenează însă într-un grup `rotate(deg)`, deci pe ecran direcția e alta.
 *  Alese din cadrul local, săgețile ieșeau perpendiculare pe ce scalau la
 *  orice piesă așezată la 90° — adică la corpul de bucătărie, dulapul și
 *  rafturile, care stau lipite de perete.
 *
 *  Se întoarce vectorul și se cade pe aceleași patru cursoare ca golurile
 *  (v. `cursorulPeDirectie`). `rotate` al SVG-ului e în sensul acelor de
 *  ceasornic, cu `y` în jos — aceeași convenție ca acolo. */
const cursorulIntors = (sx: number, sy: number, rot: number): string => {
  const r = (rot * Math.PI) / 180;
  const cs = Math.cos(r), sn = Math.sin(r);
  return cursorulPeDirectie(sx * cs - sy * sn, sx * sn + sy * cs);
};

// ====================== TRAGEREA DE CORPUL UNUI ZID ========================
//
// Gestul stă AICI, afară din componentă, fiindcă e singurul fel în care poate
// fi probat pe drumul mâinii. Ținut în `laMiscare`, el se putea verifica doar
// chemând pe rând bucățile din care e făcut — `imparteZidulLaCamera`,
// `lipesteZidul`, `mutaPereteleSingur` —, iar o probă care cheamă regula, nu
// drumul omului, trece în timp ce planșa e stricată: exact așa a trecut
// `imparteFatada.test.ts` peste o tăiere pe care runul o lega la loc.
//
// Componenta nu face aici decât ce chiar e al ei: citește cursorul, ține
// mărirea și desenează ghidajele.

/** CE SE ȚINE MINTE DE LA APĂSARE: documentul tăiat la hotarul camerei și
 *  bucățile care pleacă. V. `apucaCorpulZidului`. */
export type ApucareaZidului = {
  /** Documentul de la apăsare, cu linia deja tăiată. */
  doc0: TronsonDoc;
  /** Planul derivat din el — zidurile de care se lipește stau pe loc. */
  plan0: PlanTronson;
  /** Bucata de sub deget. */
  i: number;
  /** Punctul apucat. */
  de: Pt;
  /** Tot ce pleacă din gestul ăsta, aflat o dată, la apucare. */
  grup: readonly number[];
  /** Zidul apucat e o COPIE PROASPĂTĂ, încă în mână: trasă, ea nu-și lungește
   *  vecinii (v. `Masa::dublateAcum`). */
  proaspat: boolean;
};

/** APĂSAREA PE CORPUL UNUI ZID.
 *
 *  `alese` = pereții aprinși când a început gestul; dacă zidul apucat e între
 *  ei, toți pleacă odată. */
export function apucaCorpulZidului(
  doc: TronsonDoc, plan: PlanTronson, i: number, punct: Pt,
  alese?: readonly number[] | null,
  /** ⇧ — se apucă LINIA ÎNTREAGĂ, fără tăierea la cameră (v. mai jos). */
  intreg = false,
  /** Zidurile tocmai dublate — v. `ApucareaZidului.proaspat`. */
  dublate?: readonly number[] | null,
): ApucareaZidului {
  // ÎNTÂI SE TAIE ÎN DREPTUL CAMEREI APUCATE (v. `imparteZidulLaCamera`).
  // O fațadă merge pe lângă mai multe camere; trasă întreagă, ea o ducea și
  // pe vecina pe care n-a atins-o nimeni. Tăierea se face ACUM, la apucare,
  // fiindcă punctul apucat e cel care spune despre ce cameră e vorba — odată
  // ce mâna a plecat, nu se mai știe de unde.
  //
  // ȘI GRUPUL VINE DE-ACOLO, nu dintr-o căutare pe documentul tăiat.
  // `totPeretele` merge prin noduri, iar cele două jumătăți ale tăieturii se
  // ating cap la cap: căutat din nou, runul le lega la loc și tăierea nu
  // însemna nimic. Cine taie știe cel mai bine ce a rămas de partea mâinii —
  // foile de-alături (miez plus termoizolație) și bucățile despărțite de
  // ferestre, toate retezate la același hotar.
  //
  // NIMIC NU INTRĂ ÎN ISTORIC DINTR-UN CLIC: tăierea stă în `doc` și ajunge în
  // document abia la prima mișcare, odată cu mutarea (v. `Gest.pornit`).
  // Bucata trasă rămâne pe indicele `i`, deci gestul și selecția rămân
  // valabile.
  // CU CAMERELE DERIVATE: ele spun cât ține latura apucată, iar fără ele
  // tăierea cade pe compartimentările rezemate în linie — și pe o fațadă
  // lungă asta poate fi mult mai puțin decât camera (v. `imparteZidulLaCamera`).
  // ⇧ SARE TĂIEREA: zidul pleacă ÎNTREG, pe toată lungimea lui, cu camerele
  // de pe amândouă părțile refăcute din el. Cerut de arhitect (20.10.2026):
  // «sometimes we have a long wall across multiple rooms and if we press on a
  // portion of it and try to drag it it only moves that portion (...) we
  // should have the option to hold shift and be able to move the whole wall
  // across all the rooms and have the rooms update accordingly». Tăierea la
  // cameră rămâne gestul obișnuit, fiindcă de obicei chiar o cameră se
  // schimbă; Shift e pentru linia toată.
  const { doc: taiat, grup: subMana } = intreg
    ? { doc, grup: totPeretele(doc, i) }
    : imparteZidulLaCamera(doc, i, punct, plan.camere);
  // SELECȚIA DE GRUP TRAGE LINII ÎNTREGI, nu bucăți: cine a aprins anume cinci
  // pereți i-a aprins întregi, iar tăierea la cameră e răspunsul la o apucare,
  // nu la o alegere. Numai zidul de sub deget se confinează.
  const grup = [...new Set([
    ...subMana,
    ...((alese ?? []).includes(i)
      ? (alese ?? []).filter((x) => x !== i)
          .flatMap((x) => [...totPeretele(taiat, x)])
      : [])])];
  return { doc0: taiat, plan0: taiat === doc ? plan : deriva(taiat), i,
           de: punct, grup, proaspat: !!dublate?.includes(i) };
}

/** DEPLASAREA UNUI ZID TRAS DE CORP, cu lipirea adăugată. `brut` e cât a mers
 *  mâna, nerotunjit. Un zid oblic sau curb n-are muchii de lipit: merge pe pas
 *  și se lipește doar pe fețe, ca înainte (v. `model::lipirea`).
 *
 *  `grup` e TOT ce pleacă din gestul ăsta, nu doar foile de-alături: după
 *  tăierea la hotarul camerei (v. `model::imparteZidulLaCamera`), ciotul rămas
 *  pe loc stă pe ACELEAȘI fețe ca bucata trasă, deci lipirea l-ar fi luat
 *  drept vecin și ar fi tras-o înapoi la zero. Cine pleacă odată cu tine nu e
 *  o țintă. */
export function zidulLipit(
  plan0: PlanTronson, i: number, doc0: TronsonDoc, brut: Pt, raza: number,
  grupul?: readonly number[],
): { dep: Pt; doc: TronsonDoc; linii: Muchie[] } {
  const grup = grupul ? new Set(grupul) : lipiteleLui(doc0, i);
  const l = lipesteZidul(plan0, grup, brut, raza, PAS);
  if (l) return { dep: l.dep, doc: doc0, linii: l.linii };
  // UN ZID STRÂMB SE ÎNDREAPTĂ CA SĂ SE POATĂ LIPI.
  //
  // `lipesteZidul` știe numai dreptunghiuri — un zid oblic n-are muchii de pus
  // lângă muchiile altuia, deci cădea pe lipirea de fețe și nu se așeza
  // niciodată la locul lui. Din afară arăta ca o lipire stricată: îl tragi
  // peste zidul de dedesubt și rămâne peste el, în diagonală.
  //
  // Deci: dacă e strâmb, îl încercăm DREPT și vedem dacă așa se lipește. Se
  // îndreaptă numai când chiar se prinde de ceva — altfel rămâne cum era, și
  // cine vrea un zid oblic îl păstrează. Îndreptarea se face în jurul
  // mijlocului (v. `indreaptaPeretele`), deci zidul nu sare din loc.
  const w = doc0.plan?.walls?.[i];
  if (w && !w.bulge) {
    const u = unghiulPeretelui(w);
    const abatere = Math.min(u % 90, 90 - (u % 90));
    if (abatere > 1e-6) {
      const drept = indreaptaPeretele(doc0, i);
      if (drept !== doc0) {
        const pd = deriva(drept);
        const ld = lipesteZidul(pd, grup, brut, raza, PAS);
        if (ld) return { dep: ld.dep, doc: drept, linii: ld.linii };
      }
    }
  }
  return { dep: lipirea(doc0, i, [rotunj(brut[0]), rotunj(brut[1])], grup).dep,
           doc: doc0, linii: [] };
}

/** O MIȘCARE A MÂINII PESTE UN ZID APUCAT DE CORP: de la cursor la document.
 *
 *  `la` e unde a ajuns cursorul, în metri; `raza` e raza de lipire, în metri
 *  la mărirea de pe ecran (v. `aliniere::razaSnap`). Întoarce documentul nou,
 *  deplasarea aplicată și liniile pe care s-a lipit (ghidajele). */
export function trageCorpulZidului(
  g: ApucareaZidului, la: Pt, raza: number,
): { doc: TronsonDoc; dep: Pt; linii: Muchie[] } {
  // LIBER ÎN PLAN, și lipit de vecin când ajunge lângă el. Zidul se duce unde
  // îl duce mâna, fără să tragă linia după el — v. `mutaPereteleSingur`.
  //
  // DAR CU FOILE LIPITE DE EL, fiindcă UN ZID PENTRU MÂNĂ E TOT SANDVIȘUL (v.
  // `model::lipiteleLui`). Fațada e scrisă în două rânduri — rezistența de
  // 30 cm și zidăria de 15 lipită pe ea —, iar camera se sprijină pe fața
  // celei din spate. Trasă singură foaia dinafară, ea pleca și nu se schimba
  // NIMIC altceva: nici camera, nici mobila din ea, nici conturul. De-acolo
  // venea «trag de un perete și nu ia mobila după el». Foile intră în `grup`
  // de la apucare, tăiate la același hotar ca bucata de sub deget.
  //
  // LIPIT MUCHIE CU MUCHIE, pe amândouă axele — v. `aliniere::lipesteZidul`.
  // Mâna se citește NEROTUNJITĂ: rotunjită întâi la pasul de 5 cm, o față
  // aflată la 2,5 cm de un pas n-ar fi intrat niciodată în rază.
  //
  // ÎMPINS PERPENDICULAR PE EL, nu plimbat liber prin plan. Un zid drept se
  // mută pe normala lui și atât; pe lungul lui nu-l duce nimeni, fiindcă acolo
  // nu se schimbă nicio cameră — capetele se trag din apucătorile lor.
  //
  // FĂRĂ ASTA, VECINII NU SE MAI LUNGEAU DELOC. `lungesteVecinii` trage capătul
  // unui vecin numai când deplasarea cade pe lungul LUI, altfel l-ar strâmba
  // (v. `model::lungesteVecinii`). Mâna nu merge însă niciodată curat pe o axă:
  // 4 mm de abatere pe cealaltă, și vecinul perpendicular pica din verificare —
  // zidul pleca singur și camera rămânea deschisă. Măsurat pe M6: tras
  // [0,25; 0] se schimbau 2 ziduri, tras [0,25; 0,004] unul singur. De-aici
  // «trag de un perete și nu vin și pereții lipiți».
  //
  // NUMAI ZIDURILE DREPTE: unul oblic n-are o normală pe axe, iar `zidulLipit`
  // îl îndreaptă oricum când se lipește de ceva.
  const w0 = g.doc0.plan?.walls?.[g.i];
  const abatere0 = w0 && !w0.bulge
    ? Math.min(unghiulPeretelui(w0) % 90, 90 - (unghiulPeretelui(w0) % 90))
    : Infinity;
  const inel0 = g.plan0.pereti[g.i]?.inel;
  // PRAGUL SE MĂSOARĂ ÎN MILIMETRI DE DESEN, nu în milionimi de grad.
  //
  // Era `abatere0 <= 1e-6`, adică «exact pe axă». Niciun zid venit dintr-un
  // IMPORT nu e exact pe axă: transcris din pixeli, el iese cu sutimi de grad
  // pe lângă (măsurat: 0,058° pe un zid de 6 m, adică 6 mm pe toată lungimea
  // lui). Pentru ele `axa0` ieșea `null`, iar atunci se pierd AMÂNDOUĂ
  // lucrurile de dedesubt: constrângerea pe normală ȘI pasul de 5 cm. De-aia
  // zidul se trăgea continuu — «the wall drag is continuous again»
  // (19.10.2026) — exact pe modulele importate, adică pe toate ale ei.
  //
  // 1 CM PE TOATĂ LUNGIMEA ZIDULUI: destul cât să prindă zgomotul oricărui
  // import (de zece ori peste cel măsurat), și cu mult sub orice zid pus
  // STRÂMB cu intenție — unul oblic se abate cu grade, nu cu sutimi. Măsura e
  // chiar săgeata capătului, `L·sin(θ)`, deci un zid lung e judecat mai aspru
  // decât unul scurt, cum și trebuie: pe el aceeași abatere se vede mai mult.
  const lung0 = inel0 ? osiaPeretelui(inel0).lungime : 0;
  const sageata0 = lung0 * Math.sin((abatere0 * Math.PI) / 180);
  const axa0 = sageata0 <= DREPT_MM && inel0 ? osiaPeretelui(inel0).axa : null;
  const brut: Pt = [la[0] - g.de[0], la[1] - g.de[1]];
  // ZIDUL SE DUCE UNDE ÎL DUCE MÂNA, pe amândouă axele.
  //
  // Era ÎMPINS PERPENDICULAR pe el și atât, cu un motiv bun: pe lungul lui nu
  // se schimbă nicio cameră, iar `lungesteVecinii` trage capătul unui vecin
  // numai când deplasarea cade pe lungul LUI. Arhitectul a cerut contrariul,
  // de două ori: «when we create a wall we should be able to grab it and move
  // it anywhere not just on one axis», apoi, pe o captură cu un zid orizontal
  // ales, «it doesn't let me grab and place it anywhere i want it only moves
  // on the x axis» (19.10.2026). Un zid pus de mână e un zid pe care vrei
  // să-l așezi, nu unul pe care să-l împingi pe o șină.
  //
  // CE SE PIERDE, scris ca să se știe: tras ÎN DIAGONALĂ, zidul nu-și mai
  // lungește vecinii — ei se lungesc numai pe direcția lor. Tras drept, cum
  // se trage de obicei un perete ca să mărească o cameră, totul merge ca
  // înainte: lipirea de muchii și pasul rămân neatinse.
  const { dep, doc: baza, linii } =
    zidulLipit(g.plan0, g.i, g.doc0, brut, raza, g.grup);
  // Lipirea potrivește muchii pe amândouă axele, iar de-acum amândouă se
  // păstrează: zidul se așază unde l-a dus mâna.
  // PE PASUL DE 5 cm SE AȘAZĂ FAȚA, NU DEPLASAREA.
  //
  // Pasul se aplica până acum DIFERENȚEI (v. `rotunj` în `lipirea`), deci un
  // zid care pornea de pe o cotă transcrisă rămânea pe ea oricât l-ai fi tras:
  // 4,61 + 0,05 = 4,66, iar cota scrisă arăta un număr cu care nu lucrează
  // nimeni pe șantier. Rotunjită FAȚA, zidul intră pe caroiaj la prima atingere
  // și rămâne acolo — «don't increase/decrease just the number, actually make
  // it match the wall length and representation».
  //
  // Amândouă fețele cad odată, fiindcă grosimile catalogului sunt multipli de
  // 5 cm (0,15 / 0,25 / 0,30), deci și lungimile vecinilor lungiți de el ies pe
  // pas.
  //
  // LIPIREA BATE PASUL, ca și până acum: când zidul s-a prins de muchia
  // altuia, acolo trebuie să rămână — un pas peste ea ar desface exact
  // potrivirea cerută. De-aia se sare când s-a lipit ceva.
  if (axa0 !== null && linii.length === 0 && inel0) {
    const o0 = osiaPeretelui(inel0);
    const fata0 = Math.min(...o0.fete);
    dep[axa0] = Math.round((rotunj(fata0 + dep[axa0]) - fata0) * 1000) / 1000;
    // ȘI PE CEALALTĂ AXĂ, de când zidul se mută liber: fără ea, jumătatea pe
    // lungul lui ar fi rămas continuă, iar mutarea ar fi ieșit pe pas într-o
    // direcție și oricum în cealaltă. Capătul lui e reperul, ca fața de mai
    // sus — se rotunjește POZIȚIA, nu deplasarea, ca zidul să intre pe
    // caroiaj la prima atingere.
    const capat0 = Math.min(...o0.span);
    dep[1 - axa0] =
      Math.round((rotunj(capat0 + dep[1 - axa0]) - capat0) * 1000) / 1000;
  }
  if (!dep[0] && !dep[1] && baza === g.doc0) return { doc: g.doc0, dep, linii };
  // TRAS DE UNUL, SE MUTĂ TOȚI CEI ALEȘI, cu aceeași deplasare — tot rostul
  // selecției de grup.
  //
  // O SINGURĂ CHEMARE, CU GRUPUL ÎNTREG. Chemată o dată pentru fiecare bucată,
  // cu `cuLipitele`, fiecare chemare mai aducea o dată foile de-alături — și
  // ele erau deja în grup, deci plecau de două și de trei ori: măsurat pe M1
  // (`W17`, sandviș de trei foi), tras 0,30 m se ducea 0,90. Grupul e aflat o
  // dată, la apucare, și se mută ca un tot (v. `model::mutaPereteleSingur`).
  // `dezlipeste`: zidul tras își lasă axa unde era și nu naște alta la locul
  // nou — v. `model::cuAxeleDezlipite`. Mutarea unei AXE nu cere asta.
  // `cuVecinii` CADE pe o copie proaspătă: ea e lipită de rețea prin capete,
  // iar lungirea vecinilor ar deforma planul în jurul unei piese abia așezate.
  return { doc: mutaPereteleSingur(baza, g.i, dep,
                                   { cuVecinii: !g.proaspat, grup: g.grup,
                                     dezlipeste: true }),
           dep, linii };
}

/** `pornit` spune dacă gestul a apucat să SCHIMBE ceva.
 *
 *  Contează fiindcă apucarea începe la `pointerdown`, iar un click simplu — pe
 *  un perete, ca să-l selectezi — e tot un `pointerdown` fără nicio mișcare
 *  după el. Fără steagul ăsta, fiecare click ar pune un pas în istoric, și
 *  atunci ⌘Z ar da înapoi selecții, nu modificări. */
/** GESTUL PLEACĂ MEREU DIN DOCUMENTUL DE LA APĂSARE, nu din cel de acum.
 *
 *  Înainte fiecare `pointermove` aplica o deplasare MICĂ peste rezultatul de
 *  dinainte, iar `de` se muta odată cu cursorul. Efectul: pe drum, zidul tras
 *  ajungea lângă capete de alți pereți, iar nodurile se deduc pe apropiere (un
 *  sfert de metru, v. `LIPIRE_NOD`) — deci își făcea vecini noi din mers și-i
 *  căra mai departe. Măsurat pe M1: W1 tras 1,20 m dintr-o dată atinge 3 ziduri,
 *  tras pas cu pas atinge 7. Asta e «când trag de un perete mai mult, se lipesc
 *  lucruri de el și ajunge în situații ciudate».
 *
 *  Cu `doc0` ținut de la apăsare și cu deplasarea socotită ÎNTREAGĂ de la `de`,
 *  vecinii se află o dată, pe geometria neatinsă, și rămân aceiași cât ține
 *  tragerea. Gestul devine și reversibil: tras încolo și înapoi, dă exact
 *  documentul de la început. */
/** BANDA DE PRINDERE A MÂNERULUI DE GROSIME, în pixeli — v. `cadruAles`. */
const BANDA_GROSIME_PX = 8;

/** Cât de departe de fața zidului stă banda aia, tot în pixeli.
 *
 *  INVARIANTUL: mai mare decât JUMĂTATE din bandă, fiindcă banda e centrată pe
 *  linie și se întinde în amândouă părțile. Sub atât, mânerul intră în corpul
 *  zidului și îi fură mâna — iar un perete de 0,30 m are unsprezece pixeli la
 *  mărirea obișnuită, deci cele două mânere îl mâncau pe jumătate. Era 2 cu o
 *  bandă de 8, adică 2 px înăuntru de fiecare parte.
 *
 *  Nu e o probă fiindcă nu e ceva ce se poate randa pe server (cotele și
 *  mânerele cer mărirea de pe ecran); e o socoteală care trebuie să rămână
 *  adevărată, scrisă acolo unde se schimbă cifrele. */
const GROSIME_AFARA_PX = BANDA_GROSIME_PX / 2 + 2;

type Gest =
  | { fel: "mutaPerete"; i: number; de: Pt; doc0: TronsonDoc; axa: 0 | 1;
      pornit: boolean; plan0: PlanTronson }
  | { fel: "capatPerete"; i: number; capat: 0 | 1; de: Pt; doc0: TronsonDoc;
      axa: 0 | 1; pornit: boolean }
  /** Bucata de zid, mutată SINGURĂ — fără linia ei și fără camerele din jur.
   *  V. `apucaCorpulZidului` și `trageCorpulZidului`. */
  | (ApucareaZidului & { fel: "glisPerete"; pornit: boolean; lipit: boolean;
      /** Pornit cu Shift: ridicat fără să se fi mutat nimic, gestul a fost un
       *  CLIC, adică «adună la selecție» (v. `laRidicare`). */
      adunaDacaNuSeMisca?: boolean })
  /** O latură sau un colț al CADRULUI zidului ales — v. `model::redimPeretele`. */
  | { fel: "redimPerete"; i: number; prindere: Prindere; doc0: TronsonDoc;
      pornit: boolean }
  | { fel: "mutaGol"; fel2: "doors" | "windows"; i: number; de: Pt; dir: Pt;
      pornit: boolean;
      /** CENTRUL GOLULUI LA APĂSARE. Pasul de 5 cm se măsoară de la el, nu pe
       *  o grilă absolută: v. `laMiscare`. */
      p0: Pt;
      /** Unde e mâna ACUM, liber în plan — din ea se desenează fantoma. Golul
       *  din document nu se clintește până la ridicarea degetului. */
      la: Pt | null }
  | { fel: "curbeaza"; i: number; a: Pt; b: Pt; pornit: boolean }
  /** O piesă de mobilier, trasă. `cutie0` = cutia ei de la apăsare, pentru
   *  lipire — `null` la o piesă rotită strâmb, care n-are muchii de aliniat
   *  (v. `aliniere::cutiaPiesei`). */
  | { fel: "mutaMobila"; i: number; de: Pt; doc0: TronsonDoc;
      cutie0: Cutie | null; pornit: boolean }
  /** SCARA ÎNTREAGĂ, apucată de ORICARE parte a ei — rampă sau podest.
   *
   *  Nu ține minte de care parte a fost apucată: scara se mută ca un corp, deci
   *  `k` n-ar schimba nimic (v. `model::mutaScaraIntreaga`). */
  | { fel: "mutaScara"; i: number; de: Pt; doc0: TronsonDoc;
      /** The flights' box at grab time, for snapping as furniture does. */
      cutie0: Cutie | null;
      pornit: boolean }
  /** UN PUNCT AL PODESTULUI, tras. Ținta e ABSOLUTĂ — unde a ajuns mâna —,
   *  fiindcă acolo se și lipește de colțul rampei. `varf` e al câtelea punct
   *  din conturul de la apăsare: conturul nu-și schimbă lungimea cât ține
   *  tragerea, deci indicele ține.
   *
   *  CE FACE depinde de podest: unul DEDUS se redimensionează, împingând
   *  rampele; unul SCRIS se reformează, rămânând lipit de ele. Deosebirea o
   *  face `model::tragePodestul`, nu pânza. */
  | { fel: "varfPodest"; i: number; k: number; varf: number; doc0: TronsonDoc;
      pornit: boolean }
  /** O LATURĂ a camerei alese, trasă perpendicular pe ea: camera își schimbă
   *  mărimea împingând zidul de pe latura aia — v. `model::mutaLaturaCamerei`.
   *  SINGURUL gest care schimbă o cameră; v. antetul. */
  | { fel: "laturaCamerei"; i: number; l: LaturaCamerei; doc0: TronsonDoc;
      pornit: boolean }
  /** ROTIREA CU MÂNA: unghiul urmărește cursorul în jurul centrului.
   *
   *  Unghiul se socotește ABSOLUT — de la cel de la apăsare (`rot0`), nu din
   *  pas în pas —, fiindcă altfel lipirea la unghi drept nu s-ar putea desface:
   *  o rotire care se adună pas cu pas ar păstra pentru totdeauna diferența
   *  înghițită de prima lipire, iar obiectul ar rămâne în urma cursorului.
   *  Zidul se rotește din documentul de la apăsare, din același motiv.
   *
   *  Camera nu e în listă — v. antetul. */
  | { fel: "roteste"; ce: "mobila" | "aparat" | "perete"; i: number;
      /** Pornit cu Shift pe o piesă: ridicat fără rotire, înseamnă «adună la
       *  selecție» — v. `laObiect` și `laRidicare`. */
      adunaDacaNuSeRoteste?: boolean;
      c: Pt; unghi0: number; rot0: number; aplicat: number; doc0: TronsonDoc;
      pornit: boolean }
  /** O bucată de nucleu — scara, un puț — trasă întreagă.
   *
   *  `cutie0` e cutia ei de la apăsare, iar deplasarea se socotește ÎNTREAGĂ de
   *  acolo, peste documentul de-atunci: lipirea la vecini mai adaugă câțiva
   *  centimetri, iar adunați pas cu pas ar rămâne în obiect pentru totdeauna —
   *  desprins de linia pe care s-a lipit, ar merge de-a pururi cu decalajul
   *  înghițit acolo. */
  | { fel: "mutaParte"; i: number; de: Pt; doc0: TronsonDoc; cutie0: Cutie;
      pornit: boolean }
  /** Nucleul întreg, cu tot ce e în el. */
  | { fel: "mutaNucleu"; de: Pt; pornit: boolean }
  /** O muchie sau un colț al cutiei nucleului: strânge sau lărgește golul,
   *  împingând camerele. */
  | { fel: "redimNucleu"; apucare: Apucare; pornit: boolean }
  /** Un colț al unei PĂRȚI a nucleului — ghena, sasul, holul. Scara și
   *  lifturile n-au mânere: măsurile lor se scriu (v.
   *  `model::parteaSeCroieste`). */
  | { fel: "redimParte"; i: number; apucare: Apucare; pornit: boolean }
  | { fel: "redimMobila"; i: number; apucare: Apucare; pornit: boolean }
  | { fel: "redimGol"; fel2: "doors" | "windows"; i: number;
      capat: -1 | 1; pornit: boolean }
  /** Un COLȚ al conturului, tras liber în plan. */
  | { fel: "coltContur"; i: number; pornit: boolean }
  /** O LATURĂ a conturului, trasă perpendicular pe ea. */
  | { fel: "laturaContur"; i: number; de: Pt; pornit: boolean }
  /** Un AX de trasare, tras: mută toți pereții de pe linia lui. */
  | { fel: "mutaAx"; ax: Ax; de: Pt; doc0: TronsonDoc; pornit: boolean }
  | { fel: "mutaAparat"; i: number; de: Pt; pornit: boolean }
  | { fel: "capatBanda"; i: number; capat: "start" | "end"; pornit: boolean }
  /** `balcon` = dreptunghiul tras se închide cu PARAPEȚI pe laturile pe care
   *  nu stă deja un zid, și devine spațiu exterior — v. `model::adaugaBalcon`. */
  | { fel: "cameraNoua"; de: Pt; la: Pt; forma: FormaCamera; balcon?: boolean }
  /** Camera mergând din colț în colț, ca în Homestyler: fiecare apăsare pune un
   *  vârf, iar închiderea pe primul o termină. */
  | { fel: "camLibera"; puncte: Pt[]; la: Pt }
  /** COTA ÎN LUCRU: primul punct pus, al doilea sub cursor. */
  | { fel: "cotaNoua"; a: Pt; la: Pt }
  /** An LED strip after its first click: its start on a face, the face's
   *  normal, the face's stops along it and the length under the cursor. */
  | { fel: "bandaNoua"; a: Pt; rot: number; min: number; max: number; la: number }
  /** SPĂRTURA ÎN LUCRU: zidul ales și cele două capete ale ei pe axa LUI
   *  lungă, cât se trage (v. `model::demoleazaBucata`). */
  | { fel: "sparge"; i: number; lung: 0 | 1; t0: number; t1: number }
  /** FEREASTRA DE ÎNTINDERE, cât se trage ea. */
  | { fel: "intindeFer"; de: Pt; la: Pt }
  /** CHENARUL DE ALES CU MÂNA: apăsat pe fond și tras, el aprinde tot ce
   *  cuprinde. V. `laFond` și `laRidicare`. */
  | { fel: "alegeFer"; de: Pt; la: Pt;
      /** Chenarul a pornit DE PE UN OBIECT (o cameră), nu de pe fond: ridicat
       *  fără tragere, gestul a fost un clic pe el, deci selecția lui rămâne.
       *  V. `laObiect` și `laRidicare`. */
      dinObiect?: boolean }
  /** UN CAPĂT AL AXEI ALESE, tras în lungul ei: scurtează sau lungește numai
   *  DESENUL (v. `model::lungimeaAxei`). */
  | { fel: "lungAx"; ax: Ax; capat: 0 | 1; de: Pt; doc0: TronsonDoc;
      pornit: boolean }
  /** …și tragerea de după: `f` e fereastra înghețată, `doc0` documentul de la
   *  alegere (deplasarea se socotește ABSOLUT din el, v. nota de la `Gest`). */
  | { fel: "intindeTrag"; f: Fereastra; de: Pt; dep: Pt; doc0: TronsonDoc;
      pornit: boolean }
  /** BUCATA COPIATĂ, ÎN MÂNĂ: urmează cursorul până la o apăsare, iar între
   *  timp se poate roti și oglindi (v. `model::Bucata`). */
  | { fel: "lipeste"; b: Bucata; la: Pt }
  /** `zid` e unealta cu care a pornit: un parapet e același gest cu alte
   *  implicite (fel, grosime, înălțime) — v. `model::IMPLICITELE_ZIDULUI`. */
  | { fel: "peretNou"; de: Pt; la: Pt; arc: boolean; zid?: "perete" | "parapet" }
  /** SCARA, FĂCUTĂ ÎN TREI PAȘI — cum cere Bianca, 05.10.2026:
   *
   *    1. `rampa1`   — un dreptunghi care urmează cursorul; câte trepte are se
   *                    vede și se poate SCRIE, nu doar trage;
   *    2. `distanta` — cât de departe stă a doua rampă de prima; din ea iese
   *                    PODESTUL, iar fantoma rampei a doua apare pe loc;
   *    3. `rampa2`   — treptele celei de-a doua, tot scrise sau trase.
   *
   *  Abia la capăt se scrie scara, cu liniile ei de evacuare. Întâi se trăgeau
   *  amândouă rampele liber; numărul de trepte nu se vedea și nu se putea
   *  spune, iar depărtarea dintre rampe ieșea din nimerit cu mâna.
   *
   *  `de`/`la` sunt capetele rampei care se face acum; `r1` e prima, fixată. */
  | ({ fel: "scaraNoua"; latime: number } & StareScara)
  /** A PRESET STAIR BEING PLACED: pressed at `q`, beside the faces `fete`
   *  (v. `aliniere::fetelePornirii`), dragged the way it climbs. `dir` is
   *  null until the hand has moved a flight's minimum; `de` is where the
   *  flight starts for that way (`pornireaScarii`), `oglinda` whether a U
   *  opens away from the face it runs along. `apasat` is false after a
   *  press-and-release without a drag: the stair then follows the cursor and
   *  the next press puts it, as the custom stair is drawn (asked by Tudor,
   *  28.10.2026: one gesture for every stair). */
  | { fel: "scaraGata"; cheie: string; q: Pt; fete: FataPornirii[]; de: Pt;
      la: Pt; dir: Pt | null; oglinda: boolean;
      /** The first flight's risers, one per tread the hand has covered. */
      trepte: number | null; apasat: boolean }
  | null;

/** UN CORP VECIN pe pânză: documentul lui (în același cadru cu cel de pe
 *  masă), cheia după care pagina îl recunoaște și ce se scrie pe el. */
export type Vecin = {
  cheie: string;
  nume: string;
  doc: TronsonDoc;
  culoriApartamente?: readonly (string | undefined)[] | null;
};

/** CASETA UNEI COTE DESCHISE LA SCRIS — PESTE pânză, nu ÎN desen.
 *
 *  Toate trei casetele (cota dintre axe, cota obiectului ales, lungimea zidului
 *  tras) sunt `<input>`-uri adevărate, fiindcă au nevoie de tastatură, de
 *  selecție și de Enter. Un input nu se poate desena în SVG, deci trebuia
 *  găzduit cumva; erau găzduite într-un `<foreignObject>` de 1 × 1 px, cu
 *  `overflow: visible`, ca să se reverse din el.
 *
 *  ȘI DE-AIA NU SE DESCHIDEAU ÎN SAFARI. `overflow` pe `foreignObject` e
 *  respectat de Chrome și de Firefox, dar nu de WebKit, care taie conținutul la
 *  cutia lui oricum ar fi scris: pe Safari caseta se DESCHIDEA — primea și
 *  focusul — dar rămânea retezată la o așchie de un pixel, adică nevăzută și
 *  neapăsabilă. Raportat de trei ori ca «clicul pe cifra dintre axe nu face
 *  nimic», și de fiecare dată căutat în altă parte: în încadrare, în
 *  redesenarea la schimbarea mărimii, în furtul de focus. Probele noastre nu-l
 *  puteau prinde: jsdom n-are hit-testing, iar în Chrome caseta se vede.
 *
 *  Scoasă din desen, caseta e un element HTML obișnuit, așezat în pixeli peste
 *  pânză — fără `foreignObject` și fără `scale(1/k)` pe conținut, adică fără
 *  niciunul dintre cele două locuri în care WebKit se poartă altfel. Se mută cu
 *  desenul fiindcă i se socotește locul din aceeași transformare ca a lui.
 *
 *  `pe` = centrată PE cotă (cifra scrisă ia locul celei desenate); fără el,
 *  caseta stă lângă cursor, ferită din calea mâinii care trage. */
function CasetaCotei({ view, p, pe = false, children }: {
  view: View; p: Pt; pe?: boolean; children: ReactNode;
}) {
  return (
    <div className={pe ? "ob-ed-cota-scrisa pe-cota" : "ob-ed-cota-scrisa"}
         style={{ left: p[0] * view.k + view.tx,
                  top: p[1] * view.k + view.ty }}>
      {children}
    </div>
  );
}

export default function Panza({
  doc, ales, alese, pereteleAlese, dublateAcum, mobilaAlese, golurileAlese, laAles,
  alegeLista,
  laSchimbare,
  laGestPornit, laGestTerminat,
  unealta, laUnealta, piesa, doarCitire, mod = "plan", aparat = "priza",
  curbPermis = false, aratare = null, calc = null,
  finisaj, laFinisaj, araAxe = false, araEtichete = true,
  layere,
  araCote = true, culoriApartamente = null,
  vecini, laVecin, cheieIncadrare, cadruTot,
}: {
  doc: TronsonDoc;
  ales: Ales;
  /** Camerele alese, toate — v. `Desen::alese`. */
  alese?: readonly number[];
  /** Pereții aleși, toți — trași împreună (v. `Masa::alege`). */
  pereteleAlese?: readonly number[];
  /** ZIDURILE TOCMAI DUBLATE — v. `Masa`. Trase, ele NU-și lungesc vecinii:
   *  o copie se naște cu capetele pe fețele altor ziduri, iar `cuVecinii` ar
   *  deforma plasa în jurul unei piese pe care omul abia o așază. */
  /** CE S-A DUBLAT ACUM, cu felul lui — v. `Masa::dublateAcum`. O copie încă
   *  „în mână" nu se agață de nimic la prima tragere. */
  dublateAcum?: { fel: "perete" | "mobila"; indici: readonly number[] } | null;
  /** Aprinde deodată o listă întreagă — chenarul de ales (v. `Masa`). */
  alegeLista?: (lista: readonly Ales[]) => void;
  /** Piesele adunate cu Shift — se aprind toate (v. `Desen::mobilaAlese`). */
  mobilaAlese?: readonly number[];
  /** Golurile adunate cu ⇧ — v. `Desen::golurileAlese`. */
  golurileAlese?: readonly { fel2: "doors" | "windows"; i: number }[];
  /** `adauga` = s-a ținut ⌘ (sau Ctrl) la apăsare, deci camera se adaugă la
   *  selecție în loc s-o înlocuiască. */
  laAles: (a: Ales, adauga?: boolean) => void;
  /** `viu` = gestul a mutat ZIDURI, deci camerele se refac pe loc — v. antet. */
  laSchimbare: (d: TronsonDoc, viu?: boolean) => void;
  /** Se cheamă ÎNAINTE de prima schimbare a unui gest — v. nota din `Editor`. */
  laGestPornit: () => void;
  /** Se cheamă când gestul s-a terminat (s-a ridicat degetul), o singură dată.
   *  Acolo se refac camerele: derivarea costă câteva milisecunde, iar la
   *  fiecare `pointermove` ar fi de zeci de ori pe secundă degeaba — și oricum
   *  cât ține tragerea contează zidul care se mișcă, nu camerele din jur. */
  laGestTerminat: (fel?: "contur") => void;
  unealta: Unealta;
  /** Ce piesă pune unealta «Mobilier». */
  piesa: string;
  laUnealta: (u: Unealta) => void;
  /** Zidul ales are voie să fie curb — bifa din panou. Fără ea nu se vede
   *  nicio apucătoare de curbură: un zid e drept până spune cineva altfel. */
  curbPermis?: boolean;
  doarCitire: boolean;
  /** Deschide/închide ușa. E o EDITARE — starea stă în document —, deci trece
   *  prin istoric și marchează documentul ca nesalvat. */
  /** «plan» sau «design» — v. nota din `Desen`. */
  mod?: "plan" | "design";
  /** Ce aparat pune unealta «Electrice». */
  aparat?: string;
  /** Cu ce vopsește pensula: pardoseala sau pereții, și cu ce finisaj. */
  finisaj?: { unde: "floor" | "wall"; cheie: string };
  /** Vopsește camera. Trece prin `Editor`, ca orice editare: un pas de istoric. */
  laFinisaj?: (camera: number) => void;
  /** SE ARATĂ AXELE DE TRASARE?
   *
   *  Stinse implicit, și asta e chiar cerința: «să ai grijă să fie ușor ca UX,
   *  să nu încurce lizibilitatea schemei». Se aprind dintr-un buton, cât ține
   *  trasarea, și se sting la loc. */
  araAxe?: boolean;
  /** Se scriu numele și ariile camerelor? V. `Desen::araEtichete`. */
  araEtichete?: boolean;
  /** The view layers — v. `Desen::layere`. Passed on to the module's drawing
   *  and its neighbours'; the canvas reads them for one thing only, to keep
   *  what is not at 100% out of the stretch window. The ghost ignores them,
   *  so a tool shows where its object would land whatever the panel says. */
  layere?: Layere;
  /** Se scriu cotele planșei? V. `Desen::araCote`. */
  araCote?: boolean;
  /** Spălătura apartamentelor — v. `Desen::culoriApartamente`. */
  culoriApartamente?: readonly (string | undefined)[] | null;
  /** CE TOCMAI A SCHIMBAT ASISTENTUL, cât se vede — v. `aratare.ts`. Vine de
   *  la editor, se aprinde o dată și se stinge singură; gesturile mâinii n-o
   *  pun niciodată, fiindcă mâna știe deja pe ce a apăsat. */
  aratare?: Aratare | null;
  /** HÂRTIA DE CALC — imaginea peste care se trasează, sau `null`. V.
   *  `../Calcul.tsx`: e a mesei de lucru, nu a documentului, deci trece pe
   *  lângă istoric și nu marchează nimic ca nesalvat. */
  calc?: Calc | null;
  /** CELELALTE CORPURI ALE COTEI, desenate în jurul celui de pe masă — v.
   *  `Vecin`. Numai pe «Toate corpurile» al unei soluții. */
  vecini?: readonly Vecin[];
  /** S-a apăsat pe un vecin: el devine cel de pe masă. `p` e punctul apăsat,
   *  în coordonatele pânzei, ca pagina să aleagă ce e dedesubt; `null` pentru
   *  discul unei axe. */
  laVecin?: (cheie: string, p: Pt | null) => void;
  /** Cheia reîncadrării, când nu e `doc.id` — v. `Panza2D::useIncadrare`. Pe
   *  «Toate corpurile», trecerea de la un corp la altul NU reîncadrează. */
  cheieIncadrare?: string;
  /** Ce se încadrează, când e mai mult decât documentul. */
  cadruTot?: { x: number; y: number; w: number; h: number } | null;
}) {
  const gazda = useRef<HTMLDivElement>(null);
  const [gest, setGest] = useState<Gest>(null);
  /** What a window takes: only what is on a layer at 100% (`editor/layers`). */
  const filtruFerestrei = useMemo<boolean | FiltruFereastra>(() => (layere ? {
    pereti: editable(layere.walls),
    piesa: (k) => { const l = layerOfPiece(k); return !l || editable(layere[l]); },
    aparat: (a) => toateEditabile(layere, layersOfItem(a)),
  } : true), [layere]);
  /** Lungimea tastată cât se trage un zid. Se golește la fiecare gest nou, ca
   *  să nu rămână cifra de la zidul de dinainte. */
  /** Ce a TASTAT omul în cifra de lângă cursor, sau `null` cât n-a tastat
   *  nimic. Nu `""`: gol și netastat trebuie deosebite, fiindcă atâta timp cât
   *  n-a tastat nimeni câmpul arată lungimea de sub mână, iar un câmp golit cu
   *  Backspace trebuie să rămână gol. */
  const [lungimeScrisa, setLungimeScrisa] = useState<string | null>(null);
  /** Why the strip tool's last click wrote nothing; gone at the next one. */
  const [aviz, setAviz] = useState<string | null>(null);
  // The strip's first click lives between two presses: a tool change (Esc,
  // another palette item, another device) drops it, or the next strip click
  // would finish a strip nobody is drawing any more.
  useEffect(() => {
    setAviz(null);
    setGest((g) => (g?.fel === "bandaNoua" ? null : g));
  }, [unealta, aparat]);
  /** Shift ținut apăsat: rotirea cu mâna se lipește din 15 în 15. */
  const t = useT();
  const shift = useRef(false);
  /** THE PRESET STAIR IN HAND, as of the last move. `pointermove` updates
   *  are batched, so a `pointerup` right after the move that gave the stair
   *  its direction still saw the state without one and took the release for
   *  a click (tested 28.10.2026 with a fast drag). The ref is written
   *  synchronously and the release reads it. */
  const scaraGataViu = useRef<Extract<Gest, { fel: "scaraGata" }> | null>(null);
  useEffect(() => {
    const la = (e: KeyboardEvent) => { shift.current = e.shiftKey; };
    window.addEventListener("keydown", la);
    window.addEventListener("keyup", la);
    return () => {
      window.removeEventListener("keydown", la);
      window.removeEventListener("keyup", la);
    };
  }, []);


  /** Zidul se termină la lungimea scrisă, pe direcția în care e tras acum. */
  const fixeazaLungimea = useCallback((v: number) => {
    setGest((g) => {
      if (g?.fel !== "peretNou") return g;
      const dx = g.la[0] - g.de[0];
      const dy = g.la[1] - g.de[1];
      const l = Math.hypot(dx, dy);
      // Fără direcție nu se poate spune încotro să crească: se ia orizontala,
      // singura alegere care nu inventează un unghi.
      const ux = l > 1e-6 ? dx / l : 1;
      const uy = l > 1e-6 ? dy / l : 0;
      return { ...g, la: [rotunj(g.de[0] + ux * v), rotunj(g.de[1] + uy * v)] as Pt };
    });
    // Gestul se încheie singur: cine a scris cifra a spus tot ce avea de spus.
    setTimeout(() => laRidicareRef.current?.(), 0);
  }, []);

  /** CIFRA SCRISĂ LA SCARĂ. Alt lucru în fiecare pas — trepte, metri, trepte —
   *  și, spre deosebire de zid, NU încheie gestul: după treptele primei rampe
   *  urmează depărtarea, iar după ea rampa a doua. Scrisul doar așază mărimea;
   *  pasul următor îl cere tot o apăsare. */
  const treaptaScarii = treaptaNivelului(doc.storey_height_m ?? 2.8);
  const fixeazaScara = useCallback((v: number) => {
    setGest((g) => {
      if (g?.fel !== "scaraNoua") return g;
      if (g.faza === "latura") {
        // DEPĂRTAREA până la rampa următoare — sub o lățime de rampă n-ar
        // încăpea podestul.
        return { ...g, dist: Math.max(g.latime, v) };
      }
      const n = Math.max(1, Math.round(v));
      if (!g.rampe.length) {
        // prima rampă: treptele se întorc în lungimea ei, pe direcția trasă
        if (!g.de) return g;
        const dx = g.la[0] - g.de[0], dy = g.la[1] - g.de[1];
        const L = Math.hypot(dx, dy);
        const ux = L > 1e-6 ? dx / L : 1, uy = L > 1e-6 ? dy / L : 0;
        const lung = lungimeDinContratrepte(n, treaptaScarii);
        return { ...g, la: [rotunj(g.de[0] + ux * lung),
                            rotunj(g.de[1] + uy * lung)] as Pt };
      }
      // rampa pusă pe latură: se reașază cu numărul cerut de trepte
      const rampe = [...g.rampe];
      const ant = rampe[rampe.length - 2];
      // CU SENSUL EI, cel ales trăgând: o cifră scrisă schimbă numărul de
      // trepte, nu încotro urcă rampa.
      rampe[rampe.length - 1] = ant
        ? rampaUrmatoare(ant, g.latura ?? "sus", g.dist ?? DIST_IMPLICITA, n,
                         treaptaScarii,
                         g.sens ?? sensulImplicit(ant, g.latura ?? "sus"),
                         g.latime)
        : rampe[rampe.length - 1];
      return { ...g, rampe };
    });
    setLungimeScrisa(null);
  }, [treaptaScarii]);


  /** Ce s-ar pune dacă s-ar apăsa acum, și unde. `null` = nimic (cursorul nu e
   *  peste niciun zid, sau nicio unealtă de pus nu e activă). */
  const [fantoma, setFantoma] = useState<
    { c: Pt; rot: number; grosime: number; beton: boolean } | null>(null);
  // FANTOMA PLEACĂ CU UNEALTA. Peretele nou întoarce unealta la săgeată la
  // ridicarea mouse-ului; fără rândul ăsta bucata de perete rămânea desenată
  // sub cursor până la următoarea mișcare.
  //
  // ȘI ZIDUL ÎNCEPUT DIN CLIC. El trăiește între două apăsări, deci nu se stinge
  // de la sine: schimbarea uneltei — Escape, alt buton din paletă — îl lasă
  // baltă, iar linia întreruptă ar fi rămas agățată de cursor.
  useEffect(() => {
    setFantoma(null);
    // ȘI SCARA ÎN LUCRU: trăiește și ea între apăsări, deci schimbarea uneltei
    // (Escape, alt buton din paletă) o lasă baltă, în loc s-o țină agățată de
    // cursor peste desen.
    scaraGataViu.current = null;
    setGest((g) => (g?.fel === "peretNou" || g?.fel === "scaraNoua"
                    || g?.fel === "scaraGata" ? null : g));
  }, [unealta]);
  const [cursor, setCursor] = useState<Pt | null>(null);
  /** CÂT E ÎNTOARSĂ PIESA CARE URMEAZĂ SĂ FIE PUSĂ, în grade.
   *
   *  Cerut de arhitect (20.10.2026): «When adding a furniture piece, before
   *  placing it we should be able to rotate it at 90 degrees from just
   *  pressing shift». Până acum piesa se punea mereu la 0° și se rotea
   *  DUPĂ — două gesturi pentru un lucru pe care îl știi dinainte.
   *
   *  Se uită la schimbarea uneltei: un sfert de tur ținut minte de la o
   *  canapea la o chiuvetă ar fi o surpriză, nu o înlesnire. */
  const [rotFantoma, setRotFantoma] = useState(0);
  useEffect(() => { setRotFantoma(0); }, [unealta, piesa]);
  /** Unde a fost mâna ultima dată, citibil din ascultătorul de taste fără să-l
   *  relege la fiecare mișcare de mouse. ⌘V pune bucata acolo. */
  const ultimulPunct = useRef<Pt | null>(null);
  /** BUCATA COPIATĂ cu ⌘C, până la ⌘V — v. `model::Bucata`.
   *
   *  AL PÂNZEI, nu al aplicației. Un clipboard la nivel de modul ar fi ținut
   *  bucata și după ce se schimbă modulul, deci s-ar fi putut lipi o bucătărie
   *  dintr-un tronson în altul — tentant, dar o bucată poartă coordonate și
   *  `id`-uri dintr-un plan anume, iar lipită peste altul cade unde se
   *  nimerește. Cât ține o sesiune de editare pe același modul, pânza stă
   *  montată, deci asta e chiar durata în care copia înseamnă ceva.
   *
   *  Nu e clipboardul SISTEMULUI: acolo ar fi trebuit scris un format de text
   *  și citit înapoi, cu tot ce înseamnă să primești din afară ceva ce zice
   *  că e un plan. ⌘C peste un câmp de text copiază mai departe textul (v.
   *  paza din ascultător). */
  const clipboard = useRef<Bucata | null>(null);
  const plan = useMemo(() => deriva(doc), [doc]);
  /** ARĂTAREA ASISTENTULUI — v. `aratare.ts`. Ceasul ei stă aici, nu în editor:
   *  alunecarea cere un desen pe cadru, iar pânza e singurul lucru care se
   *  mișcă. */
  const cadruAratarii = useTranzitie(aratare);
  /** Planul DESENAT: cel derivat, cu ce alunecă încă dus înapoi spre locul de
   *  unde pleacă. Gesturile, încadrarea și apucătoarele rămân pe `plan` — ele
   *  lucrează pe unde SUNT lucrurile, nu pe unde se văd în cele patru sute de
   *  milisecunde ale unei arătări. */
  const planDesenat = useMemo(
    () => (aratare ? cuDeplasari(plan, aratare.tinte, cadruAratarii.ramase)
                   : plan),
    [plan, aratare, cadruAratarii.ramase]);
  /** TRASEELE FIRELOR — un Dijkstra pe graful pereților, deci nu la fiecare
   *  redesenare. Numai în design: pe planșa tehnică nu se desenează instalația,
   *  iar calculul ei ar fi fost muncă pentru nimic la fiecare mișcare de zid. */
  /** Finisajul cu care vopsește pensula, ca desen. */
  const finisajulPensulei = useMemo(
    () => (finisaj ? finisajul(finisaj.cheie) : null), [finisaj]);
  const firele = useMemo(() => (mod !== "design" ? []
    : calculeazaTrasee(plan.electrice, axeleZidurilor(doc), plan.nivel)),
  [mod, plan, doc]);
  // CE SE ÎNCADREAZĂ CÂND AXELE SUNT APRINSE.
  //
  // Aparatul axelor nu stă în contur: liniile ies `AX_IESIRE` metri dincolo de
  // el, discurile stau pe capătul lor, iar cifra dintre două axe încă
  // `AX_COTA_PX` pixeli mai departe. Marginea încadrării e de 0,80 m — mai
  // mică decât ieșirea —, deci pe latura care strânge încadrarea aparatul
  // cădea ÎNTOTDEAUNA afară din pânză: nu se vedea, iar apăsarea pe cifră
  // ajungea la antetul paginii sau la paletă, nu la ținta ei. Așa arăta
  // «cotele dintre axe nu se deschid la clic» — măsurat în browser pe M1:
  // patru ținte din zece cădeau peste `HEADER.ob-ed-sus`.
  //
  // Se încadrează deci conturul CRESCUT cu ieșirea, plus o rezervă în pixeli
  // pentru cifră — singura care nu se scalează. Cheia poartă și ea starea
  // axelor: aprinse, desenul se reîncadrează ca aparatul să intre în pânză.
  const cadruIncadrat = useMemo(() => {
    const c = cadruTot ?? plan.cadru;
    if (!araAxe) return cadruTot;
    return { x: c.x - AX_IESIRE, y: c.y - AX_IESIRE,
             w: c.w + 2 * AX_IESIRE, h: c.h + 2 * AX_IESIRE };
  }, [araAxe, cadruTot, plan]);
  const marginiIncadrarii = useMemo(
    () => (araAxe ? { top: AX_MARGINE_PX, left: AX_MARGINE_PX } : undefined),
    [araAxe]);
  // AXELE NU REÎNCADREAZĂ. Cheia reîncadrării spune CÂND se așază desenul
  // singur, iar `araAxe` era în ea: apăsarea butonului de axe schimba cheia,
  // deci arunca vederea omului și o refăcea de la zero — pe deasupra mai
  // depărtată, fiindcă încadrarea cu axe cere o margine în plus
  // (`cadruIncadrat`, `marginiIncadrarii`). Arătat de arhitect (22.10.2026):
  // «când apăs pe butonul de axe să nu-mi mute zoom-ul, că acum dă zoom out»;
  // tot ea înghițea și apropierea făcută cu `Z`, care «se vede o secundă și
  // dispare după».
  //
  // MARGINILE RĂMÂN, fiindcă ele nu reîncadrează nimic singure: se aplică la
  // următoarea încadrare CERUTĂ — `0`, butonul de încadrare, deschiderea
  // modulului —, și atunci axele intră în cadru ca până acum. Ce s-a scos e
  // doar încadrarea nepoftită.
  const { view, setView, incadreaza, atinge } = useIncadrare(
    plan, gazda, cheieIncadrare ?? doc.id, cadruIncadrat, marginiIncadrarii);
  /** `Z` DUCE PÂNZA PE CE E ALES — cerut de arhitect (22.10.2026):
   *  «shortcutul "Z" mi-ar plăcea să facă zoom pe obiectul selectat; dacă nu e
   *  nimic selectat să facă așa cum e acum, pe tot planul».
   *
   *  PE TOATĂ SELECȚIA, nu doar pe ultimul apăsat: patru scaune adunate cu
   *  Shift se încadrează împreună, fiindcă ele sunt «obiectul» pe care lucrează
   *  omul. Cutia fiecărei bucăți o dă `cote::cutiaAlesului`, aceeași pe care o
   *  folosesc cotele obiectului ales — socotită a doua oară aici, cele două
   *  s-ar fi despărțit la primul fel nou de obiect.
   *
   *  PLAFONUL E AL PÂNZEI DE TRONSON (720 px/m), nu cel al vizualizatorului de
   *  ansamblu: o priză încadrată la 60 px/m ar fi rămas un punct, adică tasta
   *  n-ar fi făcut nimic vizibil tocmai pe obiectele mici pentru care se apasă.
   *  `maxK` o ține totuși sub 220, ca să nu ajungă nimeni în alb, fără niciun
   *  reper în jur.
   *
   *  MARGINEA E ÎN METRI și crește cu obiectul (a zecea parte din latura lui,
   *  cel puțin 0,35 m): un perete de 8 m încadrat cu aceeași margine ca o priză
   *  ar fi ieșit lipit de muchiile ecranului.
   *
   *  ÎNTOARCE `false` CÂND N-ARE CE ÎNCADRA — nimic ales, ori un fel de obiect
   *  fără cutie —, iar atunci `Z` face ce face `0` (v. `Panza2D`). */
  const incadreazaAles = useCallback(() => {
    const el = gazda.current;
    if (!el) return false;
    const bucati: Ales[] = pereteleAlese?.length
      ? pereteleAlese.map((i) => ({ fel: "perete", i }) as Ales)
      : mobilaAlese?.length
        ? mobilaAlese.map((i) => ({ fel: "mobila", i }) as Ales)
        : alese?.length
          ? alese.map((i) => ({ fel: "camera", i }) as Ales)
          : ales ? [ales] : [];
    /** Cutia unui obiect ales, PENTRU ÎNCADRARE.
     *
     *  `cutiaAlesului` e a COTELOR și n-o dă pentru tot: camera, partea de
     *  scară și nucleul n-au cotă de obiect, deci întorcea `null`. Aici asta
     *  însemna «n-am ce încadra», iar `Z` cădea pe încadrarea întregului plan —
     *  adică DEPĂRTA în loc să apropie, tocmai când omul avea ceva ales
     *  (22.10.2026, «zoom-ul pe obiecte e tot cu probleme»). Felurile alea au
     *  o geometrie foarte limpede, doar că n-o cere nimeni pentru cote: inelul
     *  camerei, cutia piesei de scară, dreptunghiul nucleului. */
    const cutiaDeIncadrat = (b: Ales) => {
      const gata = cutiaAlesului(plan, b)?.cutie;
      if (gata) return gata;
      if (b?.fel === "camera") {
        const c = plan.camere[b.i];
        if (!c?.inel.length) return null;
        const xs = c.inel.map((q) => q[0]), ys = c.inel.map((q) => q[1]);
        return { x0: Math.min(...xs), y0: Math.min(...ys),
                 x1: Math.max(...xs), y1: Math.max(...ys) };
      }
      if (b?.fel === "scaraParte") {
        // `aliniere::cutiaPiesei` are ALT fel de cutie (`x: [min,max]`), și
        // întoarce `null` pe o piesă rotită strâmb; atunci rămâne cadrul ei.
        const f = plan.piese[b.i];
        if (!f) return null;
        const q = cutiaPiesei(f);
        if (q) return { x0: q.x[0], y0: q.y[0], x1: q.x[1], y1: q.y[1] };
        const r = Math.max(f.w, f.h) / 2;
        return { x0: f.c[0] - r, y0: f.c[1] - r,
                 x1: f.c[0] + r, y1: f.c[1] + r };
      }
      if (b?.fel === "nucleu" && plan.nucleu) {
        const [rx0, ry0, rx1, ry1] = plan.nucleu.rect;
        return { x0: Math.min(rx0, rx1), y0: Math.min(ry0, ry1),
                 x1: Math.max(rx0, rx1), y1: Math.max(ry0, ry1) };
      }
      return null;
    };
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const b of bucati) {
      const c = cutiaDeIncadrat(b);
      if (!c) continue;
      x0 = Math.min(x0, c.x0); y0 = Math.min(y0, c.y0);
      x1 = Math.max(x1, c.x1); y1 = Math.max(y1, c.y1);
    }
    if (!Number.isFinite(x0)) return false;
    const pad = Math.max(0.35, Math.max(x1 - x0, y1 - y0) / 10);
    atinge();
    setView(() => fitBbox([x0, y0, x1, y1],
                          el.clientWidth || 800, el.clientHeight || 500,
                          { padM: pad, maxK: 220, minK: 2,
                            plafon: K_MAX_TRONSON,
                            insets: marginiIncadrarii }));
    return true;
  }, [gazda, plan, ales, alese, pereteleAlese, mobilaAlese, setView, atinge,
      marginiIncadrarii]);

  const { panoramaPornita, spatiu, panoramam } = useGesturi({
    gazda, view, setView, dim: { w: 0, h: 0 }, incadreaza, incadreazaAles,
    // CU SĂGEATA, BUTONUL STÂNG PE FOND PANORAMEAZĂ — cerut de Cătălin, și
    // gestul pe care-l încearcă oricine deschide un plan: apeși și tragi.
    //
    // Nu se pierde nimic prin asta. Apăsarea pe fond cu săgeata avea un singur
    // rost, stingerea selecției, iar ea se face la APĂSARE, adică înainte să se
    // vadă dacă urmează o tragere: un clic simplu deselectează în continuare, și
    // abia dacă mâna se mișcă începe și pânza să se miște (v. `laFond`).
    //
    // Cu o unealtă activă rămâne pe desenat: acolo apăsarea pe fond pune un
    // colț de cameră sau un capăt de zid, iar o pânză care ar fugi sub prima
    // tragere ar face unealta de nefolosit.
    poatePanorama: unealta === "sageata",
    // W/A/S/D ȘI SĂGEȚILE împing ce e ales, nu pânza — v.
    // `Panza2D::obiectAles`. CAMERA NU CONTEAZĂ CA «ales» AICI: ea nu se mută
    // (v. antetul), deci tastele rămân ale pânzei, ca și cum n-ar fi nimic
    // selectat.
    obiectAles: !doarCitire && ales != null && ales.fel !== "camera",
  });

  /** Pixeli pe metru, la mărimea de acum. Din el ies și apucătoarele (desenate
   *  la mărime constantă pe ecran), și raza lipirilor — v. `razaSnap`. */
  const k = view?.k ?? 20;

  /** APUCĂTOARELE TAC CÂT ȚINE ARĂTAREA. Cât alunecă un obiect, apucătoarele
   *  lui ar fi stat deja la capătul drumului — un cadru albastru gol, cu
   *  obiectul venind spre el. Ele sunt ale mâinii; mâna are timp să aștepte o
   *  jumătate de secundă. */
  const manaLibera = !doarCitire && !aratare;

  const pct = useCallback((e: { clientX: number; clientY: number }): Pt =>
    (gazda.current && view ? laLume(e, gazda.current, view) : [0, 0]),
  [view]);

  /** UNDE SE ÎNCRUCIȘEAZĂ ALTE ZIDURI CU ZIDUL `i`, pe axa LUI lungă.
   *
   *  Spărtura se lipește de ele: cel mai des se rupe tocmai despărțitura
   *  dintre două camere, iar capetele ei sunt chiar pereții care o închid.
   *  Cerut anume: «it should snap to places where we have wall intersections
   *  (...) in case we wanna remove the separation between two rooms basically
   *  so it's easier to find the wall connection» (20.10.2026).
   *
   *  Se iau FEȚELE zidului care taie, nu axul lui: ce vrea omul e să rupă
   *  exact cât ține despărțitura, iar ea se termină la fața peretelui care o
   *  întâlnește, nu la mijlocul lui. */
  const incrucisari = useCallback((i: number): number[] => {
    const eu = plan.pereti[i];
    if (!eu) return [];
    const o = osiaPeretelui(eu.inel);
    const [f0, f1] = [Math.min(...o.fete), Math.max(...o.fete)];
    const out: number[] = [];
    plan.pereti.forEach((w, j) => {
      if (j === i) return;
      const ow = osiaPeretelui(w.inel);
      if (ow.axa === o.axa) return;              // paralel: nu taie nimic
      // …și chiar îl atinge pe axa grosimii.
      // 3 cm de joc, cât rostul dintre două ziduri lipite.
      if (Math.max(...ow.span) < f0 - 0.03
          || Math.min(...ow.span) > f1 + 0.03) return;
      out.push(Math.min(...ow.fete), Math.max(...ow.fete));
    });
    return out;
  }, [plan]);

  /** `t` pe axa lungă, lipit de o încrucișare dacă e una aproape; altfel pe
   *  pasul de 5 cm, ca orice punct pus cu mâna. */
  const tLipit = useCallback((i: number, t: number): number => {
    const prag = 12 / k;
    let cel: number | null = null;
    let d = prag;
    for (const q of incrucisari(i)) {
      if (Math.abs(q - t) < d) { d = Math.abs(q - t); cel = q; }
    }
    return cel ?? rotunj(t);
  }, [incrucisari, k]);

  /** GOLUL MUTAT, LIPIT DE O ÎNCRUCIȘARE DE PEREȚI — muchia lui la fața
   *  zidului care taie, nu centrul la axul lui.
   *
   *  Cerut de arhitect (21.10.2026): «să facă snap la perete când ne apropiem
   *  de o intersecție de pereți». Aceeași regulă ca la spărtură (v. `tLipit`,
   *  20.10.2026) și aceleași `incrucisari`, nu o a doua socoteală: dacă ce
   *  înseamnă «încrucișare» s-ar scrie de două ori, cele două s-ar despărți la
   *  primul colț ciudat.
   *
   *  MUCHIA, NU CENTRUL, și asta e toată deosebirea față de spărtură: o
   *  spărtură se face ÎNTRE două puncte, deci punctul însuși se lipește, în
   *  timp ce o ușă are lățime și ce vrea omul e TOCUL lipit de peretele
   *  perpendicular — o ușă centrată pe fața aceea ar sta pe jumătate în
   *  zidărie. Se încearcă amândouă muchiile și se ia potrivirea cea mai
   *  strânsă, fiindcă un gol se poate apropia de o încrucișare cu oricare
   *  dintre ele.
   *
   *  NUMAI PE AXA LUNGĂ a zidului gazdă: pe cealaltă golul stă în grosime, iar
   *  `mutaGolulLiber` îl așază oricum pe ax. Pragul e în PIXELI împărțiți la
   *  mărire, ca tot ce se lipește sub mână — la o planșă depărtată lipirea ar
   *  înghiți jumătate de cameră. */
  const lipitLaIncrucisare = useCallback(
    (fel2: "doors" | "windows", i: number, la: Pt): Pt => {
      const g = doc.plan?.[fel2]?.[i];
      const unde = pozitiaGolului(doc, la);
      if (!g || !unde) return la;
      const gazda = plan.pereti[unde.gazda];
      if (!gazda) return la;
      // AXA LUNGĂ a zidului gazdă. `osiaPeretelui.axa` e cea pe care peretele
      // SE MUTĂ, adică perpendiculara pe lungimea lui — luată de-a dreptul,
      // lipirea ar fi împins golul prin grosimea zidului și n-ar fi mișcat
      // nimic pe lungime.
      const axa = osiaPeretelui(gazda.inel).lungPeX ? 0 : 1;
      const lat = g.w_m ?? 0.9;
      const prag = 12 / k;
      let mut = 0;
      let d = prag;
      for (const q of incrucisari(unde.gazda)) {
        for (const semn of [-1, 1]) {
          const muchie = la[axa] + (semn * lat) / 2;
          if (Math.abs(q - muchie) < d) { d = Math.abs(q - muchie); mut = q - muchie; }
        }
      }
      if (!mut) return la;
      const iese: Pt = [la[0], la[1]];
      iese[axa] = r3(iese[axa] + mut);
      return iese;
    }, [doc, plan, k, incrucisari]);

  /** UNDE AR CĂDEA UN CAPĂT DE SPĂRTURĂ, dacă s-ar apăsa aici: zidul de sub
   *  deget, direcția lui lungă și `t`-ul lipit. `null` = mâna nu e pe niciun
   *  perete, deci n-ar începe nimic.
   *
   *  UN SINGUR LOC, fiindcă răspunsul se citește de două ori: o dată de
   *  fantoma care se desenează înainte de apăsare, o dată de apăsarea care
   *  pune capătul. Socotite separat, ce vezi sub cursor și ce se pune ar fi
   *  două lucruri diferite — v. fantoma golului mutat. */
  const capatSparturii = useCallback((p: Pt):
      { i: number; lung: 0 | 1; t: number } | null => {
    // ZIDUL DE SUB DEGET, căutat în desen: unealta lucrează pe un perete, iar
    // o apăsare pe gol nu începe nimic.
    const i = plan.pereti.findIndex((w) => inCamera(p, w.inel));
    if (i < 0) return null;
    const o = osiaPeretelui(plan.pereti[i].inel);
    const lung = (1 - o.axa) as 0 | 1;
    return { i, lung, t: tLipit(i, p[lung]) };
  }, [plan, tLipit]);

  /** PUNCTUL, LIPIT DE CEL MAI APROPIAT COLȚ DE ZID — dacă e destul de
   *  aproape. Altfel, rotunjit pe grila de 5 cm, ca orice punct pus cu mâna.
   *
   *  12 px: cât prinde ochiul că e «pe colț». Mai mult ar înhăța colțuri de la
   *  care cineva chiar voia să se depărteze. */
  const colturiApropiate = useCallback((
    p: Pt,
    /** PRIMUL PUNCT AL COTEI, când se pune al doilea. Dat, punctul care n-a
     *  găsit nimic de lipit se îndreaptă pe orizontală sau pe verticală față de
     *  el — v. mai jos. */
    ancora?: Pt,
  ): Pt => {
    const prag = 12 / k;
    // COLȚURILE BAT MUCHIILE, iar muchiile bat grila.
    //
    // Erau numai colțurile de zid. Cerut de arhitect (19.10.2026): «Cand creez
    // o cota ar trebui sa se facă snap pe pereți când se apropie, pe muchia
    // peretelui sau pe muchia unui obiect ca de obicei măsurăm camerele sau
    // distante dintre obiecte sau perete si obiect». Pe un perete lung, cota
    // nu se pune în colț: se pune undeva pe FAȚA lui, iar fără lipire acolo
    // cifra iese cu doi centimetri greșită — și cifra aia se citește ca o
    // măsură a proiectului.
    //
    // TREI TREPTE, în ordinea asta: un colț e ținta cea mai greu de nimerit și
    // cea mai precisă, deci câștigă chiar dacă o muchie trece mai aproape;
    // altfel punctul cade PE muchie, proiectat pe ea; iar dacă nu e nimic în
    // rază, rămâne grila de 5 cm, ca până acum.
    let celColt: Pt | null = null;
    let dColt = prag;
    let celMuchie: Pt | null = null;
    let dMuchie = prag;
    const peSegment = (a: Pt, b: Pt): Pt => {
      const vx = b[0] - a[0], vy = b[1] - a[1];
      const L2 = vx * vx + vy * vy;
      const t = L2 < 1e-12 ? 0
        : Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2));
      return [a[0] + vx * t, a[1] + vy * t];
    };
    const incearca = (inel: readonly Pt[]) => {
      for (let i = 0; i < inel.length; i++) {
        const a = inel[i], b = inel[(i + 1) % inel.length];
        const dc = Math.hypot(a[0] - p[0], a[1] - p[1]);
        if (dc < dColt) { dColt = dc; celColt = [a[0], a[1]]; }
        const q = peSegment(a, b);
        const dm = Math.hypot(q[0] - p[0], q[1] - p[1]);
        if (dm < dMuchie) { dMuchie = dm; celMuchie = q; }
      }
    };
    for (const w of plan.pereti) incearca(w.inel);
    // MUCHIILE OBIECTELOR: cutia fiecărei piese, rotită cum e ea. Măsurăm des
    // de la un perete la o mobilă, iar fără colțurile piesei cota se agață de
    // zidul de dincolo de ea.
    for (const f of plan.piese) {
      const rad = (f.deg * Math.PI) / 180;
      const co = Math.cos(rad), si = Math.sin(rad);
      const hw = f.w / 2, hh = f.h / 2;
      incearca(([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as const).map(
        ([u, v]) => [f.c[0] + u * co - v * si, f.c[1] + u * si + v * co] as Pt));
    }
    // UN COLȚ SAU O MUCHIE BAT ORTOGONALA, și nu e o preferință: o cotă pusă
    // «cam pe colț» scrie o cifră greșită, iar cifra aia se citește ca o măsură
    // a proiectului. Îndreptarea e pentru cazul în care n-a fost nimic de
    // lipit — altfel ar fi tras cota de pe colțul ei ca s-o facă dreaptă.
    const lipit: Pt | null = celColt ?? celMuchie;
    if (lipit) return lipit;
    const g: Pt = [rotunj(p[0]), rotunj(p[1])];
    if (!ancora) return g;
    // A DOUA APĂSARE A UNEI COTE SE ÎNDREAPTĂ PE AXE. Cerut de arhitect
    // (26.10.2026): «când creezi o cotă singur, să dea snap pe orizontală și pe
    // verticală (...) să nu se mai ducă pe diagonală, sau să poți să pui o
    // linie dreaptă mai ușor». Lăsată liberă, o cotă trasă cu mâna ieșea
    // aproape dreaptă, și o cotă aproape dreaptă e mai rea decât una oblică
    // dinadins: arată ca o măsură pe orizontală, dar nu e.
    //
    // PRAGUL E `SNAP_UNGHI`, cel cu care se lipește și rotirea — un fapt, un
    // loc. Patru grade par puține, dar se măsoară pe lungimea cotei: pe 5 m
    // înseamnă o palmă de 35 cm, destul cât mâna să nu trebuiască să nimerească
    // nimic. Peste atâta, cota rămâne OBLICĂ: un zid pieziș chiar se cotează
    // pieziș, iar îndreptarea lui ar scrie altă măsură decât cea cerută.
    const dx = g[0] - ancora[0], dy = g[1] - ancora[1];
    const pragOrto = Math.tan((SNAP_UNGHI * Math.PI) / 180);
    if (Math.abs(dy) <= Math.abs(dx) * pragOrto) return [g[0], ancora[1]];
    if (Math.abs(dx) <= Math.abs(dy) * pragOrto) return [ancora[0], g[1]];
    return g;
  }, [plan, k, rotunj]);

  // ---- lipirea la vecini -------------------------------------------------- #
  //
  // Trei ajutoare peste `aliniere.ts`, ca gesturile de mai jos să ceară lipirea
  // într-un rând. Raza vine din MĂRIREA DE ACUM: tradusă în metri la fiecare
  // chemare, ea rămâne aceeași palmă de ecran oricât ai mări — v. `razaSnap`.
  //
  // LINIILE PE CARE S-A LIPIT se țin într-un `ref` și se desenează ca ghidaje
  // (v. mai jos). Într-o stare ar fi cerut o redesenare în plus la fiecare
  // mișcare de mouse; ele se văd oricum odată cu documentul care tocmai s-a
  // schimbat.
  const ghidaje = useRef<Muchie[]>([]);

  /** E OBIECTUL ĂSTA O COPIE PROASPĂTĂ? Cât e, lipirea tace: «obiectele
   *  rezultate din dublează nu ar trebui să se agațe de nimic din obiectele
   *  existente în prima fază» (26.10.2026). Felul se cere odată cu indicele,
   *  fiindcă zidul 7 și piesa 7 sunt numere diferite. */
  /** Indicii proaspeți ai unui fel, pentru drumurile care cer lista. */
  const proaspete = useCallback(
    (fel: "perete" | "mobila") =>
      (dublateAcum?.fel === fel ? dublateAcum.indici : null),
    [dublateAcum]);

  const proaspat = useCallback(
    (fel: "perete" | "mobila", i: number) =>
      dublateAcum?.fel === fel && dublateAcum.indici.includes(i),
    [dublateAcum]);

  /** Muchiile de care se poate lipi ceva, fără cele ale obiectului mutat. */
  const vecinele = useCallback(
    (fara: { piesa?: number; parte?: number }) => muchiileDinJur(plan, fara),
    [plan]);

  /** DEPLASAREA UNEI CUTII, cu lipirea adăugată. `brut` e cât a mers mâna de la
   *  apăsare; cutia e cea de la apăsare, deci nemișcată de lipirile de până
   *  acum — v. `Gest::mutaParte`. */
  const cutiaLipita = useCallback((
    cutie0: Cutie, brut: Pt, fara: { piesa?: number; parte?: number },
    raza = razaSnap(k),
  ): Pt => {
    const mutata: Cutie = {
      x: [cutie0.x[0] + brut[0], cutie0.x[1] + brut[0]],
      y: [cutie0.y[0] + brut[1], cutie0.y[1] + brut[1]],
    };
    const l = lipesteCutia(mutata, vecinele(fara), raza);
    ghidaje.current = l.linii;
    return [brut[0] + l.dep[0], brut[1] + l.dep[1]];
  }, [vecinele, k]);

  /** UNDE AJUNGE O PIESĂ PUSĂ CU MÂNA LA `p`, după lipirea de vecini.
   *
   *  Aceeași lipire ca la tragere (v. `cutiaLipita`), dar pe o cutie care încă
   *  n-a fost pusă: se face una cât piesa, centrată pe cursor, și se lasă
   *  lipirea s-o tragă la locul ei.
   *
   *  SE FOLOSEȘTE ÎN DOUĂ LOCURI, ȘI TREBUIE SĂ FIE ACELAȘI: fantoma de sub
   *  cursor și punerea propriu-zisă. Altfel omul vede ghena lipită de perete,
   *  apasă, și ea cade unde era cursorul. Cerut: «snap to position … și când
   *  trecem cu mouse-ul pe deasupra». */
  const piesaLipita = useCallback((fel: string, p: Pt): Pt => {
    const m = felulPiesei(fel);
    if (!m) return p;
    const cutie: Cutie = {
      x: [p[0] - m.w / 2, p[0] + m.w / 2],
      y: [p[1] - m.h / 2, p[1] + m.h / 2],
    };
    // RAZĂ MARE LA PUNERE, nu cea de la tragere.
    //
    // `razaSnap` e plafonată la 6 cm (v. `aliniere::SNAP_MAX`) — bună când
    // împingi o piesă deja pusă și vrei un control fin, inutilă când o pui:
    // ghena lăsată la un metru și jumătate de perete rămânea acolo, și părea
    // că lipirea nu merge deloc. La punere, omul arată CE VREA, nu unde exact;
    // deci se prinde de la o jumătate de metru.
    const l = lipesteCutia(cutie, vecinele({}),
                           Math.max(razaSnap(k), RAZA_PUNERE));
    let q: Pt = [p[0] + l.dep[0], p[1] + l.dep[1]];

    // …ȘI SCOASĂ DIN ZID, DACĂ A INTRAT ÎN EL.
    //
    // Lipirea potrivește MUCHII, iar una dintre potrivirile bune e muchia
    // ghenei cu muchia DEPĂRTATĂ a zidului — ceea ce o așază peste corpul lui,
    // nu lângă el. Pe desen se vede ca o ghenă înfiptă în perete: «on some
    // walls it seems to snap to the inside of the wall which should not
    // happen». Deci, după lipire, dacă piesa se suprapune peste un zid, se
    // împinge afară pe drumul cel mai scurt — rămâne lipită de fața lui, care
    // e chiar ce se cerea.
    for (let pas = 0; pas < 4; pas++) {
      let lovit = false;
      for (const w of plan.pereti) {
        const xs = w.inel.map((v) => v[0]), ys = w.inel.map((v) => v[1]);
        const wx: [number, number] = [Math.min(...xs), Math.max(...xs)];
        const wy: [number, number] = [Math.min(...ys), Math.max(...ys)];
        const cx: [number, number] = [q[0] - m.w / 2, q[0] + m.w / 2];
        const cy: [number, number] = [q[1] - m.h / 2, q[1] + m.h / 2];
        const px = Math.min(cx[1], wx[1]) - Math.max(cx[0], wx[0]);
        const py = Math.min(cy[1], wy[1]) - Math.max(cy[0], wy[0]);
        if (px <= 1e-6 || py <= 1e-6) continue;   // nu se ating
        lovit = true;
        if (px <= py) {
          q = [q[0] + (q[0] < (wx[0] + wx[1]) / 2 ? -px : px), q[1]];
        } else {
          q = [q[0], q[1] + (q[1] < (wy[0] + wy[1]) / 2 ? -py : py)];
        }
      }
      if (!lovit) break;
    }
    return q;
  }, [vecinele, k, plan.pereti]);

  /** `zidulLipit`, cu ghidajele puse în `ref` — v. funcția de deasupra
   *  componentei. */
  const zidulLipitAici = useCallback((
    plan0: PlanTronson, i: number, doc0: TronsonDoc, brut: Pt,
    grupul?: readonly number[],
  ): { dep: Pt; doc: TronsonDoc } => {
    const r = zidulLipit(plan0, i, doc0, brut, razaSnap(k), grupul);
    ghidaje.current = r.linii;
    return { dep: r.dep, doc: r.doc };
  }, [k]);

  /** MÂNERELE UNUI DREPTUNGHI DREPT: patru bare de latură și patru pătrate de
   *  colț, la mărime constantă pe ecran.
   *
   *  Un singur loc pentru nucleu și pentru bucățile lui, fiindcă e același
   *  gest: muchia apucată pleacă, cea opusă stă (v. `model::Apucare`). Barele
   *  se desenează ÎNAINTEA colțurilor, ca acolo unde se ating să rămână
   *  deasupra colțul — el e ținta mai greu de nimerit. */
  const manerele = useCallback((
    c: Cutie,
    apuca: (a: Apucare) => (e: PointerEventReact) => void,
    titlu: string,
    /** NUMAI COLȚURILE, fără mânerele de latură. Pe o parte de nucleu ele n-au
     *  loc: mânerul de latură e lat de 7 px, adică 19 cm la mărirea obișnuită
     *  de 37 px/m, iar o ghenă stă la câțiva centimetri de zidul de lângă —
     *  măsurat pe M3, cu liftul ales nu exista un singur punct din care zidul
     *  să poată fi apucat (v. nota de la `model::schimbaMobila`). Colțurile
     *  sunt patru pătrățele, nu benzi pe toată muchia, deci lasă fața zidului
     *  liberă acolo unde o apucă mâna. */
    doarColturi = false,
    /** ROTIREA PIESEI, în grade — săgeata cursorului se întoarce cu ea.
     *
     *  Mânerele unei piese se desenează într-un grup `rotate(deg)`, dar
     *  cursorul se alegea din axa LOCALĂ: pe un corp așezat la 90° — adică
     *  felul în care stau lângă perete corpul de bucătărie, dulapul și
     *  rafturile — săgețile ieșeau PERPENDICULARE pe ce scalau. «Săgețile sunt
     *  orizontale când sunt în punctul în care vreau să scalez pe verticală»
     *  (26.10.2026). Nucleul și părțile lui nu se rotesc, deci acolo rămâne 0
     *  și nu se schimbă nimic. */
    rot = 0,
  ) => {
    const mij = (a: Apucare, axa: 0 | 1) => {
      const s = axa === 0 ? a.sx : a.sy;
      const [lo, hi] = axa === 0 ? c.x : c.y;
      return s ? (s < 0 ? lo : hi) : (lo + hi) / 2;
    };
    return (
      <>
        {!doarColturi && LATURI.map((a, j) => (
          <rect key={`l${j}`} data-maner="marime"
                x={mij(a, 0) - (a.sx ? 3.5 : 9) / k}
                y={mij(a, 1) - (a.sy ? 3.5 : 9) / k}
                width={(a.sx ? 7 : 18) / k} height={(a.sy ? 7 : 18) / k}
                rx={2 / k} fill={SEL} stroke="#fff" strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: cursorulIntors(a.sx, a.sy, rot) }}
                onPointerDown={apuca(a)}>
            <title>{titlu}</title>
          </rect>
        ))}
        {COLTURI.map((a, j) => (
          <rect key={`c${j}`} data-maner="marime"
                x={mij(a, 0) - 4 / k} y={mij(a, 1) - 4 / k}
                width={8 / k} height={8 / k} rx={1.5 / k}
                fill="#fff" stroke={SEL} strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                style={{ cursor: cursorulIntors(a.sx, a.sy, rot) }}
                onPointerDown={apuca(a)}>
            <title>{titlu}</title>
          </rect>
        ))}
      </>
    );
  }, [k]);

  // ---- gesturi ----------------------------------------------------------- #
  /** GARDA PORNIRII E UN REF, NU STARE — și aici era «se strică după prima
   *  micromișcare».
   *
   *  ==========================================================================
   *  Fiecare gest chema `laGestPornit()` o dată, păzit de `gest.pornit`. Dar
   *  `pornit` e stare REACT: se scrie cu `setGest` și se citește abia după ce
   *  React comite. O tragere adevărată scoate zeci de `pointermove` pe secundă,
   *  iar mai multe dintre ele intră ÎNAINTE de comitere — toate cu `pornit`
   *  încă fals, deci toate repornind gestul.
   *
   *  Iar `Editor::pornesteGest` rescrie de fiecare dată `bazaGest.current` cu
   *  documentul de ACUM. După prima micromișcare, baza nu mai e documentul de
   *  dinaintea gestului, ci unul deja mutat — iar tot ce se măsoară față de ea
   *  (`obiecteleUrmeazaZidurile`, `conturUrmeazaZidurile`) vede o deplasare
   *  aproape nulă și nu mai face nimic. Peretele mergea mai departe, fiindcă el
   *  se socotește din `gest.doc0`, care nu se clatină; mobila, camerele și
   *  conturul rămâneau pe loc. Exact ce s-a raportat de trei ori: «trag și se
   *  mută doar peretele», «din cotă merge» — cota e o singură chemare, deci
   *  n-avea cum să se repornească.
   *
   *  Nu se vedea nici în probe, nici într-o tragere sintetică: acolo gesturile
   *  vin rar și React apucă să comită între ele. Se vede doar cu mâna pe mouse.
   *
   *  Reful se scrie SINCRON, deci a doua chemare din aceeași bătaie o vede pe
   *  prima. Se stinge la ridicarea degetului, unde se stinge și gestul. */
  const pornitRef = useRef(false);
  const porneste = useCallback(() => {
    if (pornitRef.current) return;
    pornitRef.current = true;
    laGestPornit();
  }, [laGestPornit]);

  const laMiscare = useCallback((e: PointerEvent) => {
    // CÂT SE PANORAMEAZĂ, GESTUL STĂ PE LOC.
    //
    // Rotița apăsată în timpul unei trageri mută pânza (v. `Panza2D`), iar
    // amândouă ascultă același `pointermove`. Gestul rulează PRIMUL, cu
    // `view`-ul de dinaintea panoramării: o mișcare de 100 px i se pare o
    // deplasare adevărată și duce piesa cu atât — apoi pânza pleacă și ea tot
    // cu 100 px, deci obiectul fuge de sub cursor cu viteză dublă. Măsurat: cu
    // masa apucată la 5,40 m, o rotiță trasă 100 px o arunca la 7,30.
    //
    // Înghețat aici, obiectul rămâne unde e în LUME, pânza curge pe sub el, iar
    // tragerea merge mai departe când se ridică rotița — adică exact ce ceri
    // când panoramezi: să ajungi în altă parte a planului fără să strici ce ții
    // în mână.
    if (panoramam.current) return;
    const p = pct(e);
    setCursor(p);
    ultimulPunct.current = p;
    if (!gest) return;
    if (gest.fel === "peretNou") {
      // Lipit de vecini și perpendicular pe ei: capătul de pornire s-a lipit la
      // apăsare, aici se așază cel de sfârșit — v. `pereteNouLipit`. Fantoma se
      // desenează din același răspuns din care se va pune zidul.
      //
      // ȘI CAPĂTUL DE PORNIRE SE REAȘAZĂ, nu doar cel de sfârșit: pornit de la
      // capătul unui vecin, zidul se așază în unul din cele două colțuri
      // închise ale lui, iar care din ele se știe abia când se vede încotro
      // pleacă (v. `model::coltulLipit`). Recalculul nu fuge: punctul așezat
      // rămâne lângă același capăt, deci a doua trecere dă același răspuns.
      const l = pereteNouLipit(doc, gest.de, [rotunj(p[0]), rotunj(p[1])],
                               gest.zid ?? "perete");
      setGest({ ...gest, de: l.de, la: l.la });
      return;
    }
    if (gest.fel === "scaraGata") {
      // THE DRAG CHOOSES THE WAY UP and, with several flights, how many risers
      // the first one takes, as the custom stair is drawn: one riser per tread
      // the hand covers, at the storey's tread; on a U the rest go to the
      // second flight (Tudor, 28.10.2026).
      // The way is the nearer axis, never into a wall (`directiaScarii`);
      // Shift frees it. Where the flight starts follows from the way: its
      // edge on a wall it runs along, its start on one it climbs away from
      // (`pornireaScarii`). A U opens away from the wall it runs along.
      const v: Pt = [p[0] - gest.q[0], p[1] - gest.q[1]];
      const pune = (g: Extract<Gest, { fel: "scaraGata" }>) => {
        scaraGataViu.current = g;
        setGest(g);
      };
      if (Math.hypot(v[0], v[1]) < RAMPA_MIN) {
        pune({ ...gest, la: p, trepte: null });
        return;
      }
      const h = doc.storey_height_m ?? 2.8;
      const pr0 = rampelePresetului(gest.cheie, gest.q, h);
      const total = pr0 ? scaraDinRampe(pr0.rampe, h, pr0.treapta).contratrepte : 16;
      const k = pr0?.rampe.length ?? 1;
      const treapta = pr0?.treapta ?? TREAPTA_SCARA;
      const dir = directiaScarii(gest.fete, v, shift.current);
      const s = pornireaScarii(gest.fete, gest.q, dir, LATIME_RAMPA);
      const n: Pt = [-dir[1], dir[0]];
      const oglinda = s.spreZid
        ? n[0] * s.spreZid[0] + n[1] * s.spreZid[1] < 0 : false;
      // One flight takes the storey's whole count: it has to climb the storey,
      // so fewer risers would only be taller ones, and the drag says the way.
      // With more flights, risers come from the hand's way along the first
      // one, a tread each, at least a flight's minimum and never more than
      // the storey's count leaves the others.
      const along = Math.max(0, (p[0] - s.de[0]) * dir[0] + (p[1] - s.de[1]) * dir[1]);
      const trepte = k === 1 ? total : Math.min(
        total - (k - 1) * TREPTE_MIN,
        Math.max(TREPTE_MIN, Math.round(along / treapta) + 1));
      pune({ ...gest, la: p, de: s.de, dir, oglinda, trepte });
      return;
    }
    if (gest.fel === "scaraNoua") {
      const q: Pt = [rotunj(p[0]), rotunj(p[1])];
      if (gest.faza === "latura") {
        // CURSORUL ALEGE AMÂNDOUĂ: încotro stă rampa următoare și cât de
        // departe. Patru laturi, cea spre care arată — v. `laturaSpre`.
        const ultima = gest.rampe.at(-1);
        if (!ultima) return;
        const latura = laturaSpre(ultima, q);
        const dist = Math.max(gest.latime,
                              departareaPe(ultima, latura, q));
        setGest({ ...gest, la: q, latura, dist });
        return;
      }
      if (!gest.rampe.length) { setGest({ ...gest, la: q }); return; }
      // O RAMPĂ DEJA AȘEZATĂ: cursorul îi spune ȘI ÎNCOTRO URCĂ, și câte
      // trepte are. Încotro — din poziția cursorului față de capătul ei de
      // start, cele patru laturi; câte trepte — cât s-a tras pe sensul ăla.
      //
      // Până acum sensul nu se putea alege: ieșea din latură, deci rampa
      // următoare era mereu paralelă sau invers-paralelă cu cea dinainte, și o
      // scară COTITĂ — cu un sfert de tur pe un podest de colț — nu se putea
      // desena deloc. Cerut, textual: «be able to have run in each direction
      // of the landing» (06.10.2026).
      const rampe = [...gest.rampe];
      const ant = rampe[rampe.length - 2];
      let sens = gest.sens;
      // CIFRA DE LÂNGĂ CURSOR TREBUIE SĂ ARATE CE S-A PUS: `dist` se strânge
      // mai jos pentru colț, dar nu se scria înapoi în gest, deci câmpul
      // rămânea pe numărul de la mișcarea dinainte — scria 1,43 m lângă un
      // podest de 0,55 (08.10.2026).
      let distPusa = gest.dist;
      if (ant) {
        const r = rampe[rampe.length - 1];
        const dx = q[0] - r.de[0], dy = q[1] - r.de[1];
        const departe = Math.hypot(dx, dy);
        // SENSUL SE SCHIMBĂ NUMAI CÂND CHIAR AI TRAS ÎNTR-ACOLO.
        //
        // Se citea din cursor la fiecare pixel, fără prag: cursorul stă chiar
        // pe podest în clipa apăsării, iar acolo cele patru sferturi se
        // întâlnesc — o tremurătură de un pixel întorcea rampa în altă parte.
        // Sub jumătate de metru rămâne sensul de până acum.
        sens = departe >= SCHIMBA_SENS
          ? laturaDinVector(dx, dy)
          : gest.sens ?? sensulImplicit(ant, gest.latura ?? "sus");
        const [vx, vy] = VERSOR[sens];
        const proiectie = dx * vx + dy * vy;
        // PODESTUL DE COLȚ E PĂTRAT, deci rampa următoare pleacă la o JUMĂTATE
        // de lățime dincolo de capătul celei dinainte, nu la una întreagă.
        //
        // Faza `latura` nu știe încă încotro va urca, așa că ține depărtarea
        // de cel puțin o lățime — bun pentru un podest de odihnă pe aceeași
        // linie. La un sfert de tur însă asta împinge colțul cu încă o
        // jumătate de lățime mai încolo, și între capătul rampei și podest
        // rămâne o gaură de 55 cm pe o rampă de 1,10: «the landing still
        // overlaps with the run» (07.10.2026).
        const uant = Math.hypot(ant.la[0] - ant.de[0], ant.la[1] - ant.de[1]) || 1;
        const cotit = Math.abs(((ant.la[0] - ant.de[0]) / uant) * vx
                               + ((ant.la[1] - ant.de[1]) / uant) * vy) < 0.5;
        const dist = cotit
          ? Math.max(gest.latime / 2, Math.min(gest.dist ?? gest.latime / 2,
                                               gest.latime / 2))
          : gest.dist ?? DIST_IMPLICITA;
        // ȘI NU SE PRĂBUȘEȘTE LA O TREAPTĂ. Numărul se citea din cât s-a tras,
        // iar imediat după apăsarea podestului s-a tras ZERO: rampa de 2,80 m
        // pusă de gest sărea la 0,28 m — o treaptă — și trebuia trasă înapoi
        // afară. Până când cursorul trece de prima treaptă, rampa rămâne cât
        // a fost pusă.
        const n = proiectie > treaptaScarii
          ? contratrepteDinLungime(proiectie + treaptaScarii / 2, treaptaScarii)
          : scaraDinRampe([r], doc.storey_height_m ?? 2.8, treaptaScarii).perRampa[0] ?? 1;
        rampe[rampe.length - 1] = rampaUrmatoare(
          ant, gest.latura ?? "sus", dist, n, treaptaScarii, sens, gest.latime);
        distPusa = dist;
      }
      setGest({ ...gest, la: q, rampe, sens, dist: distPusa });
      return;
    }
    if (gest.fel === "cameraNoua" || gest.fel === "camLibera") {
      setGest({ ...gest, la: [rotunj(p[0]), rotunj(p[1])] });
      return;
    }
    if (gest.fel === "mutaPerete") {
      // Deplasarea ÎNTREAGĂ, de la locul apăsării, peste documentul de-atunci —
      // v. nota de la `Gest`. `de` NU se mai mută.
      //
      // PERPENDICULAR PE EL, lipit de vecinul de pe aceeași direcție când
      // ajunge lângă el. Nu-și trage linia și nu-și cară vecinii de colț:
      // «când trag de un perete nu vreau să tragă și alți pereți conectați după
      // el». Camerele se refac din ziduri la fiecare mișcare (v. `viu`), deci
      // ele îl urmează oricum.
      //
      // CU FOILE LIPITE DE EL, însă (v. `model::lipiteleLui`): fațada e scrisă
      // în două rânduri, iar camera se sprijină pe fața celei din spate. Trasă
      // singură, foaia dinafară pleca și nu se schimba nimic — nici camera,
      // nici mobila din ea. Foaia de-alături nu e alt perete, e același perete
      // văzut de-a curmezișul, deci nici lipirea nu se uită la ea.
      const delta = p[gest.axa] - gest.de[gest.axa];
      const brut: Pt = gest.axa === 0 ? [delta, 0] : [0, delta];
      const { dep, doc: baza } = zidulLipitAici(gest.plan0, gest.i, gest.doc0,
                                                brut);
      dep[1 - gest.axa] = 0;
      if (dep[gest.axa] || baza !== gest.doc0) {
        porneste();
        laSchimbare(
          mutaPereteleSingur(baza, gest.i, dep,
                             { cuLipitele: true,
                               cuVecinii: !proaspat("perete", gest.i),
                               dezlipeste: true }),
          true);
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "mutaParte") {
      const brut: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      if (brut[0] || brut[1]) {
        porneste();
        const dep = cutiaLipita(gest.cutie0, brut, { parte: gest.i });
        laSchimbare(mutaParteaNucleului(gest.doc0, gest.i, dep));
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "coltContur") {
      porneste();
      // NIMIC DINĂUNTRU NU SE MIȘCĂ: conturul e doar forma pe care o ține
      // modulul (v. `mutaColtulConturului`).
      laSchimbare(mutaColtulConturului(doc, gest.i,
                                       [rotunj(p[0]), rotunj(p[1])]));
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "mutaAx") {
      // Deplasarea ÎNTREAGĂ, peste documentul de la apăsare: un ax care mută
      // zece pereți deodată își strânge altfel vecini noi pe drum, exact ca un
      // zid tras pas cu pas (v. nota de la `Gest`).
      // PE NORMALA AXEI, nu pe x sau pe y: o axă oblică se mută de-a curmezișul
      // ei, iar mâna merge oricum. Pentru cele ortogonale normala e chiar axa
      // lor, deci cifra iese aceeași ca înainte.
      const nAx = normalaAxei(gest.ax);
      const delta = rotunj((p[0] - gest.de[0]) * nAx[0]
                         + (p[1] - gest.de[1]) * nAx[1]);
      if (delta) {
        porneste();
        laSchimbare(mutaAxul(gest.doc0, gest.ax, delta), true);
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "laturaContur") {
      const dx = rotunj(p[0] - gest.de[0]);
      const dy = rotunj(p[1] - gest.de[1]);
      if (dx || dy) {
        porneste();
        laSchimbare(mutaLaturaConturului(doc, gest.i, [dx, dy]));
        setGest({ ...gest, de: [p[0], p[1]] as Pt, pornit: true });
      }
      return;
    }
    if (gest.fel === "redimNucleu") {
      porneste();
      laSchimbare(redimNucleul(doc, gest.apucare, [rotunj(p[0]), rotunj(p[1])]),
                  true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "redimParte") {
      // ȚINTA ABSOLUTĂ, ca la cutie: colțul se duce unde e mâna, iar muchia
      // opusă stă. Pas de 5 cm, ca orice măsură croită cu mâna.
      porneste();
      laSchimbare(redimParteaNucleului(doc, gest.i, gest.apucare,
                                       [rotunj(p[0]), rotunj(p[1])]), true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "redimGol") {
      porneste();
      laSchimbare(redimGolul(doc, gest.fel2, gest.i, gest.capat, p), true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "redimMobila") {
      // FĂRĂ `rotunj` PE PUNCT: mărimea se lipește la 5 cm în reperul PIESEI
      // (`model::redimMobila`), iar o piesă rotită n-are muchiile pe axele
      // lumii — rotunjit aici, pasul ar ieși strâmb și inegal pe cele două
      // laturi ale ei.
      porneste();
      laSchimbare(redimMobila(doc, gest.i, gest.apucare, p), true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "mutaNucleu") {
      const dx = rotunj(p[0] - gest.de[0]);
      const dy = rotunj(p[1] - gest.de[1]);
      if (dx || dy) {
        porneste();
        laSchimbare(mutaNucleul(doc, [dx, dy]), true);
        setGest({ ...gest, de: [p[0], p[1]] as Pt, pornit: true });
      }
      return;
    }
    if (gest.fel === "redimPerete") {
      // Poziția ABSOLUTĂ a prinderii, peste documentul de la apăsare.
      porneste();
      laSchimbare(redimPeretele(gest.doc0, gest.i, gest.prindere,
                                [rotunj(p[0]), rotunj(p[1])]), true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "glisPerete") {
      // Tot gestul stă afară din componentă, ca să poată fi probat pe drumul
      // mâinii — v. `trageCorpulZidului`. Aici rămâne doar ce e al planșei:
      // cursorul, mărirea și ghidajele desenate.
      const r = trageCorpulZidului(gest, p, razaSnap(k));
      ghidaje.current = r.linii;
      if (r.doc !== gest.doc0) {
        porneste();
        // ȘI PIESELE DIN ACEEAȘI SELECȚIE, cu deplasarea pe care a făcut-o
        // chiar zidul (`r.dep`, după lipire) — v. perechea din `mutaMobila` și
        // `Masa::alege`. Mobila din camera împinsă de zid se mută oricum
        // singură; asta e pentru piesele ALESE DINADINS, care pleacă și când
        // stau în altă parte.
        const d = mobilaAlese?.length
          ? mutaMobilaMulte(r.doc, mobilaAlese, r.dep) : r.doc;
        laSchimbare(d, true);
        setGest({ ...gest, pornit: true, lipit: r.linii.length > 0 });
      }
      return;
    }
    if (gest.fel === "capatPerete") {
      // LIBER ÎN PLAN, nu pe lungul zidului: tras de un capăt, peretele se
      // rotește și se lungește după mână. Constrâns pe axă — cum era — nu se
      // putea decât lungi, adică exact ce nu face nimeni când apucă un capăt.
      //
      // NUMAI ZIDUL ĂSTA: vecinii care împărțeau nodul rămân pe loc, iar capătul
      // se lipește de axul vecinului când ajunge lângă el — v.
      // `mutaCapatulSingur`.
      // PAS DE 5 CM, ca la mobilă și la ferestre (19.10.2026).
      //
      // ERA CONTINUU, ȘI DINADINS: pasul fusese scos fiindcă pe un zid tras ca
      // să ajungă lângă altul el încurcă — nu-l poți pune unde vrei, ci doar
      // din 5 în 5 («no step, it should be continuous»). Cerut acum invers, și
      // amândouă cererile au dreptate despre altceva: una despre POTRIVIRE,
      // cealaltă despre CIFRE ROTUNDE. Se pot ține amândouă, fiindcă nu se bat
      // pe același lucru:
      //
      //   · pasul dă cifrele rotunde — se rotunjește DEPLASAREA, nu poziția, ca
      //     un zid pornit de la 5,809 să ajungă la 5,859 și nu la 5,85: ce s-a
      //     cerut e să CREASCĂ din 5 în 5, nu să sară pe o grilă a lumii;
      //   · LIPIREA DE VECIN rămâne deasupra lui și nu e un pas, e o potrivire:
      //     `mutaCapatulSingur` duce capătul pe fața sau pe colțul zidului de
      //     lângă, adică fix unde-l voia omul, oricât de necuprinsă în pas ar
      //     fi cota aia.
      //
      // Iar cine chiar vrea 2,63 m îl scrie în panou, unde cifra scrisă e cifra
      // care intră în document.
      const dx = rotunj(p[0] - gest.de[0]);
      const dy = rotunj(p[1] - gest.de[1]);
      if (dx || dy) {
        porneste();
        laSchimbare(mutaCapatulSingur(gest.doc0, gest.i, gest.capat, [dx, dy]),
                    true);
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "curbeaza") {
      // Săgeata (sagitta) e distanța de la coardă la cursor, măsurată pe
      // normala ei `(dy, −dx)` — partea în care se umflă un arc cu `bulge > 0`
      // (v. `arcDinAx`); `bulge` e `s / (c/2)`, adică exact `tan(θ/4)`. Deci
      // curbura se trage de mijloc, iar cifra iese din geometrie, nu dintr-o
      // scară inventată.
      const cx = (gest.a[0] + gest.b[0]) / 2;
      const cy = (gest.a[1] + gest.b[1]) / 2;
      const dx = gest.b[0] - gest.a[0];
      const dy = gest.b[1] - gest.a[1];
      const c = Math.hypot(dx, dy) || 1;
      const sag = ((p[0] - cx) * dy - (p[1] - cy) * dx) / c;
      const bulge = rotunj(sag) / (c / 2);
      porneste();
      laSchimbare(curbeazaPeretele(doc, gest.i, bulge), true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "mutaGol") {
      // GOLUL URMEAZĂ CURSORUL LIBER, iar documentul se atinge abia la
      // ridicarea degetului.
      //
      // ÎNTÂI l-am mutat pe loc, la fiecare pas, prin `mutaGolulLiber`: cât
      // mâna era departe de orice zid, funcția întorcea documentul neatins,
      // deci ușa pur și simplu NU SE MIȘCA — «the door moving doesn't work».
      // Pe ecran asta arată exact ca o unealtă stricată, deși regula lucra.
      //
      // Cerut: «we should see the cursor with the door move freely anywhere
      // but it shouldn't let us press click outside of a wall (...) like the
      // wall sections» (20.10.2026). Deci fantoma merge oriunde, iar oprirea e
      // la AȘEZARE, nu la mișcare — ca la bucata lipită cu ⌘V.
      //
      // DAR SARE DIN 5 ÎN 5, ca așezarea. Golul se pune pe pas rotund
      // (`rotunj`, la ridicarea degetului), iar fantoma aluneca liber: între
      // două trepte ea arăta un loc în care ușa nu putea ajunge, și omul o
      // vedea sărind înapoi sub deget la ridicare. Cerut de arhitect
      // (21.10.2026): «boxul acela albastru să reflecte faptul că se mișcă din
      // 5 în 5 cm (...) nu să se miște așa cursiv». Aceeași rotunjire în
      // amândouă locurile, deci ce se vede e chiar ce se pune.
      //
      // PASUL SE MĂSOARĂ DE LA POZIȚIA GOLULUI, nu pe o grilă absolută, și
      // fantoma pornește exact de pe ea. Cerut de arhitect (21.10.2026):
      // «ușile, ferestrele, obiectele în general ar trebui să se miște cu snap
      // de 5 cm față de ultima lor poziție (...) când încerc să mut vreau ca
      // select boxul să înceapă exact pe poziția curentă».
      //
      // ROTUNJITĂ ABSOLUT, POZIȚIA MÂINII ERA ȘI RĂSPUNSUL: golul se așeza
      // UNDE E CURSORUL, nu unde ajunge după ce l-ai împins. De-aici cele două
      // simptome, care sunt unul: (1) un gol care nu stă pe un multiplu de
      // 5 cm — orice gol de pe un plan importat — nu putea ajunge niciodată
      // într-un alt punct la 5 cm de el, fiindcă toate drumurile duceau pe
      // grilă; (2) o împingere mai scurtă de o jumătate de treaptă rotunjea
      // înapoi în aceeași celulă, iar `mutaGolulLiber` întoarce documentul
      // NEATINS când centrul n-ar fi altul — deci ușa nu se mișca deloc:
      // «câteodată ușile rămân agățate pe pozițiile inițiale».
      //
      // Mobila se purta bine de mult, fiindcă ea rotunjește DEPLASAREA
      // (v. gestul `mobila` din aceeași funcție). Aici e aceeași socoteală.
      const dep: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      setGest({ ...gest, la: lipitLaIncrucisare(gest.fel2, gest.i,
        [r3(gest.p0[0] + dep[0]), r3(gest.p0[1] + dep[1])]) });
    }
    if (gest.fel === "roteste") {
      // UNGHIUL ABSOLUT, de la cel de la apăsare — v. `Gest`. Cu Shift, din 15
      // în 15: o rotire liberă e bună ca să vezi, dar ce rămâne pe planșă e
      // aproape întotdeauna un unghi rotund. Fără Shift, se lipește de
      // unghiurile drepte ale planșei și ale vecinilor, la patru grade în jur.
      const u = (Math.atan2(p[1] - gest.c[1], p[0] - gest.c[0]) * 180) / Math.PI;
      const brut = gest.rot0 + (u - gest.unghi0);
      const rot = shift.current
        ? Math.round(brut / 15) * 15
        : lipesteUnghiul(brut, unghiurileDinJur(plan, gest.c,
            gest.ce === "mobila" ? { piesa: gest.i } : {}));
      const dg = Math.round((rot - gest.rot0) * 10) / 10;
      if (!gest.pornit || Math.abs(rot - gest.aplicat) > 0.05) {
        porneste();
        // Zidul rotit e o schimbare de GEOMETRIE, deci camerele se refac sub
        // mână, ca la orice gest care mișcă ziduri (v. `viu` din antet). Se
        // rotește din documentul de la apăsare, cu unghiul întreg.
        laSchimbare(
          gest.ce === "perete" ? rotestePeretele(gest.doc0, gest.i, dg)
          : gest.ce === "aparat" ? schimbaAparat(gest.doc0, gest.i, { rot })
          : schimbaMobila(gest.doc0, gest.i, { rot }),
          gest.ce === "perete");
        setGest({ ...gest, aplicat: rot, pornit: true });
      }
      return;
    }
    if (gest.fel === "laturaCamerei") {
      // Latura se duce unde a ajuns mâna — poziția ABSOLUTĂ, peste documentul
      // de la apăsare (v. nota de la `Gest`) —, iar acolo se lipește de fața
      // celui mai apropiat zid. Fără lipire, o cameră lărgită «până la peretele
      // de dincolo» s-ar opri la 2 cm de el și ar lăsa un rost pe care nu-l
      // vede nimeni până la cotare.
      const brut = rotunj(p[gest.l.axa]);
      const tinta = lipestePeFata(gest.doc0, gest.l, brut);
      const delta = tinta - gest.l.valoare;
      if (delta) {
        porneste();
        laSchimbare(mutaLaturaCamerei(gest.doc0, gest.i, gest.l, delta), true);
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    // A strip's end handle: the pointer's place along the face, from the
    // strip's start as it is now, so the hand never drifts off it.
    if (gest.fel === "capatBanda") {
      const e = plan.electrice.find((x) => x.idx === gest.i);
      if (e) {
        const rad = (e.deg * Math.PI) / 180;
        const t = (p[0] - e.c[0]) * Math.sin(rad) - (p[1] - e.c[1]) * Math.cos(rad);
        const d2 = mutaCapatBanda(doc, gest.i, gest.capat, rotunj(t));
        if (d2 !== doc) {
          porneste();
          laSchimbare(d2);
          if (!gest.pornit) setGest({ ...gest, pornit: true });
        }
      }
      return;
    }
    if (gest.fel === "mutaAparat") {
      const dep: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      if (dep[0] || dep[1]) {
        porneste();
        // A box slides on the wall under the pointer (`lipesteDoza`): moved by
        // the increment it could never leave its first wall, since each step
        // across is dropped.
        laSchimbare(doc.plan?.electrical?.[gest.i]?.kind === "doza"
          ? lipesteDoza(doc, gest.i, p) : mutaAparat(doc, gest.i, dep));
        setGest({ ...gest, de: [p[0], p[1]] as Pt, pornit: true });
      }
      return;
    }
    if (gest.fel === "mutaScara") {
      // DIN `doc0` plus deplasarea întreagă, nu pas cu pas: altfel rotunjirea
      // fiecărui pas s-ar aduna și scara ar luneca față de mână.
      //
      // Fără `viu`: scara nu atinge niciun zid, deci n-are de ce să plătească
      // rederivarea camerelor (v. antetul).
      const brut: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      if (brut[0] || brut[1]) {
        porneste();
        // Snapped to the walls and pieces around as a piece of furniture is
        // (confirmed with the architect, 28.10.2026), from farther off
        // (`LIPIRE_SCARA`: a stair goes against walls); a fresh copy not on
        // its first drag, for the same reason as furniture's.
        const dep = gest.cutie0 && !proaspat("mobila", gest.i)
          ? cutiaLipita(gest.cutie0, brut, { piesa: gest.i }, LIPIRE_SCARA) : brut;
        laSchimbare(mutaScaraIntreaga(gest.doc0, gest.i, dep));
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "lungAx") {
      // cât s-a mers DE-A LUNGUL axei (v. `model::peAxa`)
      const t = peAxa(gest.ax, p);
      const jos = gest.capat === 0 ? t : gest.ax.jos;
      const sus = gest.capat === 1 ? t : gest.ax.sus;
      if (sus - jos > 0.05) {
        porneste();
        laSchimbare(lungimeaAxei(gest.doc0, gest.ax.nume,
                                 Math.min(jos, sus), Math.max(jos, sus)), true);
        setGest({ ...gest, pornit: true });
      }
      return;
    }
    if (gest.fel === "cotaNoua") {
      setGest({ ...gest, la: colturiApropiate(p, gest.a) });
      return;
    }
    if (gest.fel === "bandaNoua") {
      setGest({ ...gest, la: lungimeaBenzii(gest, p) });
      return;
    }
    if (gest.fel === "sparge") {
      const t = tLipit(gest.i, p[gest.lung]);
      if (t !== gest.t1) setGest({ ...gest, t1: t });
      return;
    }
    if (gest.fel === "intindeFer") {
      setGest({ ...gest, la: [rotunj(p[0]), rotunj(p[1])] });
      return;
    }
    if (gest.fel === "alegeFer") {
      setGest({ ...gest, la: p });
      return;
    }
    // BUCATA DIN MÂNĂ URMEAZĂ CURSORUL, pe grila de 5 cm ca orice punct pus
    // cu mâna. Nu atinge documentul: se scrie abia la apăsare.
    if (gest.fel === "lipeste") {
      const la: Pt = [rotunj(p[0]), rotunj(p[1])];
      if (la[0] !== gest.la[0] || la[1] !== gest.la[1]) setGest({ ...gest, la });
      return;
    }
    if (gest.fel === "intindeTrag") {
      // ABSOLUT din documentul de la alegere, ca orice tragere (v. `Gest`):
      // altfel deplasarea s-ar aduna de la pas la pas.
      const dep: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      // LIPIREA PE AXELE DE PORNIRE: aproape de una dintre ele, deplasarea pe
      // cealaltă se face zero, deci bucata merge drept.
      const prag = LIPIRE_AX_PX / k;
      if (Math.abs(dep[0]) < prag) dep[0] = 0;
      if (Math.abs(dep[1]) < prag) dep[1] = 0;
      if (dep[0] !== gest.dep[0] || dep[1] !== gest.dep[1]) {
        setGest({ ...gest, dep });
      }
      return;
    }
    if (gest.fel === "varfPodest") {
      // ȚINTA, nu deplasarea: punctul se duce unde e mâna, iar `model` îl
      // lipește de colțul rampei dacă a ajuns lângă unul. Din `doc0`, deci
      // tragerea nu se adună de la pas la pas.
      const mana = inCadrulPiesei(plan.piese[gest.i], p);
      // DIN 5 ÎN 5 cm, DE LA VÂRFUL APUCAT. Cerut de arhitect (21.10.2026):
      // «scările (podestul adică) nu se măresc/micșorează cu câte 5 cm când
      // trag de puncte» — aici nu era NICIO rotunjire, punctul urma cursorul
      // la milimetru, deci un podest tras ieșea cu 1,37 pe o latură.
      //
      // DE LA VÂRF, nu pe o grilă absolută, din același motiv ca la goluri
      // (v. gestul `mutaGol`): conturul unui podest citit din planșă nu stă pe
      // multipli de 5 cm, iar rotunjit absolut primul milimetru de tragere i-ar
      // fi sărit latura pe grilă — adică o mărime pe care n-a cerut-o nimeni.
      // Așa, pasul MĂSOARĂ cât s-a mărit latura, care e chiar ce se citește.
      //
      // ÎNAINTEA LIPIRII DE COLȚUL RAMPEI, care rămâne a modelului: pasul
      // spune unde ajunge mâna, iar `tragePodestul` are ultimul cuvânt când
      // vârful a ajuns lângă un colț — ca la gol, unde încrucișarea bate pasul.
      //
      // VÂRFUL SE CERE DE LA `conturulPodestului`, NU din `scara.podeste`:
      // podestul care n-a fost încă tras de nimeni n-are contur SCRIS, el se
      // deduce din rampe — și ăsta e chiar cazul reclamat, fiindcă orice
      // podest e dedus până la prima tragere. Citit din `podeste`, vârful ar
      // fi lipsit exact atunci, pasul ar fi tăcut, iar scara s-ar fi mărit
      // mai departe din milimetru în milimetru. V. `redimPodestul`, care
      // socotește lungimea și adâncimea nouă chiar din deplasarea vârfului:
      // rotunjită ea, în 5 cm crește ce se vede și se măsoară — podestul.
      const piesa0 = gest.doc0.plan?.furniture?.[gest.i];
      const v0 = piesa0 ? conturulPodestului(piesa0, gest.k)[gest.varf] : null;
      const spre: Pt = v0
        ? [r3(v0[0] + rotunj(mana[0] - v0[0])),
           r3(v0[1] + rotunj(mana[1] - v0[1]))]
        : mana;
      porneste();
      laSchimbare(tragePodestul(gest.doc0, gest.i, gest.k, gest.varf, spre),
                  true);
      setGest({ ...gest, pornit: true });
      return;
    }
    if (gest.fel === "mutaMobila") {
      // LIBER, pe amândouă axele: o piesă de mobilier nu se sprijină pe nimic.
      // Lipită de muchiile vecinilor când ajunge lângă ele, și numai atunci —
      // v. `cutiaLipita`.
      const brut: Pt = [rotunj(p[0] - gest.de[0]), rotunj(p[1] - gest.de[1])];
      if (brut[0] || brut[1]) {
        porneste();
        // O COPIE PROASPĂTĂ NU SE LIPEȘTE DE NIMIC la prima tragere: ea se
        // naște la 30 cm de original, adică tocmai lângă el și lângă vecinii
        // lui, deci prima mișcare ar fi fost mâncată de magnet.
        const dep = gest.cutie0 && !proaspat("mobila", gest.i)
          ? cutiaLipita(gest.cutie0, brut, { piesa: gest.i }) : brut;
        // TOATĂ SELECȚIA PLEACĂ, apucată de oricare piesă din ea. `mobilaAlese`
        // are și piesa aleasă singură (v. `Masa::alege`), deci drumul e unul
        // pentru una și pentru mai multe; paza pe `includes` e pentru tura în
        // care gestul a pornit înaintea rescrierii selecției de către React.
        const ale = mobilaAlese?.includes(gest.i) ? mobilaAlese : [gest.i];
        // ȘI ZIDURILE DIN ACEEAȘI SELECȚIE, cu aceeași deplasare. Cerut de
        // arhitect (22.10.2026): «vreau să pot să selectez cu shift și piese și
        // pereți în același timp, să mut mobilier cu tot cu pereți în anumite
        // cazuri» — v. `Masa::alege`, unde ⇧ nu mai stinge cealaltă listă.
        //
        // PIESELE ÎNTÂI, ZIDURILE PESTE ELE: `mutaPereteleSingur` duce după el
        // ce stă PE ziduri (goluri, prize) și mută camerele de pe fețe, deci
        // trebuie să lucreze pe documentul în care piesele au ajuns deja unde
        // trebuie. `grup` îi dă toată selecția deodată — chemat pe rând pentru
        // fiecare zid, foile lipite între ele ar pleca de mai multe ori.
        //
        // DEPLASAREA E A PIESELOR, cu lipirea lor cu tot: ce apucă mâna
        // hotărăște, iar zidurile urmează. Altfel cele două jumătăți ale
        // selecției s-ar fi despărțit la prima lipire de muchie.
        let d = mutaMobilaMulte(gest.doc0, ale, dep);
        if (pereteleAlese?.length) {
          d = mutaPereteleSingur(d, pereteleAlese[0], dep,
                                 { grup: pereteleAlese, dezlipeste: true });
        }
        laSchimbare(d);
        setGest({ ...gest, pornit: true });
      }
    }
  }, [gest, doc, plan, pct, laSchimbare, cutiaLipita, proaspat]);

  /** Zidul în lucru s-a pus deja?
   *
   *  Clicul care ÎNCHEIE un zid desenat din două apăsări e urmat de propriul
   *  `pointerup`, iar ascultătorul de pe fereastră poate apuca să se declanșeze
   *  cu gestul dinainte în mână — deci al doilea clic ar pune două ziduri.
   *  Steagul e pe un `ref`, nu pe stare: se citește în aceeași bătaie în care
   *  s-a scris. */
  const pus = useRef(false);

  /** PUNE ZIDUL ÎN LUCRU, dacă are lungime. `false` = n-a pus nimic, deci
   *  gestul rămâne deschis (v. cele două feluri de desenat, din antet). */
  /** RAMPELE ARĂTATE ACUM — una singură pentru fantomă, pentru cifra de sub
   *  cursor și pentru ce se scrie la sfârșit. Trei socoteli paralele s-ar fi
   *  despărțit la prima schimbare.
   *
   *  Cât se alege latura, rampa URMĂTOARE se arată deja acolo: altfel ai alege
   *  o latură fără să vezi unde duce. */
  const rampeleScarii = useCallback((g: Extract<Gest, { fel: "scaraNoua" }>) => {
    if (!g.rampe.length) {
      return g.de ? [rampaPeTrepte({ de: g.de, la: g.la }, treaptaScarii)] : [];
    }
    if (g.faza !== "latura") return g.rampe;
    const ultima = g.rampe[g.rampe.length - 1];
    const n = scaraDinRampe([ultima], doc.storey_height_m ?? 2.8, treaptaScarii)
      .perRampa[0] ?? 1;
    // CU SENSUL ALES, nu cu cel implicit. Fantoma chema `rampaUrmatoare` fără
    // `sens`, deci oricât ai fi tras în sus, ea desena rampa următoare tot
    // înainte sau tot înapoi — adică ARĂTA că scara nu cotește, chiar în clipa
    // în care omul o cotea. Gestul era bun, desenul de sub cursor nu.
    return [...g.rampe,
            rampaUrmatoare(ultima, g.latura ?? "sus",
                           g.dist ?? DIST_IMPLICITA, n, treaptaScarii,
                           g.sens ?? sensulImplicit(ultima, g.latura ?? "sus"),
                           g.latime)];
  }, [doc.storey_height_m, treaptaScarii]);

  /** SCRIE SCARA TRASĂ și încheie unealta.
   *
   *  Piesa păstrează cutia (centru, lățime, adâncime) ca orice mobilier — ca
   *  s-o poți apuca și muta — dar cutia IESE din rampe, nu invers. */
  const scrieScara = useCallback(
    (rampe: { de: Pt; la: Pt }[], latime: number) => {
      import("./model").then(({ adaugaScaraDesenata }) => {
        // FELUL IESE DIN DRUM, nu din câte rampe au ieșit și nici din unealta
        // apăsată. Aici scria «mai mult de o rampă ⇒ scaraU», iar o scară
        // DREAPTĂ cu un podest de odihnă la mijloc — trasă de două ori în
        // aceeași direcție — ieșea «Scară în U». Regula e una singură și stă
        // în `plan/scara::felulScarii`: se întoarce sensul sau nu.
        const r = adaugaScaraDesenata(doc, rampe, felulScarii(rampe), latime,
                                      treaptaScarii);
        if (!r) return;
        laGestPornit();
        laSchimbare(r.doc, true);
        laAles(r.ales);
        // Înapoi la săgeată: scara trasă e gata, iar cine a tras-o vrea de
        // obicei s-o privească, nu să tragă imediat a doua.
        laUnealta("sageata");
      });
      setGest(null);
    }, [doc, laSchimbare, laGestPornit, laAles, laUnealta, treaptaScarii]);

  /** PUTS THE PRESET STAIR where the gesture left it and ends the tool, as
   *  `scrieScara` does for a drawn one. */
  const scrieScaraGata = useCallback(
    (g: Extract<Gest, { fel: "scaraGata" }>) => {
      if (!g.dir) return;
      const dir = g.dir, oglinda = g.oglinda, trepte = g.trepte ?? undefined;
      import("./model").then(({ adaugaScaraPreset }) => {
        const r = adaugaScaraPreset(doc, g.cheie, g.de,
                                    doc.storey_height_m ?? 2.8, dir, oglinda,
                                    trepte);
        if (!r) return;
        laGestPornit();
        laSchimbare(r.doc, true);
        laAles(r.ales);
        laUnealta("sageata");
      });
      scaraGataViu.current = null;
      setGest(null);
    }, [doc, laSchimbare, laGestPornit, laAles, laUnealta]);

  const puneZidul = useCallback((g: Extract<Gest, { fel: "peretNou" }>) => {
    if (pus.current) return false;
    const L = Math.hypot(g.la[0] - g.de[0], g.la[1] - g.de[1]);
    if (L <= 0.2) return false;
    pus.current = true;
    const arc = g.arc;
    const zid = g.zid ?? "perete";
    import("./model").then(({ adaugaPerete }) => {
      laGestPornit();
      // Grosimea și înălțimea le pune unealta, nu locul ăsta: un parapet iese
      // de 15 cm și de 1,00 m, un perete de 10 cm și cât nivelul.
      const r = adaugaPerete(doc, g.de, g.la, undefined, arc ? 0.4 : 0, zid);
      // CAMERELE SE REFAC ODATĂ CU ZIDUL (`viu`), nu la sfârșitul gestului.
      //
      // Modelul se încarcă la cerere, deci zidul se pune într-o promisiune, iar
      // sfârșitul de gest apucase să treacă demult: derivarea rula peste
      // documentul de DINAINTE de zid și nu găsea nimic nou. Un zid tras prin
      // mijlocul unui living nu-l împărțea în două până la următorul gest,
      // oricare — se vedea ca «am desenat un perete și nu s-a întâmplat nimic».
      //
      // Nici chemat aici la sfârșit n-ar merge: gazda își citește documentul
      // dintr-un `ref` scris la randare, iar el încă îl are pe cel vechi în
      // bătaia asta — deci ar pune la loc documentul fără zid. Cu `viu`,
      // derivarea se face chiar pe documentul care se scrie.
      laSchimbare(r.doc, true);
      // ZIDUL NOU E ALES ȘI UNEALTA SE ÎNTOARCE LA SĂGEATĂ. A fost pe dos —
      // «cine desenează pereți desenează mai mulți» —, dar cerut altfel:
      // «când termin de desenat un perete, după ce iau mouse-ul, să se
      // facă direct, să nu mai fie nevoie să dau Escape». Cine vrea încă
      // unul apasă iar pe unealtă.
      laAles(r.ales);
      laUnealta("sageata");
    });
    return true;
  }, [doc, laGestPornit, laSchimbare, laAles, laUnealta]);

  const laRidicareRef = useRef<(() => void) | null>(null);
  const laRidicare = useCallback((e?: PointerEvent) => {
    // RIDICAREA ROTIȚEI NU ÎNCHEIE TRAGEREA. Pe mouse toate butoanele împart
    // un singur `pointerId`, deci `pointerup` vine și când se lasă rotița cu
    // care s-a panoramat — iar gestul, ascultând orice ridicare, scăpa piesa
    // din mână la jumătatea drumului. Tragerea s-a deschis cu butonul STÂNG
    // (v. `Desen::apuca`, care cere `button === 0`), deci numai el o și închide.
    //
    // NU SE ÎNTREABĂ DE `panoramam`: ascultătorii gestului se leagă din nou la
    // fiecare mișcare, deci la ridicare `laRidicare` ajunge DUPĂ `sus`-ul
    // panoramării, care tocmai a stins semnul. Butonul ridicat e singurul
    // martor care nu se schimbă.
    if (e && e.button !== 0) return;
    // THE PRESET STAIR: released after a drag, it is put; released on the
    // spot, it stays in the hand for the next press (v. `laFond`).
    const inMana = scaraGataViu.current;
    if (inMana) {
      if (!inMana.apasat) return;
      if (inMana.dir) scrieScaraGata(inMana);
      else {
        scaraGataViu.current = { ...inMana, apasat: false };
        setGest(scaraGataViu.current);
      }
      return;
    }
    // Conturul liber nu se termină la ridicarea degetului: el se face din
    // apăsări, nu dintr-o tragere.
    // CHENARUL DE ALES se încheie la ridicarea degetului: ce e înăuntru se
    // aprinde, și atât — nimic nu se scrie în document.
    if (gest?.fel === "alegeFer") {
      const f: Fereastra = {
        x0: Math.min(gest.de[0], gest.la[0]), y0: Math.min(gest.de[1], gest.la[1]),
        x1: Math.max(gest.de[0], gest.la[0]), y1: Math.max(gest.de[1], gest.la[1]),
      };
      setGest(null);
      // SUB 20 cm A FOST UN CLIC, nu un chenar. E o ECONOMIE, nu o regulă, și
      // merită spus: un chenar de doi centimetri n-ar cuprinde oricum nimic,
      // deci selecția s-ar goli la fel. Scurtătura scutește `ceCuprinde` — care
      // mătură toate zidurile, camerele, golurile și piesele — de o trecere la
      // fiecare clic pe fond, iar pe un modul mare clicurile sunt dese.
      // Același prag ca al ferestrei de întins.
      // …iar dacă chenarul a pornit DE PE un obiect, clicul a fost al LUI:
      // selecția făcută la apăsare rămâne, nu se stinge.
      if (f.x1 - f.x0 < 0.2 || f.y1 - f.y0 < 0.2) {
        if (!gest.dinObiect) laAles(null);
        return;
      }
      // CE CUPRINDE FEREASTRA — aceeași socoteală ca a întinderii și a
      // demolării (`ceCuprinde`), ca cele trei gesturi să nu aibă păreri
      // deosebite despre ce e „înăuntru".
      const ce = ceCuprinde(doc, f, filtruFerestrei);
      // DINTR-O SINGURĂ CHEMARE, nu adunând unul câte unul: adunarea citește
      // refurile selecției, iar ele se scriu la RANDARE — zece chemări într-o
      // tură citesc toate aceeași listă veche, deci ultima le-ar șterge pe
      // celelalte. Măsurat: dintr-un chenar peste două piese rămânea una.
      //
      // ZIDURI, PIESE ȘI GOLURI DEODATĂ: listele sunt deosebite, dar nu se
      // sting una pe alta — ⇧ adună de mult ziduri ȘI piese (v. `Masa`).
      const lista: Ales[] = [
        ...ce.ziduriIntregi.map((i) => ({ fel: "perete", i }) as Ales),
        ...ce.piese.map((i) => ({ fel: "mobila", i }) as Ales),
        ...ce.goluri.map((g) => ({ fel: "gol", fel2: g.fel, i: g.i }) as Ales),
      ];
      if (alegeLista) alegeLista(lista); else laAles(null);
      return;
    }
    if (gest?.fel === "intindeFer") {
      const f: Fereastra = {
        x0: Math.min(gest.de[0], gest.la[0]), y0: Math.min(gest.de[1], gest.la[1]),
        x1: Math.max(gest.de[0], gest.la[0]), y1: Math.max(gest.de[1], gest.la[1]),
      };
      // o fereastră de câțiva centimetri e un clic scăpat, nu o alegere
      if (f.x1 - f.x0 < 0.2 || f.y1 - f.y0 < 0.2) { setGest(null); return; }
      setGest({ fel: "intindeTrag", f, de: gest.la, dep: [0, 0], doc0: doc,
                pornit: false });
      return;
    }
    // COTA ÎN LUCRU NU SE ÎNCHEIE LA RIDICAREA DEGETULUI — ea se pune din
    // DOUĂ apăsări, ca și conturul liber. Fără rândul ăsta, ridicarea de după
    // prima apăsare cădea până jos, la `setGest(null)`, și omora cota începută:
    // a doua apăsare o lua mereu de la capăt, deci nicio cotă nu se punea
    // vreodată («when I try to create a cota it doesn't appear», 15.10.2026).
    //
    // …DAR O TRAGERE SE ÎNCHEIE: cine apasă și trage până la celălalt punct a
    // spus deja tot ce avea de spus, iar a-l pune să mai dea un clic ar fi
    // nefiresc. Peste 10 px e o tragere; sub, a fost un clic, și se așteaptă
    // a doua apăsare.
    if (gest?.fel === "cotaNoua") {
      const d = Math.hypot(gest.la[0] - gest.a[0], gest.la[1] - gest.a[1]);
      if (d > 10 / k) {
        const { a, la } = gest;
        setGest(null);
        laUnealta("sageata");
        import("./model").then(({ adaugaCota }) => {
          const nou = adaugaCota(doc, a, la);
          if (nou === doc) return;
          laGestPornit();
          laSchimbare(nou);
          laGestTerminat();
        });
      }
      return;
    }
    if (gest?.fel === "camLibera" || gest?.fel === "bandaNoua") return;
    // Scara se desenează din APĂSĂRI, rampă cu rampă — nu dintr-o tragere.
    if (gest?.fel === "scaraNoua") return;
    if (gest?.fel === "peretNou") {
      // DOUĂ FELURI DE A DESENA UN ZID, amândouă îngăduite — cerute anume:
      // «ori dau click să încep să desenez și click să mă opresc, ori dau click
      // dar nu iau degetul, drag, și iau degetul să mă opresc».
      //
      // Aici se despart de la sine: dacă la ridicarea degetului zidul are deja
      // lungime, degetul a fost tras, deci gestul s-a terminat. Dacă n-are —
      // adică degetul s-a ridicat de unde a apăsat —, a fost primul clic din
      // doi: zidul rămâne în lucru, urmărește cursorul, și se încheie la a doua
      // apăsare (v. `laFond`).
      if (!puneZidul(gest)) return;
    }
    if (gest?.fel === "cameraNoua") {
      const g = gest;
      import("./model").then(({ adaugaCamera, adaugaBalcon }) => {
        const r = g.balcon ? adaugaBalcon(doc, g.de, g.la)
                           : adaugaCamera(doc, g.de, g.la, g.forma);
        // `null` = dreptunghiul e prea mic, adică a fost un clic scăpat, nu o
        // cameră. Unealta rămâne activă: următoarea apăsare e a doua încercare.
        if (!r) return;
        laGestPornit();
        // CU `viu`, ca documentul să treacă prin conturul și camerele gazdei.
        // Ridicarea degetului a anunțat deja sfârșitul gestului, mai jos, pe
        // documentul de DINAINTE — promisiunea asta se rezolvă după ea. Fără
        // `viu`, camera sau balconul intrau în document fără ca nimeni să
        // întrebe dacă lărgesc conturul: balconul tras în afara tronsonului
        // rămânea lângă o amprentă care nu știa de el.
        laSchimbare(r.doc, true);
        laAles(r.ales);
        // ÎNAPOI LA SĂGEATĂ, spre deosebire de peretele nou: cine trage o cameră
        // vrea de obicei s-o aranjeze imediat, nu să tragă a doua.
        laUnealta("sageata");
      });
    }
    // Camerele se refac o dată, la sfârșit — și numai dacă gestul chiar a
    // schimbat ceva.
    // Zidul nou și-o cheamă singur, din promisiunea lui (v. `puneZidul`).
    // GOLUL SE AȘAZĂ LA RIDICAREA DEGETULUI, pe zidul de sub fantomă. Dacă
    // acolo nu e niciun zid în rază, `mutaGolulLiber` întoarce documentul
    // neatins și ușa rămâne unde era — adică apăsarea în afara unui perete nu
    // face nimic, cum s-a cerut.
    if (gest?.fel === "mutaGol") {
      const la = gest.la;
      setGest(null);
      if (la) {
        // AȘA CUM E, nerotunjit încă o dată: `la` e deja pe pasul măsurat de
        // la poziția de la apăsare (v. `laMiscare`). Rotunjit aici pe grila
        // absolută, așezarea ar fi căzut în alt punct decât fantoma — adică
        // tocmai saltul de sub deget pe care l-am scos.
        const d2 = mutaGolulLiber(doc, gest.fel2, gest.i, la);
        if (d2 !== doc) {
          laGestPornit();
          laSchimbare(d2);
          laGestTerminat();
        }
      }
      return;
    }
    // SPĂRTURA NU SE ÎNCHEIE LA RIDICAREA DEGETULUI: ea se pune din DOUĂ
    // apăsări, ca o cotă (v. `laFond`).
    if (gest?.fel === "sparge") return;
    // ⇧+CLIC PE UN ZID ADUNĂ LA SELECȚIE — gestul a pornit ca o glisare a
    // liniei întregi, dar degetul s-a ridicat fără s-o clintească.
    if (gest?.fel === "glisPerete" && gest.adunaDacaNuSeMisca && !gest.pornit) {
      laAles({ fel: "perete", i: gest.i }, true);
      setGest(null);
      return;
    }
    // SHIFT+CLIC PE O PIESĂ ADUNĂ LA SELECȚIE. Gestul a pornit ca o rotire
    // (Shift ținut pe corpul piesei), dar degetul s-a ridicat fără ca ea să
    // schimbe ceva — deci n-a fost o tragere, a fost un clic. Verificarea e pe
    // `pornit`, nu pe distanța parcursă: `pornit` devine adevărat exact când
    // rotirea chiar a scris în document (v. `laMiscare`), adică e chiar
    // întrebarea «s-a rotit ceva?».
    if (gest?.fel === "roteste" && gest.adunaDacaNuSeRoteste && !gest.pornit) {
      laAles({ fel: "mobila", i: gest.i }, true);
      setGest(null);
      return;
    }
    // LIPIREA NU E O TRAGERE: ea ține de la ⌘V până la apăsarea care o pune,
    // iar între timp degetul se poate ridica de câte ori vrea. De-aia nu are
    // `pornit` și de-aia iese de-aici înaintea oricărei alte socoteli.
    if (gest?.fel === "lipeste") return;
    if (gest && gest.fel !== "peretNou" && gest.fel !== "scaraGata"
        && (gest.fel === "cameraNoua" || pornitRef.current || gest.pornit)) {
      // Un gest PE CONTUR e voința omului asupra formei: gazda nu-l mai
      // corectează după ziduri (v. `Editor::cuCamere`).
      laGestTerminat(gest.fel === "coltContur" || gest.fel === "laturaContur"
        ? "contur" : undefined);
    }
    ghidaje.current = [];
    pornitRef.current = false;
    setGest(null);
  }, [gest, doc, laSchimbare, laGestPornit, laGestTerminat, laAles, laUnealta,
      scrieScaraGata,
      puneZidul, filtruFerestrei]);
  laRidicareRef.current = laRidicare;

  useEffect(() => {
    // CURSORUL SE URMĂREȘTE ȘI FĂRĂ GEST, cât e armată cota sau spărtura: din
    // el se desenează punctul de pornire, înainte de orice apăsare (v.
    // fantomele `data-cota-pornire` și `data-sparge-pornire`). Altfel `cursor`
    // rămâne gol până la primul gest, și tocmai înainte de el trebuie să se
    // vadă de ce se agață punctul.
    if (!gest && unealta !== "cota" && unealta !== "demoleaza") return;
    window.addEventListener("pointermove", laMiscare);
    window.addEventListener("pointerup", laRidicare);
    return () => {
      window.removeEventListener("pointermove", laMiscare);
      window.removeEventListener("pointerup", laRidicare);
    };
  }, [gest, laMiscare, laRidicare, unealta]);

  /** ÎNCHEIE SCARA ÎN LUCRU, de oriunde s-ar cere. */
  const incheieScara = useCallback(() => {
    setGest((g) => {
      if (g?.fel === "scaraNoua" && g.rampe.length) {
        scrieScara(g.rampe, g.latime);
      }
      return g?.fel === "scaraNoua" && g.rampe.length ? null : g;
    });
  }, [scrieScara]);

  /** ÎNCHIDE CONTURUL LIBER — camera sau zona desenată din apăsări.
   *
   *  Chemată din două locuri, fiindcă sunt două feluri de a spune «gata»:
   *  apăsarea pe primul vârf, cum se închide un contur pe planșă, și ENTER,
   *  cerut anume (15.10.2026: «I should press on enter when I'm done, so that
   *  I have a way to finish the room»). Pe o zonă înghesuită într-un colț,
   *  primul vârf e greu de nimerit — iar fără Enter rămâneai prins în unealtă,
   *  exact ca la scară înainte de 07.10.2026. */
  /** `zona` = FELUL camerei fără pereți, sau `null` când conturul se zidește.
   *
   *  Era un `boolean` — „e zonă de desfumare sau nu" —, iar felul se scria în
   *  `adaugaZona`. Cu două feluri de cameră fără pereți (desfumarea și camera
   *  oarecare, căreia omul îi alege felul pe urmă) întrebarea nu mai e dacă, ci
   *  CARE: felul vine de la unealtă, fiindcă numai ea îl știe. */
  const incheieConturul = useCallback((puncte: Pt[], zona: string | null) => {
    // Sub trei puncte nu e un contur — dar n-o spune nimeni aici: `adaugaZona`
    // și `adaugaCameraContur` întorc amândouă `null`, iar o a doua pază pe
    // aceeași regulă e o regulă care se poate desincroniza.
    import("./model").then(({ adaugaCameraContur, adaugaZona }) => {
      const r = zona ? adaugaZona(doc, puncte, zona)
                     : adaugaCameraContur(doc, puncte);
      if (!r) return;
      laGestPornit();
      laSchimbare(r.doc);
      laAles(r.ales);
      laUnealta("sageata");
      laGestTerminat();
    });
    setGest(null);
  }, [doc, laGestPornit, laSchimbare, laAles, laUnealta, laGestTerminat]);

  /** TASTA `S` ARMEAZĂ ÎNTINDEREA, Esc o lasă.
   *
   *  `S` e tasta din AutoCAD pentru STRETCH, iar arhitecții o au în degete —
   *  de-aia a cerut-o pe ea (15.10.2026). Nu se armează cât se scrie într-un
   *  câmp: acolo «s» e o literă. */
  useEffect(() => {
    const la = (e: KeyboardEvent) => {
      const unde = e.target as HTMLElement | null;
      if (unde && (unde.tagName === "INPUT" || unde.tagName === "TEXTAREA"
                   || unde.isContentEditable)) return;
      if (e.key === "Escape" && (unealta === "intinde" || gest?.fel === "intindeFer"
                                 || gest?.fel === "intindeTrag")) {
        setGest(null);
        laUnealta("sageata");
        return;
      }
      // ESC LASĂ COTA ÎNCEPUTĂ. O cotă se pune din DOUĂ apăsări, iar între ele
      // nu era nicio ieșire: primul punct rămânea pus, iar următoarea apăsare
      // oriunde pe planșă termina o cotă pe care n-o mai voia nimeni. «when we
      // press esc in the cota editor the cota should disappear if we haven't
      // placed it yet» (19.10.2026). Nimic nu se scrie în document — cota
      // nepusă trăiește numai în gest —, deci nu e nimic de anulat după.
      if (e.key === "Escape" && (unealta === "cota" || gest?.fel === "cotaNoua"
                                 || gest?.fel === "bandaNoua")) {
        setGest(null);
        laUnealta("sageata");
        return;
      }
      // ȘTERGE CE E ALES, cât ține gestul. Oprește și celelalte ascultătoare:
      // `Masa` are Delete-ul lui, pe obiectul din panou, iar amândouă pe
      // aceeași tastă ar șterge două lucruri dintr-o apăsare. Ascultătorul
      // pânzei se pune primul (efectele copilului merg înaintea părintelui),
      // deci oprirea chiar ajunge la timp.
      if ((e.key === "Delete" || e.key === "Backspace")
          && gest?.fel === "intindeTrag" && !doarCitire) {
        e.preventDefault();
        e.stopImmediatePropagation();
        laGestPornit();
        // An off layer is not in the window, as in CAD: the four window
        // gestures (delete, copy, drag, preview) must agree on that.
        laSchimbare(stergeCuprinsul(gest.doc0, gest.f, filtruFerestrei), true);
        setGest(null);
        laUnealta("sageata");
        return;
      }
      if (doarCitire || e.metaKey || e.ctrlKey || e.altKey) return;
      // SHIFT+S, nu S singur: `s` MUTĂ ÎN JOS obiectul ales, cu 5 cm, de când
      // există tastele WASD (v. `Masa`). Armarea pe `s` i-a luat tasta —
      // «now we have the down functionality on the S key» (15.10.2026) —, iar
      // o unealtă nouă n-are voie să ia o tastă care face deja ceva.
      if ((e.key === "s" || e.key === "S") && e.shiftKey) {
        e.preventDefault();
        setGest(null);
        laUnealta("intinde");
      }
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [doarCitire, unealta, gest, laUnealta, filtruFerestrei]);

  /** ⇧ ÎNTOARCE PIESA CARE E PE CALE SĂ FIE PUSĂ, din sfert în sfert.
   *
   *  Numai cât o unealtă de PUS e armată: în rest Shift rămâne ce era —
   *  adună la selecție, rotește piesa deja pusă, deschide fereastra de
   *  întindere. Se ascultă `keydown`, deci ținut apăsat se rotește o dată la
   *  fiecare repetare a tastei, ceea ce e chiar ce vrea mâna. */
  useEffect(() => {
    const pune = unealta === "mobila" || unealta === "lift"
      || unealta === "ghena";
    if (!pune) return;
    const la = (e: KeyboardEvent) => {
      if (e.key !== "Shift" || e.metaKey || e.ctrlKey || e.altKey) return;
      const unde = e.target as HTMLElement | null;
      if (unde && (unde.tagName === "INPUT" || unde.tagName === "TEXTAREA")) return;
      e.preventDefault();
      setRotFantoma((r) => (r + 90) % 360);
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [unealta]);

  /** COPIAZĂ ȘI LIPEȘTE O PORȚIUNE — ⌘C / ⌘V, Ctrl pe Windows.
   *
   *  Cerut pe 19.10.2026: «When we select a portion with shift S we should be
   *  able to copy it and paste with ctrl c and ctrl v (or command for Mac),
   *  and when placing a selected portion we should be able to flip it or
   *  rotate from buttons and keys».
   *
   *  ⌘C CERE O SELECȚIE DESCHISĂ (Shift+S, apoi fereastra trasă): de-acolo se
   *  știe exact ce e ales, și tot de-acolo se și șterge cu Delete. Fără una,
   *  tasta nu face nimic — nu se copiază „ce e sub cursor”.
   *
   *  CÂT SE SCRIE ÎNTR-UN CÂMP, tastele sunt litere: ⌘C într-un câmp de cotă
   *  copiază textul, nu planul. Aceeași pază ca la Shift+S.
   *
   *  TASTELE DE AȘEZARE (R, H, V) N-AU MODIFICATOR, deci stau în ascultătorul
   *  de dedesubt, care rulează numai cât bucata e în mână. */
  useEffect(() => {
    const la = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const unde = e.target as HTMLElement | null;
      if (unde && (unde.tagName === "INPUT" || unde.tagName === "TEXTAREA"
                   || unde.isContentEditable)) return;
      const tasta = e.key.toLowerCase();
      if (tasta === "c" && gest?.fel === "intindeTrag") {
        const b = copiazaCuprinsul(gest.doc0, gest.f, filtruFerestrei);
        if (!b) return;
        e.preventDefault();
        clipboard.current = b;
        return;
      }
      if (tasta === "v" && clipboard.current && !doarCitire) {
        e.preventDefault();
        // BUCATA APARE UNDE E CURSORUL. Dacă mâna n-a trecut încă peste pânză
        // de la încărcare, cade pe mijlocul a ce se vede — altfel ar apărea la
        // origine, adică de obicei în afara ecranului.
        const la0 = ultimulPunct.current
          ?? [plan.cadru.x + plan.cadru.w / 2, plan.cadru.y + plan.cadru.h / 2] as Pt;
        setGest({ fel: "lipeste", b: clipboard.current,
                  la: [rotunj(la0[0]), rotunj(la0[1])] });
        laUnealta("sageata");
      }
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [gest, doarCitire, laUnealta, plan.cadru, filtruFerestrei]);

  /** CÂT E BUCATA ÎN MÂNĂ: R o rotește, H și V o oglindesc, Esc o lasă.
   *
   *  Tastele sunt cele din orice editor de plan — R de la «rotate», H și V de
   *  la axele oglindirii —, și sunt aceleași cu ale butoanelor de deasupra
   *  bucății: cine le apasă cu mâna vede pe ecran ce tastă face același lucru.
   *  ⇧R rotește invers, ca peste tot în editor (v. `Masa`, comanda `RO`).
   *
   *  ASCULTĂTORUL EXISTĂ NUMAI CÂT ȚINE GESTUL, deci tastele nu fură nimic
   *  când nu e nimic de așezat: `h` e liber, dar `r` nu — iar fără paza asta
   *  ar fi început să rotească bucăți inexistente. */
  useEffect(() => {
    if (gest?.fel !== "lipeste") return;
    const la = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const unde = e.target as HTMLElement | null;
      if (unde && (unde.tagName === "INPUT" || unde.tagName === "TEXTAREA"
                   || unde.isContentEditable)) return;
      const tasta = e.key.toLowerCase();
      if (tasta === "escape") { e.preventDefault(); setGest(null); return; }
      if (tasta === "enter") {
        e.preventDefault();
        laGestPornit();
        const r = lipesteBucata(doc, gest.b, gest.la);
        laSchimbare(r.doc);
        laAles(r.ales);
        laGestTerminat();
        setGest(null);
        return;
      }
      if (tasta === "r") {
        e.preventDefault();
        setGest({ ...gest, b: rotesteBucata(gest.b, e.shiftKey ? -90 : 90) });
        return;
      }
      if (tasta === "h" || tasta === "v") {
        e.preventDefault();
        setGest({ ...gest, b: oglindesteBucata(gest.b, tasta as "h" | "v") });
      }
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [gest, doc, laSchimbare, laAles, laGestPornit, laGestTerminat]);

  /** ENTER ÎNCHEIE SCARA. Se încheia numai din dublu-clic pe pânză, iar cine
   *  nu știa asta rămânea prins în unealtă: singurul fel de a ieși era să
   *  ștergi scara. Cerut: «when we create the custom stair once we are done we
   *  should press enter. Now there is no way to exit the stair tool without
   *  deleting the stair» (07.10.2026). Dublu-clicul rămâne — cine l-a învățat
   *  nu-l pierde. */
  useEffect(() => {
    const la = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || doarCitire) return;
      // Nu când se scrie o cifră într-un câmp: acolo Enter o fixează.
      const unde = e.target as HTMLElement | null;
      if (unde && (unde.tagName === "INPUT" || unde.tagName === "TEXTAREA"
                   || unde.isContentEditable)) return;
      e.preventDefault();
      // CONTURUL LIBER ÎNAINTEA SCĂRII: dacă se desenează o cameră sau o zonă,
      // Enter o încheie pe ea. Cele două gesturi nu sunt niciodată deschise
      // deodată, dar ordinea face citirea limpede.
      if (gest?.fel === "camLibera") {
        incheieConturul(gest.puncte, felulZonei(unealta));
        return;
      }
      incheieScara();
    };
    window.addEventListener("keydown", la);
    return () => window.removeEventListener("keydown", la);
  }, [doarCitire, incheieScara, incheieConturul, gest, unealta]);
  /** Apăsare pe FOND. */
  const laFond = (e: React.PointerEvent) => {
    const panoreaza = panoramaPornita(e);
    // MIJLOCUL, DREAPTA ȘI SPAȚIUL SUNT DOAR ALE PANORAMĂRII: acolo apăsarea nu
    // înseamnă nimic altceva, deci se oprește aici. Butonul stâng cu săgeata
    // face amândouă — pornește panoramarea și trece mai departe, ca să stingă
    // selecția dacă tragerea nu vine.
    if (panoreaza && (e.button !== 0 || spatiu)) return;
    if (doarCitire || e.button !== 0) return;
    atinge();
    const p = pct(e);
    // BUCATA DIN MÂNĂ SE PUNE LA PRIMA APĂSARE, înaintea oricărei unelte:
    // cât ține lipirea, pânza n-are altă treabă. Bucata RĂMÂNE în clipboard,
    // deci se poate pune de mai multe ori — ⌘V o ia de la capăt.
    if (gest?.fel === "lipeste") {
      const la: Pt = [rotunj(p[0]), rotunj(p[1])];
      setGest(null);
      laGestPornit();
      const r = lipesteBucata(doc, gest.b, la);
      laSchimbare(r.doc);
      laAles(r.ales);
      laGestTerminat();
      return;
    }
    // ZONA DE DESFUMARE se trage ca un poligon liber — triunghi, trapez, ce
    // desenează arhitectul —, dar NU se zidește: e o bucată din camera peste
    // care e trasă, nu o încăpere nouă (v. `model::adaugaZona`).
    // SPARGE ZID — DOUĂ APĂSĂRI, CA O COTĂ.
    //
    // Era o tragere apucată de pe zid, și nu se vedea nimic: apăsarea pe un
    // perete se ridică prin `laObiect`, dar urcă apoi și la `laFond`, iar de
    // acolo gestul era stins înainte să apuce să se desenează — «the sparge
    // zid still doesn't work it doesn't select or draw points» (20.10.2026).
    // Cerut explicit ca la cotă: «It should let me do a section like a cota
    // basically and separate that part».
    //
    // Deci: prima apăsare pune capătul dintâi PE ZIDUL de sub deget, a doua îl
    // pune pe al doilea și rupe. Între ele, banda se vede sub cursor. Aici, în
    // `laFond`, fiindcă el prinde ORICE apăsare pe planșă — pe zid sau pe
    // golul dintre ele —, deci nu mai atârnă de cine a prins evenimentul
    // întâi.
    if (unealta === "demoleaza") {
      if (gest?.fel === "sparge") {
        const { i, t0, lung } = gest;
        const t1 = tLipit(i, p[lung]);
        setGest(null);
        if (Math.abs(t1 - t0) >= PAS) {
          laGestPornit();
          laSchimbare(demoleazaBucata(doc, i, t0, t1));
          laAles(null);
          laGestTerminat();
        }
        return;
      }
      const c = capatSparturii(p);
      if (c) setGest({ fel: "sparge", i: c.i, lung: c.lung, t0: c.t, t1: c.t });
      return;
    }
    if (unealta === "cota") {
      // PUNCTELE SE LIPESC DE COLȚURILE DESENULUI. O cotă pusă «cam pe colț»
      // scrie o cifră cu doi centimetri greșită, iar cifra aia se citește ca o
      // măsură a proiectului. Colțurile zidurilor sunt chiar ce vrea măsurat
      // cineva care pune o cotă de mână.
      const q = colturiApropiate(
        p, gest?.fel === "cotaNoua" ? gest.a : undefined);
      if (gest?.fel === "cotaNoua") {
        const a = gest.a;
        setGest(null);
        laUnealta("sageata");
        import("./model").then(({ adaugaCota }) => {
          const d = adaugaCota(doc, a, q);
          if (d === doc) return;
          laGestPornit();
          laSchimbare(d);
          laGestTerminat();
        });
        return;
      }
      setGest({ fel: "cotaNoua", a: q, la: q });
      return;
    }
    if (unealta === "intinde") {
      // a doua apăsare PUNE tragerea; prima deschide fereastra
      if (gest?.fel === "intindeTrag") {
        if (gest.dep[0] || gest.dep[1]) {
          laGestPornit();
          laSchimbare(mutaCuprinsul(gest.doc0, gest.f, gest.dep, filtruFerestrei), true);
        }
        setGest(null);
        laUnealta("sageata");
        return;
      }
      setGest({ fel: "intindeFer", de: [rotunj(p[0]), rotunj(p[1])],
                la: [rotunj(p[0]), rotunj(p[1])] });
      return;
    }
    if (unealta === "cameraLibera" || unealta === "zonaDesfumare"
        || unealta === "cameraFaraPereti") {
      const zona = felulZonei(unealta);
      const q: Pt = [rotunj(p[0]), rotunj(p[1])];
      const puncte = gest?.fel === "camLibera" ? gest.puncte : [];
      // ÎNCHIDEREA E O APĂSARE PE PRIMUL VÂRF, cum se închide un contur pe
      // planșă. `LIPIT` e cât de aproape trebuie nimerit — mai puțin ar cere
      // precizie de pixel, mai mult ar închide conturul din greșeală.
      const LIPIT = 0.3;
      if (puncte.length >= 3
          && Math.hypot(q[0] - puncte[0][0], q[1] - puncte[0][1]) < LIPIT) {
        incheieConturul(puncte, zona);
        return;
      }
      setGest({ fel: "camLibera", puncte: [...puncte, q], la: q });
      return;
    }
    if (FORME.includes(unealta) || unealta === "balcon") {
      // BALCONUL E GESTUL DE CAMERĂ. Se trage un dreptunghi, la fel; ce se
      // schimbă e cu ce se închide (parapeți, pe laturile libere) și ce iese
      // (spațiu exterior, care nu intră în suprafața utilă).
      setGest({ fel: "cameraNoua", de: [rotunj(p[0]), rotunj(p[1])],
                la: [rotunj(p[0]), rotunj(p[1])],
                forma: unealta === "balcon" ? "camera" : (unealta as FormaCamera),
                ...(unealta === "balcon" ? { balcon: true } : {}) });
      return;
    }
    if (unealta === "perete" || unealta === "arc" || unealta === "parapet") {
      // A DOUA APĂSARE ÎNCHEIE ZIDUL ÎNCEPUT DIN CLIC (v. `laRidicare`). Prea
      // scurt încă, apăsarea nu face nimic: gestul rămâne deschis, ca omul să
      // poată duce capătul mai departe în loc să înceapă altul din același loc.
      if (gest?.fel === "peretNou") {
        // Fără sfârșit de gest: zidul se pune într-o promisiune, iar camerele
        // se refac odată cu el (v. `puneZidul`).
        if (puneZidul(gest)) setGest(null);
        return;
      }
      setLungimeScrisa(null);
      // Capătul de pornire se lipește de axul zidului de lângă, dacă e vreunul
      // la un sfert de metru — v. `pereteNouLipit`.
      const q: Pt = [rotunj(p[0]), rotunj(p[1])];
      const de = pereteNouLipit(
        doc, q, q, unealta === "parapet" ? "parapet" : "perete").de;
      pus.current = false;
      setGest({ fel: "peretNou", de, la: de, arc: unealta === "arc",
                zid: unealta === "parapet" ? "parapet" : "perete" });
      return;
    }
    if (unealta.startsWith("scaraGata_")) {
      // A PRESET IS PLACED AS THE CUSTOM STAIR IS DRAWN: press where it
      // starts, against a wall when there is one, drag the way it climbs
      // (asked by Tudor, 28.10.2026). It used to land centred on one click,
      // half into the wall it was meant to lean on. The second press, after
      // a press-and-release that did not drag, puts it where the ghost is.
      const inMana = scaraGataViu.current;
      if (inMana) {
        if (inMana.dir) scrieScaraGata(inMana);
        return;
      }
      const q: Pt = [rotunj(p[0]), rotunj(p[1])];
      // walls only: pressed by a dining table, the stair ran along the
      // table's edge instead of the wall behind it (tested 28.10.2026)
      const fete = fetelePornirii(muchiileDinJur(plan, { faraPiese: true }), q);
      const s = pornireaScarii(fete, q, directiaScarii(fete, null), LATIME_RAMPA);
      const nou: Extract<Gest, { fel: "scaraGata" }> = {
        fel: "scaraGata", cheie: unealta.slice("scaraGata_".length),
        q, fete, de: s.de, la: q, dir: null, oglinda: false, trepte: null,
        apasat: true };
      scaraGataViu.current = nou;
      setGest(nou);
      return;
    }
    if (unealta === "scara") {
      // SCARA SE TRAGE, NU SE PUNE. Apasă o dată — acolo începe rampa; miști —
      // se așază contratrepte, una la fiecare treaptă, și ți se spune câte au
      // mai rămas de urcat; apeși iar — rampa s-a terminat. A doua rampă începe
      // la următoarea apăsare, iar PODESTUL dintre ele se face singur. Când nu
      // mai rămâne nicio contratreaptă, scara se scrie și unealta se încheie.
      //
      // Era pusă ca o cutie, cu rampele împărțite în ea de desen. Cutia e însă
      // rezultatul scării, nu scara: «we need the stair to be created in this
      // exact manner shown in the video» (05.10.2026).
      const q: Pt = [rotunj(p[0]), rotunj(p[1])];
      const hNivel = doc.storey_height_m ?? 2.8;
      const st: StareScara = gest?.fel === "scaraNoua"
        ? gest
        : { faza: "rampa", rampe: [], de: null, la: q };
      // DUBLU-CLIC ÎNCHEIE SCARA. Nu se mai încheie singură: câte rampe are o
      // spune omul, deci tot el spune și când a terminat.
      const pas = pasulScarii(st, q, hNivel, e.detail >= 2);
      if (pas.fel === "nimic") return;
      setLungimeScrisa(null);
      if (pas.fel === "gata") {
        scrieScara(pas.rampe, LATIME_RAMPA);
        return;
      }
      setGest({ fel: "scaraNoua", latime: LATIME_RAMPA, ...pas.stare });
      return;
    }
    if (unealta === "mobila"
        || unealta === "lift" || unealta === "ghena") {
      // SCARA ȘI LIFTUL SE PUN CA ORICE PIESĂ. Erau bucăți de nucleu, în
      // `core_parts` — corect ca loc în format, dar acolo nu se pot apuca, roti
      // sau redimensiona, iar Cătălin le vrea exact ca pe mobilier: le pui, le
      // vezi înainte, le tragi de colț. Aceleași drumuri, deci același cod.
      const fel = unealta === "mobila" ? piesa : unealta;
      import("./model").then(({ adaugaMobila, rotesteObiectul }) => {
        // `felulPiesei`, nu lista casei: o piesă adusă din CAD nu e în
        // `PIESE_MOBILIER`, deci căutată acolo ieșea `undefined` și se punea
        // la măsura implicită de 1,60 × 0,90 — un fotoliu de 0,79 × 0,95
        // ajungea lat de un metru șase.
        const m = felulPiesei(fel);
        // UNDE A ARĂTAT FANTOMA, nu unde e cursorul — v. `piesaLipita`.
        const r = adaugaMobila(doc, fel, piesaLipita(fel, p), m);
        // UNGHIUL FANTOMEI INTRĂ ÎN PIESĂ: altfel ce ai văzut sub cursor și ce
        // s-a pus ar fi două lucruri diferite.
        const cuRot = rotFantoma
          ? rotesteObiectul(r.doc, r.ales, rotFantoma) : r.doc;
        laGestPornit();
        laSchimbare(cuRot);
        // Se alege pe loc: cine tocmai a pus o canapea vrea de obicei s-o
        // rotească sau s-o mute, nu s-o caute înapoi cu un al doilea click.
        laAles(r.ales);
      });
      return;
    }
    // THE LED STRIP (sheet row 114) is drawn like a cote: a first click on a
    // wall face, a second along it, clamped to the face's stops.
    if (unealta === "aparat" && aparat === "banda_led") {
      if (gest?.fel === "bandaNoua") {
        const L = lungimeaBenzii(gest, p);
        if (Math.abs(L) < LUNGIME_MINIMA_BANDA) { setAviz(t("panza.banda_scurta")); return; }
        const { a, rot } = gest;
        setGest(null);
        setAviz(null);
        import("./model").then(({ adaugaBandaLed }) => {
          const r = adaugaBandaLed(doc, a, rot, L);
          laGestPornit();
          laSchimbare(r.doc);
          laAles(r.ales);
        });
        return;
      }
      const s = lipitDeZid(doc, p, "banda_led");
      if (!gazdaDozei(plan, { c: s.p, deg: s.rot })) {
        setAviz(t("panza.banda_fara_zid"));
        return;
      }
      const f = capeteleFetei(plan, s.p, s.rot);
      setAviz(null);
      setGest({ fel: "bandaNoua", a: s.p, rot: s.rot, min: f?.stanga ?? -Infinity,
                max: f?.dreapta ?? Infinity, la: 0 });
      return;
    }
    if (unealta === "aparat") {
      // LIPIT DE ZID LA PUNERE, nu cât e tras: v. `model::lipitDeZid`. O priză
      // căzută în mijlocul camerei nu e o priză, e o greșeală de mână — dar una
      // TRASĂ prin mijlocul camerei e cineva care o duce pe alt perete.
      // Then slid along its face to a place its symbol fits (`cote::locPeFata`).
      const lipit = lipitDeZid(doc, p, aparat);
      const unde = { p: locPeFata(plan, aparat, lipit.p, lipit.rot), rot: lipit.rot };
      const fel = aparat;
      import("./model").then(({ adaugaAparat }) => {
        const r = adaugaAparat(doc, fel, unde.p, unde.rot);
        laGestPornit();
        laSchimbare(r.doc);
        laAles(r.ales);
      });
      return;
    }
    if (USI[unealta] || unealta === "fereastra") {
      import("./model").then(({ adaugaGol }) => {
        const u = USI[unealta];
        const r = u ? adaugaGol(doc, "doors", p, u.latime, u.model)
                    : adaugaGol(doc, "windows", p, 1.2);
        if (r) {
          laGestPornit();
          laSchimbare(r.doc);
        }
        // Când `r` e `null`, apăsarea a căzut lângă un zid, nu pe el: un gol
        // care nu stă într-un perete e desenat în aer, iar catalogul îl refuză
        // («names wall …, which is not here»). Unealta rămâne activă, deci
        // următoarea apăsare e o a doua încercare, nu o repornire.
      });
      return;
    }
    // CHENARUL DE ALES. Cerut de arhitect (27.10.2026): «ar trebui să putem
    // face o selecție multiplă cu ajutorul mouse-ului: click & drag ca să facă
    // un chenar cu ajutorul căruia să putem selecta mai multe obiecte deodată
    // (cea cu Shift+S), și să meargă și la mobilă, nu doar la pereți».
    //
    // DESELECTAREA RĂMÂNE, și e chiar același gest: un clic pe fond stinge
    // selecția, o TRAGERE o face. Care din ele a fost se știe abia la
    // ridicarea degetului (v. `laRidicare`), deci aici se ține doar punctul de
    // pornire — nimic nu se stinge încă.
    //
    // NEROTUNJIT: chenarul nu pune nimic în document, doar cuprinde, iar pe
    // grila de 5 cm marginea ar fi sărit vizibil sub deget.
    setGest({ fel: "alegeFer", de: p, la: p });
  };

  /** UN PUNCT DIN PLAN, adus în cadrul neîntors al piesei (și invers, cu
 *  `spreP = true`).
 *
 *  Scara trasă își ține rampele — și formele podestelor — în coordonatele
 *  PLANULUI, dar se desenează într-un cadru mutat în centrul piesei și întors
 *  cu unghiul ei (v. `Desen`). Mâna vine în coordonate de plan: pe o scară
 *  așezată strâmb, un deget dus spre dreapta pe ecran nu e dreapta podestului,
 *  iar fără întoarcerea asta punctul ar pleca pe diagonală. */
function inCadrulPiesei(pi: { c: Pt; deg: number } | undefined, p: Pt,
                        spreP = false): Pt {
  if (!pi) return p;
  const rad = ((spreP ? pi.deg : -pi.deg) * Math.PI) / 180;
  const co = Math.cos(rad), si = Math.sin(rad);
  const dx = p[0] - pi.c[0], dy = p[1] - pi.c[1];
  return [pi.c[0] + dx * co - dy * si, pi.c[1] + dx * si + dy * co];
}

/** Apăsare pe un OBIECT: îl selectează ȘI îl apucă.
   *
   *  SE TRAGE DE CORPUL PERETELUI, nu doar de apucătoarea din mijloc. Așa se
   *  face în orice editor de plan: pui degetul pe zid și-l împingi. Cu
   *  apucătoarea singură, gestul cerea întâi un click de selecție și apoi
   *  nimerit un cerc de 7 px — verificat în editor: o tragere de corpul
   *  peretelui nu mișca absolut nimic, ceea ce arată exact ca «nu se poate».
   *
   *  Apucătoarele rămân, fiindcă fac altceva: cele de la capete LUNGESC zidul,
   *  iar corpul îl MUTĂ. Două gesturi, două locuri de apucat.
   *
   *  Nimic nu intră în istoric până nu se mișcă ceva — v. `Gest.pornit`. */
  const laObiect = (a: Ales, e: React.PointerEvent) => {
    if (doarCitire || e.button !== 0 || spatiu) return;
    // Degetul pe planșă = vederea e a omului, nu a încadrării (v. `atinge`).
    atinge();
    // CÂT E O BUCATĂ ÎN MÂNĂ, orice apăsare o PUNE — și pe un obiect, nu doar
    // pe fond. Fără rândul ăsta, o bucată lipită peste un plan deja desenat
    // (adică mai tot timpul) ar fi apucat zidul de dedesubt în loc să se
    // așeze, iar de pus n-ai fi putut-o pune decât pe o bucată goală de
    // planșă. Gestul e același ca în `laFond`; de-aia trece prin el.
    if (gest?.fel === "lipeste") { laFond(e); return; }
    // PENSULA VOPSEȘTE ȘI NU SELECTEAZĂ. Un click cu pensula pe o cameră e o
    // editare, nu o alegere: selecția rămasă aprinsă ar fi ținut apucătoarele
    // peste desen, iar următorul click ar fi apucat camera în loc s-o vopsească.
    if (unealta === "pensula" && a?.fel === "camera" && laFinisaj) {
      laFinisaj(a.i);
      return;
    }
    // SHIFT ADAUGĂ LA SELECȚIE — pe camere și pe pereți. Nu apucă nimic: cine
    // ține Shift alege, nu trage, altfel al doilea zid ar fi plecat din loc
    // chiar în clipa în care se aduna la primul.
    //
    // SHIFT, NU ⌘: cerut așa, și e tasta cu care se adună un ȘIR în orice
    // program. Pe mobilier și pe aparate Shift rămâne ROTIREA (mai jos) —
    // feluri diferite de obiect, deci gesturile nu se calcă.
    // ⇧ PE UN ZID APUCĂ LINIA ÎNTREAGĂ; ⇧ pe o cameră adună, ca până acum.
    //
    // Cele două înțelesuri ale lui Shift pe un zid — «adună la selecție» și
    // «mută linia toată» — se împacă pe MIȘCARE, ca la mobilă: dacă degetul se
    // ridică fără ca zidul să se fi clintit, gestul a fost un clic, deci o
    // adunare (v. `laRidicare`). Așa nu se pierde selecția de grup.
    if (a?.fel === "perete" && e.shiftKey && !doarCitire) {
      setGest({ ...apucaCorpulZidului(doc, plan, a.i, pct(e), pereteleAlese,
                                      true, proaspete("perete")),
                fel: "glisPerete", pornit: false, lipit: false,
                adunaDacaNuSeMisca: true });
      return;
    }
    if (a?.fel === "camera" && e.shiftKey) {
      laAles(a, true);
      return;
    }
    // ⇧ PE UN GOL ADUNĂ LA SELECȚIE, ca pe cameră. Arătat de arhitect
    // (22.10.2026): «încă nu pot selecta mai multe uși; ar trebui să mă lase cu
    // Shift și select ca la mobilă».
    //
    // LISTA EXISTA DEJA (`Masa::alege`, ramura `gol`), dar nimeni n-o chema:
    // `laObiect` preface ⇧ în «adună» PE FELURI, una câte una — zid, cameră,
    // mobilă, aparat —, iar golul n-avea ramura lui. Apăsarea cădea deci pe
    // `laAles(a)` fără `adauga`, adică pe alegerea simplă, care tocmai golește
    // lista. Jumătatea de jos a reparației de azi era scrisă, dar nelegată.
    //
    // SE IESE PE LOC, fără să mai pornească gestul de mutat golul: ⇧+clic e o
    // alegere, nu o tragere.
    if (a?.fel === "gol" && e.shiftKey) {
      laAles(a, true);
      return;
    }
    // …ȘI PE MOBILĂ TOT SHIFT, ca pe camere și pe ziduri. Cerut anume:
    // «it should allow me to multi select by pressing shift and seeing the
    // highlight of the selected elements» (19.10.2026). ⌘ a fost prima mea
    // încercare — fiindcă Shift ținut pe o piesă o ROTEȘTE de mult —, dar o
    // a doua tastă pentru același înțeles e exact ce nu ține mâna minte.
    //
    // CELE DOUĂ GESTURI SE ÎMPACĂ PE MIȘCARE, nu pe tastă: Shift+APĂSARE
    // adună la selecție, Shift+TRAGERE rotește. Se hotărăște la ridicarea
    // degetului (v. `laRidicare`): dacă rotirea n-a apucat să schimbe nimic,
    // apăsarea a fost un clic. Așa nu se pierde nicio rotire învățată.
    // ⌘ rămâne și el, pentru cine l-a prins în ora în care a existat singur.
    if (a?.fel === "mobila" && (e.metaKey || e.ctrlKey)) {
      laAles(a, true);
      return;
    }
    // SHIFT PE O PIESĂ NU ALEGE ÎNCĂ. Hotărârea se ia la ridicarea degetului:
    // tragere = rotire, clic = adunare (v. `laRidicare`). Chemat aici,
    // `laAles(a)` fără `adauga` GOLEA lista și o punea pe ea singură, iar
    // adunarea de pe urmă o scotea înapoi — selecția rămânea goală după
    // fiecare Shift+clic, adică exact «the shift multi select still doesn't
    // work» (19.10.2026). Era greșeala mea de la reparația dinainte: proba se
    // uita doar la ULTIMA chemare, nu la câte sunt.
    const shiftPeMobila = a?.fel === "mobila" && e.shiftKey;
    if (!shiftPeMobila) laAles(a);
    if (a?.fel === "aparat") {
      const q = pct(e);
      if (e.shiftKey) {
        const el = plan.electrice.find((x) => x.idx === a.i);
        if (el) {
          setGest({ fel: "roteste", ce: "aparat", i: a.i, c: el.c, pornit: false,
                    rot0: el.deg, aplicat: el.deg, doc0: doc,
                    unghi0: (Math.atan2(q[1] - el.c[1], q[0] - el.c[0]) * 180) / Math.PI });
        }
        return;
      }
      setGest({ fel: "mutaAparat", i: a.i, de: q, pornit: false });
      return;
    }
    if (a?.fel === "anvelopa") return;      // doar se alege; mărimile din panou
    if (a?.fel === "nucleuParte") {
      const c = cadrulPartii(plan.nucleu?.parti[a.i]?.inel ?? []);
      setGest({ fel: "mutaParte", i: a.i, de: pct(e), doc0: doc,
                cutie0: { x: [c.x, c.x + c.w], y: [c.y, c.y + c.h] },
                pornit: false });
      return;
    }
    if (a?.fel === "nucleu") {
      setGest({ fel: "mutaNucleu", de: pct(e), pornit: false });
      return;
    }
    if (a?.fel === "perete") {
      // TRAS DE CORP, ZIDUL SE MUTĂ ÎNTREG, liber în plan — ales dinainte sau
      // nu. Redimensionarea e a cadrului, care apare doar pe zidul ales.
      //
      // Apucarea stă afară din componentă (v. `apucaCorpulZidului`): ea taie
      // linia la hotarul camerei de sub deget și spune ce pleacă. Grupul se
      // îngheață ACUM, fiindcă selecția se poate schimba în timpul gestului,
      // iar zidurile trase trebuie să rămână cele aprinse când a început.
      setGest({ ...apucaCorpulZidului(doc, plan, a.i, pct(e), pereteleAlese,
                                      false, proaspete("perete")),
                fel: "glisPerete", pornit: false, lipit: false });
    } else if (a?.fel === "gol") {
      const g = (doc.plan?.[a.fel2] ?? [])[a.i];
      const rad = ((g?.rot_deg ?? 0) * Math.PI) / 180;
      setGest({ fel: "mutaGol", fel2: a.fel2, i: a.i, de: pct(e),
                dir: [Math.cos(rad), Math.sin(rad)], pornit: false, la: null,
                p0: [g?.x.a ?? 0, g?.y.a ?? 0] });
    }
    // CAMERA SE ALEGE ȘI ATÂT — nici trasă, nici întoarsă (v. antetul): `laAles`
    // de mai sus a făcut tot ce avea de făcut, iar mărimea se schimbă din
    // mânerele de latură.
    //
    // DAR CHENARUL DE ALES PORNEȘTE ȘI DE PE EA. O cameră acoperă aproape tot
    // planul, deci o tragere începută oriunde în interiorul ei cădea pe ea și
    // chenarul nu mai pornea niciodată: «selecția funcționează doar că atunci
    // când aș vrea să încep o selecție în interiorul camerei el va selecta
    // camera și nu mai pornește chenarul de selecție» (27.10.2026).
    //
    // CELE DOUĂ GESTURI SE ÎMPACĂ PE MIȘCARE, ca peste tot aici (v. Shift pe o
    // piesă): CLIC pe cameră o alege, TRAGERE face chenar. Care din ele a fost
    // se știe abia la ridicarea degetului — `dinObiect` îi spune lui
    // `laRidicare` să NU stingă selecția dacă degetul n-a mers nicăieri.
    if (a?.fel === "camera" && !e.shiftKey) {
      setGest({ fel: "alegeFer", de: pct(e), la: pct(e), dinObiect: true });
      return;
    }
    if (a?.fel === "mobila") {
      const q = pct(e);
      // SHIFT ȚINUT = ROTIRE, chiar de pe corpul piesei. Mânerul de deasupra
      // rămâne, dar cere întâi o selecție și apoi nimerit un cerc de 7 px; cu
      // Shift, apuci piesa oriunde și o întorci.
      if (e.shiftKey) {
        const pi = plan.piese[a.i];
        if (!pi) return;
        setGest({ fel: "roteste", ce: "mobila", i: a.i, c: pi.c, pornit: false,
                  rot0: pi.deg, aplicat: pi.deg, doc0: doc,
                  // `adunaDacaNuSeRoteste`: dacă degetul se ridică fără ca
                  // rotirea să fi mișcat ceva, gestul a fost un CLIC cu Shift,
                  // adică o adunare la selecție (v. `laRidicare`).
                  adunaDacaNuSeRoteste: true,
                  unghi0: (Math.atan2(q[1] - pi.c[1], q[0] - pi.c[0]) * 180)
                          / Math.PI });
        return;
      }
      setGest({ fel: "mutaMobila", i: a.i, de: q, doc0: doc,
                cutie0: plan.piese[a.i] ? cutiaPiesei(plan.piese[a.i]) : null,
                pornit: false });
    }
    // SCARA SE TRAGE DE ORICE PARTE A EI — rampă sau podest —, și se mută
    // ÎNTREAGĂ. Lipirea podestului de rampe, cerută când el se muta singur
    // («make sure we can also click and drag it around and it should try to
    // snap to the stair runs», 13.10.2026), nu mai are ce lipi: mutat cu toată
    // scara, podestul nu pleacă niciodată de pe rampele lui.
    //
    // Punctele podestului se trag mai departe din pătratele de mai jos; ele
    // sunt singurul gest care schimbă FORMA unei părți.
    if (a?.fel === "scaraParte" && !doarCitire) {
      // the box of the drawn shape, flights and landings, not the piece's
      // stored size: an imported stair can carry a box that does not match
      // its parts
      const sc = doc.plan?.furniture?.[a.i]?.scara;
      const c = sc && cutiaScarii(sc);
      setGest({ fel: "mutaScara", i: a.i, de: pct(e), doc0: doc,
                cutie0: c ? { x: [c.cx - c.w / 2, c.cx + c.w / 2],
                              y: [c.cy - c.h / 2, c.cy + c.h / 2] } : null,
                pornit: false });
    }
  };

  /** Axele de trasare, citite din pereți — o socoteală pe document, nu pe
   *  fiecare mișcare de mouse. */
  const axeleLui = useMemo(() => (araAxe ? axele(doc) : []), [araAxe, doc]);
  /** Până unde se trag axele — v. `model::cutiaDesenului`. */
  const cutiaDes = useMemo(() => cutiaDesenului(plan), [plan]);
  /** Vecinii, derivați o dată pe document — fiecare cu axele LUI: un corp își
   *  are trasarea lui, numerotată de la el, nu continuată de la vecin. */
  const veciniDesenati = useMemo(() => (vecini ?? []).map((v) => ({
    ...v, plan: deriva(v.doc), axe: araAxe ? axele(v.doc) : [],
  })), [vecini, araAxe]);

  const zidAles = ales?.fel === "perete" ? plan.pereti[ales.i] : null;
  const osia = zidAles ? osiaPeretelui(zidAles.inel) : null;

  /** Axul peretelui ales — cel scris, altfel dedus din dreptunghi. Pe el stau
   *  toate apucătoarele: un zid rotit are cadrul de încadrare mai mare decât el
   *  însuși, deci apucătoarele așezate pe cadru ar pluti pe lângă perete. */
  const axAles = useMemo(() => {
    if (!zidAles) return null;
    return zidAles.ax ?? axulPeretelui(zidAles.inel);
  }, [zidAles]);

  /** CAPETELE ZIDULUI ALES, de tras — pe orice zid, drept sau curb.
   *
   *  Au fost o vreme doar pe cel curb, iar zidul drept primise în loc un cadru
   *  cu patru pătrățele în colțuri. Cerut înapoi cum era: «doar două cercuri,
   *  unul în stânga și unul în dreapta, care mă lasă să mișc capetele liber».
   *  Și chiar liber — capătul se duce oriunde în plan, deci zidul se rotește și
   *  se lungește după mână (v. `model::mutaCapatulSingur`), nu doar se
   *  întinde pe axa lui. Grosimea rămâne a laturilor lungi. */
  const manere = useMemo(() => {
    if (!axAles) return [];
    const [a, b] = axAles;
    return [
      { fel: "capat" as const, p: a, capat: 0 as const },
      { fel: "capat" as const, p: b, capat: 1 as const },
    ];
  }, [axAles, zidAles]);

  /** Cadrul zidului ales, drept: colțuri și laturi de tras. */
  const cadruAles = useMemo(() => {
    if (ales?.fel !== "perete") return null;
    const w = doc.plan?.walls?.[ales.i];
    return w ? cadrulPeretelui(w) : null;
  }, [ales, doc]);

  /** LATURILE CAMEREI ALESE, de tras.
   *
   *  Numai cele care chiar pot pleca: una ținută de un zid al conturului nu se
   *  clintește (v. `model::mutaLaturaCamerei`), iar o apucătoare care nu face
   *  nimic e mai rea decât niciuna. Se recalculează la fiecare document, deci
   *  urmează latura sub mână cât ține tragerea. */
  const laturiAlese = useMemo(
    () => (ales?.fel === "camera" ? laturileCamerei(doc, ales.i)
      .filter((l) => !l.tintuita && l.sus - l.jos > 0.25) : []),
    [ales, doc]);

  /** Se vede apucătoarea de curbură? Numai cu bifa din panou, sau pe un zid
   *  care e deja curb. */
  const curbabil = curbPermis || Math.abs(zidAles?.bulge ?? 0) > 1e-6;

  /** CÂT DE APROAPE DE AXA DE PORNIRE SE LIPEȘTE bucata trasă, în pixeli.
   *
   *  Axele sunt cele două linii punctate prin locul de unde a plecat bucata:
   *  una pe orizontală, una pe verticală. Adusă aproape de una, tragerea se
   *  așază exact pe ea — așa se mută o bucată «drept în jos» fără să scape un
   *  centimetru în lateral.
   *
   *  8 px, «not very sensitive though» (15.10.2026): cât o palmă de ecran, nu
   *  cât un sfert de cameră. În PIXELI, nu în metri, ca să prindă la fel la
   *  orice mărire. */
  const LIPIRE_AX_PX = 8;
  const R = 7 / k;             // apucătoarea: 7 px, oricât ar fi mărirea

  /** The box chains as `Desen` drew them, wanted only while a box or another
   *  wall device is selected: its blue cotes clear them, and listening always
   *  would redraw the canvas twice on every step of every drag. */
  const [lanturiDesenate, setLanturiDesenate] = useState<readonly LantDoza[]>();
  const dozaAleasa = ales?.fel === "aparat"
    && plan.electrice.some((e) => e.idx === ales.i && ePePerete(e.kind));

  /** Distanțele obiectului ales până la vecini — se refac la fiecare mutare,
   *  fiindcă `plan` se reface; costă o trecere peste pereți, piese și nucleu. */
  const coteObiect = useMemo<Cota[]>(
    () => coteleObiectului(plan, ales, view?.k, lanturiDesenate),
    [plan, ales, view?.k, lanturiDesenate]);

  /** CÂT E OBIECTUL ALES — lățimea și adâncimea lui, scrise pe planșă.
   *
   *  Cerut de arhitect (21.10.2026): «mi-ar plăcea să văd, pe lângă cote,
   *  dimensiunea obiectului selectat și pe plan». Până acum măsurile lui se
   *  citeau numai din panou, iar cotele de pe planșă spuneau cât e până la
   *  VECINI, nu cât e el.
   *
   *  ÎN CADRUL PIESEI, nu pe axele planului: o canapea întoarsă cu 30° are
   *  lățimea ei pe axa ei, iar cutia din jur ar fi spus o cifră care nu e
   *  scrisă nicăieri și pe care n-o poate schimba nimeni.
   *
   *  NU SE POT SCRIE, și de-aia stau deoparte de `coteObiect`: cotele alea
   *  sunt DISTANȚE, iar o cifră tastată în ele MUTĂ obiectul (v. `Cota::muta`).
   *  Măsura piesei s-ar schimba din cu totul altceva — redimensionare —, iar un
   *  câmp care arată ca celelalte dar face altceva e mai rău decât unul care
   *  lipsește. Mărimea se scrie mai departe în panou. */
  const marimeaAlesului = useMemo<Cota[]>(() => {
    if (ales?.fel !== "mobila") return [];
    const pi = plan.piese[ales.i];
    if (!pi || !(pi.w > 0) || !(pi.h > 0)) return [];
    const rad = (pi.deg * Math.PI) / 180;
    const co = Math.cos(rad), si = Math.sin(rad);
    const lume = (x: number, y: number): Pt =>
      [pi.c[0] + x * co - y * si, pi.c[1] + x * si + y * co];
    // o palmă în afara piesei, ca linia să nu cadă peste desenul ei
    const o = COTA_PX.gol / k;
    const w2 = pi.w / 2, h2 = pi.h / 2;
    return [
      { a: lume(-w2, h2 + o), b: lume(w2, h2 + o), valoare: pi.w },
      { a: lume(w2 + o, -h2), b: lume(w2 + o, h2), valoare: pi.h },
    ];
  }, [plan, ales, k]);

  /** CE COTĂ E DESCHISĂ LA SCRIS, și ce s-a tastat în ea.
   *
   *  Poziția în `coteObiect`, nu cota însăși: lista se reface la fiecare
   *  document, iar o cotă ținută de obiect ar fi rămas cea de dinaintea
   *  ultimei mutări. `text` e `null` cât n-a tastat nimeni — atunci câmpul
   *  arată măsura de acum, ca la cifra zidului tras.
   *
   *  SE ÎNCHIDE LA SCHIMBAREA SELECȚIEI: cotele sunt ale obiectului ales, deci
   *  o casetă rămasă deschisă peste alt obiect ar scrie într-o măsură care nu
   *  mai există. */
  /** COTA PLANȘEI deschisă la scris — cea din desen, nu a obiectului ales.
   *  Ține cota ÎNSĂȘI, nu un indice: lista desenului se reface la fiecare
   *  document și nu e a noastră. */
  const [cotaPlan, setCotaPlan] =
    useState<{ c: Cota; text: string | null } | null>(null);
  const [cotaScrisa, setCotaScrisa] =
    useState<{ i: number; text: string | null } | null>(null);
  useEffect(() => setCotaScrisa(null), [ales]);

  /** DUCE OBIECTUL ALES PÂNĂ CÂND GOLUL MĂSURAT DE COTĂ E CEL SCRIS.
   *
   *  Deplasarea iese din cotă, nu din cursor: `semn × (măsurat − scris)` pe axa
   *  ei (v. `cote::Cota::muta`). Se rotunjește la MILIMETRU, nu la pasul de 5
   *  cm al mâinii — cine tastează 0,83 a cerut 0,83, iar lipirea la pas ar fi
   *  exact greșeala pe care câmpul o repară.
   *
   *  PORNIT ȘI SCHIMBARE VIE, DAR FĂRĂ «TERMINAT». Primele două trebuie:
   *  pornirea pune documentul de dinainte în istoric (deci ⌘Z se întoarce la
   *  el) și îl reține ca bază, iar `viu` cere gazdei tot ce urmează după o
   *  mutare de zid — mobila îl urmează, conturul îl urmează, camerele se refac
   *  (v. `pages/Editor::conturulDupaDesen`). Al treilea NU: el reface camerele
   *  o dată, la RIDICAREA degetului, citind documentul din `docRef` — care,
   *  chemat în aceeași tură de JavaScript, e încă cel de DINAINTE de mutare.
   *  Chemat aici, ștergea exact schimbarea tocmai făcută: cifra se scria, se
   *  vedea o clipă, și planul rămânea cum fusese. */
  /** COTA DINTRE AXE deschisă la scris: care dintre ele, și ce s-a tastat.
   *
   *  Stare separată de `cotaScrisa`, fiindcă nu se alege nimic ca s-o
   *  deschizi — se apasă pe cifră — și fiindcă ce o scrie e alt gest:
   *  `mutaAxul`, nu `mutaObiectul`. Selecția intră totuși în socoteală, dar
   *  numai ca să spună CARE capăt pleacă (v. `scrieCotaAxului`). */
  const [cotaAx, setCotaAx] = useState<{ j: number; text: string | null } | null>(null);

  /** Scrie distanța dintre două axe: mută axa a DOUA, ca o tragere de ea.
   *
   *  ACELAȘI DRUM CA TRAGEREA, deliberat: cine scrie 6,20 în loc să tragă
   *  până acolo trebuie să obțină exact același desen. De-aia se cheamă tot
   *  `mutaAxul`, care duce cu el întregul run de ziduri al axei — și, prin el,
   *  camerele care se sprijină pe ele.
   *
   *  SE MUTĂ AXA ALEASĂ, dacă e unul din capetele cotei. Cerut de arhitect
   *  (22.10.2026): «aici am modificat cota interax și a mutat axa B. Aș vrea să
   *  mute axa selectată (la fel ca în cazul pereților) — dacă selectez A și
   *  modific acea cotă ar trebui să mute A».
   *
   *  E aceeași regulă ca la ziduri, unde cota unei camere mută ZIDUL ALES când
   *  el e una din fețele ei (v. `cotelePlanului`, `zidAles`): cine a aprins
   *  ceva și scrie o cifră vorbește despre LUCRUL APRINS.
   *
   *  ALTFEL, A DOUA, ca până acum: o cotă se citește de la stânga la dreapta,
   *  iar cine o schimbă fără să fi ales nimic se așteaptă ca începutul ei să
   *  rămână pe loc. Cotele de după se strâng sau se lărgesc cu ea, ca la
   *  tragere.
   *
   *  SEMNUL SE ÎNTOARCE CÂND SE MUTĂ PRIMA: `de` e mereu mai mic decât `la`
   *  (v. `distanteleAxelor`, care le sortează), deci o cotă crește fie ducând
   *  capătul al doilea ÎNAINTE, fie pe primul ÎNAPOI. Fără întoarcerea asta,
   *  scrisă pe axa aleasă din stânga, cifra ar fi făcut exact pe dos. */
  const scrieCotaAxului = useCallback((
    d: { axa: 0 | 1; de: number; la: number; valoare: number }, v: number,
  ) => {
    setCotaAx(null);
    if (doarCitire || !Number.isFinite(v) || v <= 0) return;
    const delta = Math.round((v - d.valoare) * 1000) / 1000;
    if (!delta) return;
    const toate = axele(doc);
    // Axa aleasă, dar numai dacă e chiar unul din capetele cotei ăsteia: una
    // aprinsă în altă parte a planșei n-are nimic de-a face cu cifra scrisă
    // aici, iar mutată ar fi fost o surpriză.
    const aleasa = ales?.fel === "ax" ? toate[ales.i] : null;
    const peDe = !!aleasa && aleasa.axa === d.axa
      && Math.abs(aleasa.valoare - d.de) < 1e-6;
    const ax = peDe ? aleasa
      : toate.find((x) => x.axa === d.axa && Math.abs(x.valoare - d.la) < 1e-6);
    if (!ax) return;
    laGestPornit();
    laSchimbare(mutaAxul(doc, ax, peDe ? -delta : delta), true);
  }, [doc, ales, doarCitire, laGestPornit, laSchimbare]);

  const scrieCota = useCallback((c: Cota, v: number) => {
    setCotaScrisa(null);
    if (doarCitire || !c.muta || !Number.isFinite(v) || v < 0) return;
    // A strip's length: its end goes to the typed figure, its sign kept.
    if (c.muta.capat === "end" && ales?.fel === "aparat") {
      const L = doc.plan?.electrical?.[ales.i]?.length_m ?? 0;
      const d2 = mutaCapatBanda(doc, ales.i, "end", (L < 0 ? -1 : 1) * v);
      if (d2 !== doc) {
        laGestPornit();
        laSchimbare(d2, true);
      }
      return;
    }
    const delta = Math.round(c.muta.semn * (c.valoare - v) * 1000) / 1000;
    if (!delta) return;
    const dep: Pt = c.muta.axa === 0 ? [delta, 0] : [0, delta];
    laGestPornit();
    // COTA UNEI CAMERE MIȘCĂ ZIDUL, nu camera: aceeași cale ca tragerea de
    // mână — se apucă zidul CHIAR ÎN DREPTUL camerei, ca să plece cu el
    // pereții ei alăturați și să nu plece vecina de dincolo. Aria și cealaltă
    // cotă se refac singure din derivare, nu le scrie nimeni.
    if (c.peZid) {
      const { doc0, grup } = apucaCorpulZidului(doc, plan, c.peZid.i,
                                                c.peZid.punct, null);
      // ÎNCOTRO SE MUTĂ ZIDUL. `mutaPeretele` adună deplasarea DIRECT la
      // coordonata lui pe axă, deci semnul trebuie să fie al capătului pe care
      // stă cota: capătul mare crește spre plus, cel mic spre minus. Scris
      // fără el, mutarea ieșea mereu `v − măsurat`, adică mereu în aceeași
      // parte — și balconul, a cărui latură bună e cea mică, se MICȘORA de
      // fiecare dată când i se cerea mai mult (13.10.2026, din filmare:
      // 1,50 → cerut 2,30 → 12,61 m² au ajuns 11,94).
      laSchimbare(mutaPeretele(doc0, c.peZid.i, c.muta.semn * (v - c.valoare),
                               { grup: new Set(grup) }), true);
      return;
    }
    laSchimbare(mutaObiectul(doc, ales, dep), true);
  }, [doc, plan, ales, doarCitire, laGestPornit, laSchimbare]);

  const cotaVie = (() => {
    if (!gest || !cursor) return null;
    if (gest.fel === "peretNou") {
      const L = Math.hypot(gest.la[0] - gest.de[0], gest.la[1] - gest.de[1]);
      const u = (Math.atan2(gest.la[1] - gest.de[1], gest.la[0] - gest.de[0]) * 180) / Math.PI;
      return { p: gest.la, text: `${L.toFixed(2)} m`, editabil: true,
               valoare: L,
               sub: `${(((u % 360) + 360) % 360).toFixed(1)}°` };
    }
    if (gest.fel === "scaraNoua") {
      // CIFRA DE SUB CURSOR E ALTA ÎN FIECARE PAS, și în amândouă se poate
      // SCRIE: pe o scară, treptele și depărtările sunt hotărâri, nu nimerite.
      const h = doc.storey_height_m ?? 2.8;
      const rampe = rampeleScarii(gest);
      const sc = scaraDinRampe(rampe, h, treaptaScarii);
      if (gest.faza === "latura") {
        return { p: gest.la, editabil: true, unitate: "m",
                 valoare: gest.dist ?? 0,
                 text: `${(gest.dist ?? 0).toFixed(2)} m`,
                 sub: LATURA_TEXT[gest.latura ?? "sus"](t) };
      }
      const n = sc.perRampa.at(-1) ?? 0;
      // ÎNCOTRO URCĂ, scris lângă câte trepte are. Pe pasul ăsta omul alege și
      // sensul rampei, trăgând de ea — dar nu scria nimeni ce a ales, deci
      // singurul fel de a afla era să pui scara și să te uiți la ea.
      const ultima = gest.rampe.at(-2);
      const sens = gest.rampe.length > 1
        ? gest.sens ?? (ultima
            ? sensulImplicit(ultima, gest.latura ?? "sus") : null)
        : null;
      // CONTRATREAPTA SPUNE CÂND SCARA NU MAI E O SCARĂ. Rampele își iau
      // treptele din LUNGIMEA lor, iar nivelul se împarte la câte au ieșit:
      // patru rampe lungi pe un nivel de 2,80 m dau 49 de contratrepte de
      // 5,7 cm, și nimic nu se plângea. 14–19 cm e ce se urcă; în afară, cifra
      // se scrie altfel, ca omul s-o vadă înainte s-o pună (08.10.2026).
      const cm = sc.pas * 100;
      const pasul = cm < 14 || cm > 19
        ? t("panza.scara_pas_rau", { cm: cm.toFixed(1) })
        : t("panza.scara_pas_cm", { cm: cm.toFixed(1) });
      // how far the drawing is from the count the storey wants
      const ramase = lungimileScarii(h).contratrepte - sc.contratrepte;
      const parti = [sens && t(SENS_TEXT[sens]), pasul,
                     ramase > 0 && t("panza.scara_ramase", { n: ramase })];
      return { p: gest.la, editabil: true, unitate: "trepte", valoare: n,
               text: t("panza.scara_contratrepte", { n }),
               sub: parti.filter(Boolean).join(" · ") };
    }
    if (gest.fel === "scaraGata") {
      // the same figure as under the custom stair: the first flight's
      // risers and the riser height they make
      const h = doc.storey_height_m ?? 2.8;
      const pr = rampelePresetului(gest.cheie, gest.de, h, gest.dir ?? [1, 0],
                                   gest.oglinda, gest.trepte ?? undefined);
      if (!pr) return null;
      const sc = scaraDinRampe(pr.rampe, h, pr.treapta);
      const cm = sc.pas * 100;
      const pasul = cm < 14 || cm > 19
        ? t("panza.scara_pas_rau", { cm: cm.toFixed(1) })
        : t("panza.scara_pas_cm", { cm: cm.toFixed(1) });
      return { p: gest.la, text: t("panza.scara_contratrepte", { n: sc.perRampa[0] ?? 0 }),
               sub: gest.dir ? pasul : t("editor.sfat.scara_gata_trage") };
    }
    if (gest.fel === "camLibera") {
      const ultim = gest.puncte.at(-1);
      if (!ultim) return null;
      const L = Math.hypot(gest.la[0] - ultim[0], gest.la[1] - ultim[1]);
      return { p: gest.la, text: `${L.toFixed(3)} m`,
               sub: t("panza.cam_libera_sub", { n: gest.puncte.length }) };
    }
    if (gest.fel === "cameraNoua") {
      // Laturile dreptunghiului tras, nu aria formei: cât timp tragi, cifrele
      // care contează sunt cele pe care le poți ținti.
      const w = Math.abs(gest.la[0] - gest.de[0]);
      const h = Math.abs(gest.la[1] - gest.de[1]);
      if (!gest.balcon) {
        return { p: gest.la, text: `${w.toFixed(2)} × ${h.toFixed(2)} m`,
                 sub: `${(w * h).toFixed(2)} m²` };
      }
      // BALCONUL SPUNE DACĂ SE PRINDE, cât e tras. Unul care nu atinge clădirea
      // nu se pune — o dală care nu se sprijină pe nimic n-are unde fi —, iar
      // aflat abia la ridicarea degetului arată ca un editor care înghite
      // gesturi.
      const prins = balconulSePrinde(doc, gest.de, gest.la);
      return { p: gest.la, text: `${w.toFixed(2)} × ${h.toFixed(2)} m`,
               sub: prins
                 ? t("panza.balcon_prins", { arie: (w * h).toFixed(2) })
                 : t("panza.balcon_departe") };
    }
    if (gest.fel === "laturaCamerei") {
      // MĂRIMEA CAMEREI, nu deplasarea laturii: cine trage de o margine are în
      // cap «vreau 3,20 aici», nu «mut peretele cu 18 cm». Se citește de pe
      // camera de ACUM, deci cifra e cea de sub mână.
      const inel = plan.camere[gest.i]?.inel;
      if (!inel) return null;
      const v = inel.map((q) => q[gest.l.axa]);
      const L = Math.max(...v) - Math.min(...v);
      const alta = (1 - gest.l.axa) as 0 | 1;
      const w = inel.map((q) => q[alta]);
      return { p: cursor, text: `${L.toFixed(2)} m`,
               sub: t("panza.latura_cealalta",
                       { n: (Math.max(...w) - Math.min(...w)).toFixed(2) }) };
    }
    // SPĂRTURA SPUNE CÂT RUPE, cât se trage — ca orice secțiune pusă cu mâna.
    // Până acum banda se vedea, dar nu scria nimic: cât de mare e gaura se
    // afla abia după ce o făceai, deci o spărtură de 90 cm se nimerea din a
    // treia încercare. Cerut odată cu lipirea de încrucișări (20.10.2026).
    //
    // STĂ ÎNAINTEA PORȚII `zidAles`: unealta lucrează pe un perete, dar nu-l
    // ALEGE — `laAles(null)` —, deci o ramură pusă mai jos n-ar fi ajuns
    // niciodată să fie citită.
    //
    // CIFRA E A BENZII, nu a cursorului: `t1` e deja lipit (`tLipit`), iar
    // mâna poate să fi plecat de pe zid cât ține tragerea. Scrisă din cursor,
    // ar fi arătat altceva decât bucata care chiar se dărâmă.
    if (gest.fel === "sparge") {
      const w = plan.pereti[gest.i];
      if (!w) return null;
      const o = osiaPeretelui(w.inel);
      const mij = (Math.min(...o.fete) + Math.max(...o.fete)) / 2;
      const q: Pt = gest.lung === 0 ? [gest.t1, mij] : [mij, gest.t1];
      return { p: q, text: `${Math.abs(gest.t1 - gest.t0).toFixed(2)} m`,
               sub: t("panza.spartura_sub") };
    }
    if (!zidAles || !osia) return null;
    if (gest.fel === "redimPerete" && axAles) {
      // CIFRA MARE E CEA CARE SE MIȘCĂ SUB MÂNĂ.
      //
      // Cadrul schimbă două mărimi: o latură lungă trasă schimbă GROSIMEA, una
      // scurtă LUNGIMEA. Cifra mare era însă mereu lungimea — deci cine îngroșa
      // un zid trăgea de fața lui și citea un număr care nu se clintea, adică
      // «pare că scrie o valoare greșită». Acum scrie mărimea trasă, cu numele
      // ei dedesubt, și amândouă când se trage de un colț.
      const [a, b] = axAles;
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const g = zidAles.grosime;
      const { capat, fata } = gest.prindere;
      if (fata && capat == null) {
        return { p: cursor, text: `${g.toFixed(2)} m`, sub: "grosime" };
      }
      if (capat != null && !fata) {
        return { p: cursor, text: `${L.toFixed(2)} m`, sub: "lungime" };
      }
      return { p: cursor, text: `${L.toFixed(2)} × ${g.toFixed(2)} m`,
               sub: "lungime × grosime" };
    }
    if (gest.fel === "capatPerete" && axAles) {
      // LUNGIMEA CONTURULUI, NU A AXULUI — adică exact cifra pe care o arată
      // panoul după ce dai drumul.
      //
      // Se arăta lungimea AXULUI. Cele două nu sunt egale: acolo unde un vecin
      // retează capătul, conturul e mai scurt decât axul dus până în axul
      // vecinului. Deci trăgeai până scria 3,40, dădeai drumul, apăsai pe zid
      // și panoul zicea 3,37 — «sometimes it says a length when I pull and
      // then when I press on the wall it's a different length» (19.10.2026).
      // Nu era o rotunjire pierdută, erau două MĂSURI diferite ale aceluiași
      // perete, iar cea de pe ecran era cea pe care n-o vede nimeni pe urmă.
      //
      // Documentul e deja mutat cât ține tragerea (`laSchimbare(..., true)`),
      // deci conturul de sub mână e chiar cel care se va scrie.
      const inel = plan.pereti[gest.i]?.inel;
      const L = inel ? osiaPeretelui(inel).lungime
        : Math.hypot(axAles[1][0] - axAles[0][0], axAles[1][1] - axAles[0][1]);
      const [a, b] = axAles;
      const u = (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
      return { p: cursor, text: `${L.toFixed(3)} m`,
               sub: `${(((u % 360) + 360) % 360).toFixed(1)}°` };
    }
    return { p: cursor, text: `${osia.lungime.toFixed(3)} m`, sub: null };
  })();

  return (
    <div className="ob-ed-panza" data-unealta={unealta}
         /* CLIC DREAPTA IESE, ca Escape. Cerut: «add another shortcut for
            escaping the select or add. When we press right click it should
            take us out of that like esc» (20.10.2026). Un gest în lucru se
            lasă, selecția se stinge, unealta se întoarce la săgeată — adică
            exact ce face Esc, pe butonul pe care mâna îl are deja pe mouse.
            Meniul browserului se oprește, altfel ar acoperi planșa. */
         onContextMenu={(e) => {
           e.preventDefault();
           setGest(null);
           laAles(null);
           laUnealta("sageata");
         }}
         data-panorama={spatiu ? "1" : undefined} ref={gazda}>
      {aviz && (
        <div className="ob-ed-aviz" role="status">{aviz}</div>
      )}
      <svg width="100%" height="100%" onPointerDown={laFond}
           onPointerMove={(e) => {
             // Numai cât o unealtă de gol e activă și nu se trage de nimic:
             // altfel ar fi un calcul pe fiecare `pointermove`, degeaba.
             const puneGol = !!USI[unealta] || unealta === "fereastra";
             // SCARA N-ARE FANTOMĂ DE PIESĂ. Avea — dreptunghiul ei desenat
             // sub cursor, ca la orice mobilier —, și era chiar ce nu trebuia:
             // scara nu se mai pune ca o cutie, se face în trei pași, iar
             // cutia aia arăta o scară gata făcută înainte să fi spus cineva
             // câte trepte are («the preview should not look like this»,
             // 05.10.2026). Ce se vede acum e rampa în lucru, din gest.
             const punePiesa = unealta === "mobila"
               || unealta === "lift" || unealta === "ghena"
               || unealta === "aparat";
             // UNEALTA DE PERETE N-ARE FANTOMĂ. Avea: o bucată de zid desenată
             // sub cursor, lipită de fața vecinului și întoarsă perpendicular
             // pe el. Adică sărea de colo-colo înainte să fi apăsat cineva ceva
             // — «are un comportament ciudat». Ce arată acum unealta e un
             // CURSOR, un semn de perete care stă lipit de vârful săgeții și nu
             // se lipește de nimic (v. `editor.css`, `data-unealta`). Lipirea
             // rămâne acolo unde contează: la zidul care chiar se desenează.
             if (gest || (!puneGol && !punePiesa)) {
               if (fantoma) setFantoma(null);
               return;
             }
             if (!puneGol) {
               // MOBILIERUL cade unde e cursorul, fără gazdă: nu se sprijină pe
               // nimic, deci fantoma lui nu poate lipsi.
               const p = pct(e);
               // APARATUL SE VEDE DEJA LIPIT. Fantoma lui arată unde va ajunge
               // după lipire, nu unde e cursorul — altfel priza pare că se pune
               // în mijlocul camerei și sare pe zid abia la apăsare.
               const lipit = unealta === "aparat" ? lipitDeZid(doc, p, aparat) : null;
               const unde = lipit
                 ? { p: locPeFata(plan, aparat, lipit.p, lipit.rot), rot: lipit.rot }
                 : { p: piesaLipita(unealta === "mobila" ? piesa : unealta, p),
                     rot: 0 };
               setFantoma({ c: unde.p, rot: unde.rot, grosime: 0,
                            beton: false });
               return;
             }
             const unde = pozitiaGolului(doc, pct(e));
             setFantoma(unde ? { c: unde.centru, rot: unde.rot,
                                 grosime: unde.grosime,
                                 beton: unde.beton } : null);
           }}
           onPointerLeave={() => setFantoma(null)}
           onContextMenu={(e) => e.preventDefault()}>
        {view && (
          <g transform={`translate(${view.tx} ${view.ty}) scale(${view.k})`}>
            {/* SELECȚIA E STINSĂ CÂT TIMP O UNEALTĂ E ACTIVĂ. Nu e doar
                comoditate: `Desen` oprește propagarea când prinde un click, iar
                un gol se pune APĂSÂND PE UN PERETE — deci cu selecția pornită,
                clickul de plasare ajungea la perete, îl selecta, și golul nu se
                mai punea niciodată. Măsurat în editor: unealta «Ușă simplă»
                activă, click pe zid, zero goluri adăugate și peretele ales. */}
            {/* CORPURILE VECINE, sub corpul de pe masă. Se văd întregi —
                pereți, camere, cote, axe —, dar nu se apucă: o apăsare
                oriunde pe unul îl pune pe masă (v. `laVecin`). ESTOMPAȚI, cât
                să se vadă dintr-o privire care corp e pe masă — cotele și axele
                lui se trag, ale vecinilor doar se citesc. */}
            {veciniDesenati.map((v) => (
              <g key={`vc${v.cheie}`} opacity={0.5}
                 style={{ cursor: doarCitire ? "default" : "pointer" }}
                 onPointerDown={(e) => {
                   // UN CLIC, NU O APĂSARE: apăsarea merge mai departe la fond,
                   // ca tragerea peste un vecin să panorameze ca oriunde; corpul
                   // vine pe masă abia dacă mâna se ridică pe loc. Ridicarea se
                   // ascultă pe fereastră, fiindcă panoramarea capturează
                   // pointerul pe pânză.
                   if (e.button !== 0 || spatiu || !laVecin) return;
                   const x0 = e.clientX, y0 = e.clientY, p = pct(e);
                   window.addEventListener("pointerup", (u) => {
                     if (Math.hypot(u.clientX - x0, u.clientY - y0) < 5) laVecin(v.cheie, p);
                   }, { once: true });
                 }}>
                <title>{v.nume}</title>
                {v.axe.map((ax, j) => {
                  const c = v.plan.cadru;
                  const iesire = 0.9;
                  const a1 = ax.axa === 0
                    ? [[ax.valoare, c.y - iesire], [ax.valoare, c.y + c.h + iesire]]
                    : [[c.x - iesire, ax.valoare], [c.x + c.w + iesire, ax.valoare]];
                  const r = 9 / k;
                  return (
                    <g key={`vax${j}`}>
                      <line x1={a1[0][0]} y1={a1[0][1]} x2={a1[1][0]} y2={a1[1][1]}
                            stroke={AX_CULOARE} strokeWidth={1}
                            strokeDasharray="10 6" vectorEffect="non-scaling-stroke"
                            pointerEvents="none" />
                      <circle cx={a1[0][0]} cy={a1[0][1]} r={r} fill={HARTIE}
                              stroke={AX_CULOARE} strokeWidth={1.2}
                              vectorEffect="non-scaling-stroke" />
                      <text x={a1[0][0]} y={a1[0][1]} textAnchor="middle"
                            dominantBaseline="central" fontSize={r * 1.05}
                            fontWeight={700} fill={AX_CULOARE} pointerEvents="none">
                        {ax.nume}
                      </text>
                    </g>
                  );
                })}
                {/* Zona de apăsat: cutia corpului, sub desenul lui — desenul
                    unui vecin nu e interactiv, deci n-ar prinde clicul. */}
                <rect x={v.plan.cadru.x} y={v.plan.cadru.y}
                      width={v.plan.cadru.w} height={v.plan.cadru.h}
                      fill="transparent" pointerEvents="all" />
                <Desen plan={v.plan} ales={null} pxPerM={view.k}
                       interactiv={false} mod={mod}
                       araEtichete={araEtichete}
                       layere={layere}
                       araCote={araCote}
                       culoriApartamente={v.culoriApartamente ?? null} />
              </g>
            ))}

            <Desen plan={planDesenat} ales={ales} alese={alese}
                   laLanturi={dozaAleasa ? setLanturiDesenate : undefined}
                   pereteleAlese={pereteleAlese} mobilaAlese={mobilaAlese}
                   golurileAlese={golurileAlese}
                   pxPerM={view.k}
                   cursorPerete="move"
                   laAles={laObiect} mod={mod} trasee={firele}
                   panorameaza={spatiu} araEtichete={araEtichete}
                   layere={layere}
                   araCote={araCote} culoriApartamente={culoriApartamente}
                   /* CIFRELE PLANȘEI SE POT SCRIE — v. `Desen.laCota`. */
                   /* O COTĂ SE ȘI ALEGE, nu doar se deschide: așa intră pe
                      drumul pe care merge orice obiect ales — tasta `Delete`,
                      bara de context, istoricul —, cerut de arhitect
                      (26.10.2026): «ar trebui să poată fi selectate și șterse
                      cotele pe care le pune el automat». Caseta rămâne: ea e
                      locul în care se SCRIE o măsură. */
                   laCota={doarCitire || unealta !== "sageata" ? undefined
                     : (c, pastreazaSelectia) => {
                       setCotaPlan({ c, text: null });
                       // COTELE UNUI GOL ALES NU SCHIMBĂ SELECȚIA: scrierea
                       // lor mută GOLUL, iar `scrieCota` mută `ales` — ales ar
                       // fi devenit cota însăși, și n-ar mai fi avut ce mișca.
                       if (!pastreazaSelectia) {
                         laAles({ fel: "cota", cheie: cheiaCotei(c) });
                       }
                     }}
                   /* ÎN DESIGN, PENSULA TREBUIE SĂ POATĂ APĂSA PE CAMERE.
                      «Interactiv doar cu săgeata» e regula care face ca o
                      unealtă de pus să nu fie furată de obiectul de dedesubt —
                      dar pensula NU pune, ea vopsește ce e dedesubt, deci ei îi
                      trebuie exact clickul pe care celelalte îl evită. */
                   interactiv={unealta === "sageata" || unealta === "pensula"}
                   pensula={unealta === "pensula"} />

            {planDesenat.piese.map((f, i) => (
              f.scara && verificaScara(f.scara, planDesenat.nivel).length > 0 && (
                <rect key={i} className="ob-ed-scara-rea" data-scara-rea={i}
                      x={f.c[0] - f.w / 2} y={f.c[1] - f.h / 2}
                      width={f.w} height={f.h} />
              )
            ))}

            {/* AXELE DE TRASARE — A, B, C pe verticală, 1, 2, 3 pe orizontală.
                PESTE desen, cerut de Olivia: «axurile cu roșu să apară pe
                deasupra desenului». Erau dedesubt, ca un caroiaj de lucru, și
                pe un modul plin dispăreau sub pereți și mobilă — adică exact
                când ai nevoie de ele. Linia nu prinde clicuri (`pointerEvents`
                pe none), deci deasupra fiind, tot nu fură nimic din desen.
                Stinse implicit — v. `araAxe`. */}
            {axeleLui.map((ax, j) => {
              const iesire = AX_IESIRE;
              // O AXĂ OBLICĂ NU E NICI PE X, NICI PE Y: linia ei se trage pe
              // întinderea proprie (v. `punctulAxei`), prelungită cu aceeași
              // ieșire ca a celorlalte. Cele ortogonale rămân cum erau — duse
              // dintr-o margine a cadrului în cealaltă.
              // DE UNDE PÂNĂ UNDE SE DESENEAZĂ — dintr-un singur loc, ca
              // linia, bulele cu numele și apucătoarele să cadă pe aceleași
              // două capete (v. `model::intindereaAxei`).
              const [t0, t1] = intindereaAxei(ax, cutiaDes);
              const cap = punctulAxei(ax, t0 - iesire);
              const cap2 = punctulAxei(ax, t1 + iesire);
              const a1 = [cap, cap2];
              // BULA CARE AR CĂDEA PE DESEN NU SE SCRIE. Scurtată până
              // înăuntrul modulului, axa își duce capătul peste camere, iar
              // litera ajunge peste pereți și cifre — «if the cota goes into
              // the drawing the letter or number should disappear so it's not
              // cluttered» (15.10.2026). Linia rămâne întreagă: ea arată unde
              // s-a tras, numele doar s-ar încurca cu desenul.
              //
              // SE ÎNTREABĂ DOAR DESPRE AXELE SCURTATE. Una întreagă e tăiată
              // pe cutia desenului și apoi dusă încă `AX_IESIRE` afară, deci
              // n-are cum să cadă peste ceva; numai scurtarea îi poate aduce
              // capătul înăuntru. Fără paza asta s-ar fi căutat prin cele 50
              // de camere și 87 de ziduri de două ori pentru fiecare dintre
              // cele 55 de axe, la fiecare desenare.
              const peDesen = (q: Pt) => !!ax.scurtat
                && (plan.camere.some((r) => inCamera(q, r.inel))
                 || plan.pereti.some((w) => inCamera(q, w.inel)));
              const araCap = !peDesen(cap);
              const araCap2 = !peDesen(cap2);
              const r = 9 / k;
              return (
                <g key={`ax${j}`}>
                  <line x1={a1[0][0]} y1={a1[0][1]} x2={a1[1][0]} y2={a1[1][1]}
                        stroke={ales?.fel === "ax" && ales.i === j
                          ? SEL : AX_CULOARE}
                        strokeWidth={ales?.fel === "ax" && ales.i === j ? 2.4 : 1}
                        strokeDasharray="10 6" vectorEffect="non-scaling-stroke"
                        pointerEvents="none" />
                  {/* SE APUCĂ DE ORIUNDE DE PE LINIE, nu doar de disc.
                      Discul are 18 px și stă la un capăt, în afara planșei:
                      ca să muți axa trebuia să-l nimerești acolo, deși linia
                      pe care o muți traversează tot desenul. Cerut: «să pot
                      să dau click oriunde pe axă ca s-o trag, nu doar pe A,
                      B, 1, 2».

                      BANDA E ÎNGUSTĂ (7 px), și dinadins. Linia stă PESTE
                      desen (v. nota de mai sus), iar una care prinde clicuri
                      le ia de la pereții și camerele de dedesubt. La 7 px
                      fură numai clicurile chiar de pe ea — cât un zid subțire
                      pe ecran —, iar restul planșei rămâne al desenului. */}
                  <line x1={a1[0][0]} y1={a1[0][1]} x2={a1[1][0]} y2={a1[1][1]}
                        stroke="transparent" strokeWidth={7}
                        vectorEffect="non-scaling-stroke"
                        style={{ cursor: doarCitire ? "inherit"
                          : ax.axa === 0 ? "ew-resize" : "ns-resize" }}
                        pointerEvents={doarCitire ? "none" : "stroke"}
                        onPointerDown={(e) => {
                          if (e.button !== 0 || doarCitire) return;
                          e.stopPropagation();
                          // ALEGE ȘI APUCĂ, ca orice obiect (v. `laObiect`):
                          // aleasă, axa se șterge cu `Delete`; apucată, se
                          // trage. Un singur gest le face pe amândouă, fiindcă
                          // un clic fără mișcare nu schimbă nimic.
                          laAles({ fel: "ax", i: j });
                          setGest({ fel: "mutaAx", ax, de: pct(e), doc0: doc,
                                    pornit: false });
                        }}>
                    <title>{t("panza.ax_titlu",
                      { nume: ax.nume, n: ax.ziduri.length })}</title>
                  </line>
                  {araCap && (<>
                  <circle cx={cap[0]} cy={cap[1]} r={r} fill={HARTIE}
                          stroke={ales?.fel === "ax" && ales.i === j
                            ? SEL : AX_CULOARE}
                          strokeWidth={ales?.fel === "ax" && ales.i === j ? 2.4 : 1.2}
                          vectorEffect="non-scaling-stroke"
                          style={{ cursor: doarCitire ? "inherit"
                            : ax.axa === 0 ? "ew-resize" : "ns-resize" }}
                          pointerEvents={doarCitire ? "none" : "all"}
                          onPointerDown={(e) => {
                            if (e.button !== 0 || doarCitire) return;
                            e.stopPropagation();
                            // Discul face ce face și linia: alege ȘI apucă.
                            // Litera e locul spre care se duce mâna când vrei
                            // axa «B», deci trebuie s-o și aleagă.
                            laAles({ fel: "ax", i: j });
                            setGest({ fel: "mutaAx", ax, de: pct(e), doc0: doc,
                                      pornit: false });
                          }}>
                    <title>{t("panza.ax_titlu",
                      { nume: ax.nume, n: ax.ziduri.length })}</title>
                  </circle>
                  <text x={cap[0]} y={cap[1]} textAnchor="middle"
                        dominantBaseline="central" fontSize={r * 1.05}
                        fontWeight={700} fill={AX_CULOARE} pointerEvents="none">
                    {ax.nume}
                  </text>
                  </>)}
                  {/* A DOUA BULĂ, la celălalt capăt: numele axei se citește și
                      sus, și jos (respectiv stânga și dreapta). Pe o planșă
                      mare, cine se uită la capătul de jos al desenului nu mai
                      are de unde ști pe ce axă e — «să se vadă și în sus și
                      jos / stânga și dreapta» (15.10.2026). */}
                  {araCap2 && (<>
                  <circle cx={cap2[0]} cy={cap2[1]} r={r} fill={HARTIE}
                          stroke={ales?.fel === "ax" && ales.i === j
                            ? SEL : AX_CULOARE}
                          strokeWidth={ales?.fel === "ax" && ales.i === j ? 2.4 : 1.2}
                          vectorEffect="non-scaling-stroke"
                          style={{ cursor: doarCitire ? "inherit"
                            : ax.axa === 0 ? "ew-resize" : "ns-resize" }}
                          pointerEvents={doarCitire ? "none" : "all"}
                          onPointerDown={(e) => {
                            if (e.button !== 0 || doarCitire) return;
                            e.stopPropagation();
                            laAles({ fel: "ax", i: j });
                            setGest({ fel: "mutaAx", ax, de: pct(e), doc0: doc,
                                      pornit: false });
                          }}>
                    <title>{t("panza.ax_titlu",
                      { nume: ax.nume, n: ax.ziduri.length })}</title>
                  </circle>
                  <text x={cap2[0]} y={cap2[1]} textAnchor="middle"
                        dominantBaseline="central" fontSize={r * 1.05}
                        fontWeight={700} fill={AX_CULOARE} pointerEvents="none">
                    {ax.nume}
                  </text>
                  </>)}
                  {/* APUCĂTOARELE DE SCURTAT, pe axa aleasă. Trase în lungul
                      ei, scurtează sau lungesc numai DESENUL — poziția și
                      pereții rămân (v. `model::lungimeaAxei`). Bula capătului
                      vine după ele, deci numele se mută spre partea de care
                      tragi. */}
                  {ales?.fel === "ax" && ales.i === j && !doarCitire
                    && ([0, 1] as const).map((capat) => {
                    const P = punctulAxei(ax, capat === 0 ? t0 : t1);
                    return (
                      <rect key={`al${capat}`} x={P[0] - 5 / k} y={P[1] - 5 / k}
                            width={10 / k} height={10 / k}
                            fill={HARTIE} stroke={SEL} strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            style={{ cursor: "grab" }}
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              e.stopPropagation();
                              setGest({ fel: "lungAx", capat, de: pct(e),
                                        ax: { ...ax, jos: t0, sus: t1,
                                              scurtat: true },
                                        doc0: doc, pornit: false });
                            }}>
                        <title>{t("panza.ax_scurteaza")}</title>
                      </rect>
                    );
                  })}
                </g>
              );
            })}


            {/* CÂT E ÎNTRE DOUĂ AXE, ca o cotă. Cerut tot de Olivia: «să se
                vadă distanța dintre axuri ca o cotă». Se scrie afară din
                planșă, dincolo de discurile cu litere, ca să nu se încurce cu
                cotele camerelor; desenul e al cotelor obișnuite (v.
                `CotaDesen`), doar tușul e al axelor, ca să se citească dintr-o
                privire că măsura e între axe, nu între ziduri. */}
            {distanteleAxelor(axeleLui).map((d, j) => {
              const c = plan.cadru;
              const unde = (d.axa === 0 ? c.y : c.x)
                - AX_IESIRE - AX_COTA_PX / k;
              const cota: Cota = {
                a: d.axa === 0 ? [d.de, unde] : [unde, d.de],
                b: d.axa === 0 ? [d.la, unde] : [unde, d.la],
                valoare: d.valoare,
              };
              const mx = (cota.a[0] + cota.b[0]) / 2;
              const my = (cota.a[1] + cota.b[1]) / 2;
              return (
                <g key={`ca${j}`}>
                  <CotaDesen c={cota} k={k} culoare={AX_CULOARE}
                             hartie={HARTIE} />
                  {/* DE APĂSAT PE CIFRĂ, ca la cotele obiectului. Dreptunghi
                      transparent în jurul ei: linia cotei e subțire, iar o
                      țintă de un pixel nu se nimerește. */}
                  {/* ȚINTA SE ÎNTOARCE ODATĂ CU CIFRA. Cota dintre axele
                      orizontale e o linie VERTICALĂ, iar `CotaDesen` scrie
                      numărul de-a lungul ei, deci rotit: o casetă lată și
                      joasă cădea alături de el, și apăsarea nimerea pânza.
                      Aceleași 52 × 18 px, pe lat sau pe înalt după cum stă
                      cifra. */}
                  {!doarCitire && (
                    <rect x={mx - (d.axa === 0 ? 26 : 9) / k}
                          y={my - (d.axa === 0 ? 9 : 26) / k}
                          width={(d.axa === 0 ? 52 : 18) / k}
                          height={(d.axa === 0 ? 18 : 52) / k}
                          fill="transparent" strokeWidth={0}
                          style={{ cursor: "text" }} pointerEvents="all"
                          // MUTAREA FOCUSULUI SE OPREȘTE PE `mousedown`, NU
                          // DOAR PE `pointerdown`.
                          //
                          // Caseta se deschide cu `autoFocus`; apăsarea își
                          // duce pe urmă focusul la elementul pe care a căzut,
                          // iar `onBlur` o închide în aceeași apăsare — se
                          // vede ca o cotă pe care clicul n-o deschide deloc.
                          // `preventDefault` pe `pointerdown` oprește asta
                          // NUMAI dacă browserul suprimă și `mousedown`-ul de
                          // compatibilitate: Chrome o face, Safari nu, și
                          // atunci defectul se întoarce — pe un browser pe
                          // care probele noastre nu-l ating. Mutarea
                          // focusului e însă acțiunea implicită a lui
                          // `mousedown`, deci oprită acolo, e oprită peste
                          // tot. (Aceeași soluție ca la orice meniu care
                          // trebuie să rămână deschis sub cursor.)
                          onMouseDown={(e) => e.preventDefault()}
                          data-cota-ax={j}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setCotaAx({ j, text: null });
                          }} />
                  )}
                </g>
              );
            })}

            {/* HÂRTIA DE CALC, peste planșă și sub arătarea asistentului: ea e
                sursa după care se trasează, deci trebuie să se vadă peste
                zidurile deja trasate. V. `../Calcul.tsx`. */}
            {calc && <Calcul calc={calc} cadru={plan.cadru} />}

            {/* CE TOCMAI A ATINS ASISTENTUL — v. `aratare.ts`. Peste desen și
                sub apucătoare: e o arătare, nu un obiect. */}
            {aratare && (
              <Arata plan={planDesenat} vechi={aratare.vechi}
                            tinte={aratare.tinte} cadru={cadruAratarii} k={k} />
            )}

            {/* DISTANȚELE OBIECTULUI ALES până la ce îl înconjoară — v. antet.

                ZONA DE APĂSAT E A LINIEI, nu a cifrei. Cifra are 11 px și stă
                în mijlocul liniei; cerută drept singura țintă, ar fi fost de
                nimerit doar la mărire mare. Deci toată cota răspunde, cu o
                linie nevăzută groasă cât un deget peste ea — aceeași socoteală
                ca la zona de apucat a golurilor (v. `Desen::zonaGolului`). */}
            {!aratare && (coteObiect.length > 0 || marimeaAlesului.length > 0) && (
              <>
                <Cote cote={coteObiect} k={k} culoare={SEL} corp={11} />
                {/* …și cât e OBIECTUL, nu doar cât e până la vecini. */}
                <Cote cote={marimeaAlesului} k={k} culoare={SEL} corp={11} />
                {!doarCitire && unealta === "sageata" && coteObiect.map((c, i) => {
                  const apasa = (e: PointerEventReact) => {
                    if (e.button !== 0 || spatiu) return;
                    e.stopPropagation();
                    // ȘI `preventDefault`, altfel caseta se deschide și se
                    // închide în aceeași apăsare: câmpul primește focusul din
                    // `autoFocus`, iar apăsarea își duce pe urmă focusul, ca
                    // orice apăsare, la elementul pe care a căzut — adică îl
                    // ia de la câmp, iar `onBlur` îl închide. Se vedea ca o
                    // cotă pe care clicul n-o deschide deloc.
                    e.preventDefault();
                    setCotaScrisa({ i, text: null });
                  };
                  return (
                    <g key={`cz${i}`} style={{ cursor: "pointer" }}
                       // V. nota de la ținta cotei dintre axe: mutarea
                       // focusului e acțiunea implicită a lui `mousedown`,
                       // deci se oprește acolo, nu doar pe `pointerdown`.
                       onMouseDown={(e) => e.preventDefault()}
                       onPointerDown={apasa}>
                      {/* LINIA NU PRINDE CLICUL, doar cifra o face.
                          O cotă a obiectului ALES pleacă chiar de pe el și
                          trece peste el: linia avea o zonă de prindere de 16 px
                          pe toată lungimea ei, iar un zid de 0,30 m are 11 px
                          la mărirea obișnuită. Măsurat pe W2 din M1, cu zidul
                          ales: din cei 11 px ai lui, unul singur mai răspundea
                          cu «mută» — restul erau cotele lui și mânerele de
                          grosime. De-acolo venea «trag de perete și nu ia
                          mobila»: mâna nu apuca peretele deloc.
                          Nu se pierde nimic: nota de mai jos spune chiar ea că
                          ochiul apasă pe NUMĂR, iar numărul are pastila lui. */}
                      <line x1={c.a[0]} y1={c.a[1]} x2={c.b[0]} y2={c.b[1]}
                            stroke="transparent" strokeWidth={16 / k}
                            strokeLinecap="round" pointerEvents="none" />
                      {/* ȘI CIFRA, cu o pastilă a ei. Linia singură se
                          nimerește greu pe o cotă lungă și subțire, iar ochiul
                          oricum apasă pe NUMĂR — el e ce vrea schimbat.

                          PASTILA NU TRECE DE CAPETELE COTEI. 13 px e raza pe
                          care o cere degetul, dar o cotă poate fi mai scurtă
                          decât ea: pe M3, liftul stă la 6,4 cm de fața lui W49
                          — 2,3 px la mărirea obișnuită —, iar pastila de 26 px
                          a cotei ăleia acoperea tot zidul. Stefan trăgea de
                          W49 și i se deschidea, în loc, caseta unei cifre.
                          Deci raza se taie la jumătatea cotei: ținta unei
                          măsuri stă în măsura pe care o arată, oricât de mică
                          o face asta. O cotă de-o palmă e greu de nimerit, dar
                          nu mai fură zidul de lângă ea.

                          ȘI `strokeWidth={0}`, fără de care raza n-ar fi
                          însemnat nimic: același halou de un metru care punea
                          ușile peste ziduri (v. nota din `Desen`, zona
                          golului). Măsurat aici cu `elementFromPoint`: pastila
                          strânsă la 2,3 px răspundea în continuare pe 23. */}
                      <circle cx={punctulCifrei(c)[0]} cy={punctulCifrei(c)[1]}
                              r={Math.min(13 / k,
                                          Math.hypot(c.b[0] - c.a[0],
                                                     c.b[1] - c.a[1]) / 2)}
                              fill="transparent" strokeWidth={0}
                              pointerEvents="all" />
                    </g>
                  );
                })}
              </>
            )}

            {/* CASETELE COTELOR NU STAU AICI. Sunt `<input>`-uri adevărate,
                deci nu se pot desena în SVG; stăteau într-un `foreignObject`
                și de-aia nu se deschideau în Safari. Acum sunt HTML peste
                pânză — v. `CasetaCotei`, și mai jos, după `</svg>`. */}

            {/* CE VOPSEȘTE PENSULA, arătat înainte de apăsare.
                O cameră se vopsește dintr-un click, iar clickul ăla intră în
                istoric — deci întrebarea «pe care apăs» trebuie să aibă răspuns
                ÎNAINTE, nu după un ⌘Z. Camera de sub cursor se umple cu
                finisajul ales, ca o pensulă trecută peste ea. */}
            {unealta === "pensula" && cursor && finisajulPensulei && (() => {
              const cam = plan.camere.find((c) => inCamera(cursor, c.inel));
              if (!cam) return null;
              const f = finisajulPensulei;
              return (
                <path d={`M${cam.inel.map((q) => `${q[0]} ${q[1]}`).join("L")}Z`}
                      fill={f.fond} fillOpacity={0.72} stroke={SEL}
                      strokeWidth={2.5} strokeDasharray="8 5"
                      vectorEffect="non-scaling-stroke" pointerEvents="none" />
              );
            })()}

            {/* FANTOMA GOLULUI, sub cursor.
                Fără ea, punerea unei uși era pe ghicite: apăsai și abia apoi
                vedeai unde a căzut, iar dacă nimeriai lângă zid nu se întâmpla
                nimic și nimic nu spunea de ce. Acum se vede peretele-gazdă,
                locul pe el și cât ocupă — iar când nu e niciun zid sub cursor,
                fantoma dispare, ceea ce spune singur că apăsarea n-ar face
                nimic. Aceleași cifre ca ale golului adevărat: `pozitiaGolului`
                e chemată și de plasare. */}
            {fantoma && (() => {
              // SEMNUL ADEVĂRAT AL GOLULUI, nu o pată colorată.
              //
              // Prima variantă desena un dreptunghi în culoarea accentului. Se
              // vedea unde cade, dar nu CE cade: o ușă și o fereastră arătau la
              // fel, iar sensul de deschidere se afla abia după ce apăsai. Aici
              // se desenează chiar simbolul de planșă — foaia cu arcul ei de
              // baleiaj, sau cele două linii ale tocului —, doar mai palid, ca
              // să se citească «încă nu e pus».
              if (unealta === "aparat") {
                const fel = aparatul(aparat);
                if (!fel) return null;
                return (
                  <g className="ob-ed-fantoma ob-ed-fantoma-mob"
                     pointerEvents="none"
                     transform={`translate(${fantoma.c[0]} ${fantoma.c[1]}) `
                              + `rotate(${fantoma.rot - 90})`}>
                    {/* The ghost promises the size the placed symbol will
                        have: `Aparate` floors the drawn diameter in pixels,
                        so the ghost floors it the same way. */}
                    <g transform={fel.picior ? `translate(0 ${
                      drawnDiameter(marimeaSimbolului(fel.kind), view.k) / 2})`
                      : undefined}>
                      {simbolElectric(fel.kind,
                        drawnDiameter(marimeaSimbolului(fel.kind), view.k))}
                    </g>
                  </g>
                );
              }
              if (unealta === "mobila" || unealta === "scara"
                  || unealta === "lift"
                  || unealta === "ghena") {
                // CHIAR PIESA, la mărimea ei, nu un dreptunghi: cine pune o
                // canapea vrea să vadă dacă încape acolo, iar asta se vede din
                // desenul ei, nu dintr-un contur.
                //
                // `felulPiesei`, nu lista casei: altfel cele 205 obiecte aduse
                // din CAD n-aveau fantomă deloc — căutate în `PIESE_MOBILIER`
                // ieșeau `undefined`, iar fantoma se întorcea `null`. Le pui pe
                // orbește, exact ce nu trebuie pe o bibliotecă în care nu știi
                // pe nume ce e fiecare.
                const m = felulPiesei(unealta === "mobila" ? piesa : unealta);
                if (!m) return null;
                return (
                  <g className="ob-ed-fantoma ob-ed-fantoma-mob"
                     pointerEvents="none"
                     transform={`translate(${fantoma.c[0]} ${fantoma.c[1]}) `
                       + `rotate(${rotFantoma})`}>
                    {/* Fantoma arată contratreptele nivelului ăstuia, ca
                        piesa pusă să fie chiar cea văzută. Traseul de
                        evacuare nu: pe o fantomă care urmează cursorul
                        arcul lui mătură tot desenul. */}
                    {piesaSingura(m.kind, m.w, m.h,
                                  { hNivel: doc.storey_height_m })}
                  </g>
                );
              }
              const u = USI[unealta];
              // BETONUL SE VEDE ÎNAINTE DE APĂSARE. Fantoma roșie e tot ce are
              // omul între el și un refuz mut: într-un zid portant nu se taie
              // gol (v. `model::esteBeton`), iar dacă asta s-ar afla abia din
              // apăsarea care nu face nimic, editorul ar părea stricat.
              // Culoarea e a interdicției, nu a accentului, și se pune pe
              // TOATE liniile semnului — foaie, arc, toc —, ca să nu rămână
              // jumătate de ușă albastră, adică jumătate de «da».
              //
              // Aici se e deja pe calea golurilor — mobila și aparatele s-au
              // întors mai sus —, deci `beton` singur ajunge: ușă sau
              // fereastră, zidul se taie la fel.
              const oprit = fantoma.beton;
              const lat = u?.latime ?? 1.2;
              const [cx, cy] = fantoma.c;
              const ux = fantoma.rot === 0 ? 1 : 0;
              const uy = fantoma.rot === 0 ? 0 : 1;
              const a: Pt = [cx - (ux * lat) / 2, cy - (uy * lat) / 2];
              const b: Pt = [cx + (ux * lat) / 2, cy + (uy * lat) / 2];
              // Normala golului; foaia se deschide într-o parte a ei. Care
              // parte, se hotărăște la punere (v. `spreCamera`); fantoma o arată
              // pe cea pozitivă, ca să se vadă că ușa ARE un sens.
              const nx = -uy;
              const ny = ux;
              const gros = Math.max(fantoma.grosime, 0.05);
              return (
                <g className={`ob-ed-fantoma${oprit ? " nu" : ""}`}
                   pointerEvents="none">
                  {/* golul din zid: fâșia albă pe care o va tăia */}
                  <path className="taietura"
                        d={`M${a[0] + nx * gros / 2} ${a[1] + ny * gros / 2}`
                         + `L${b[0] + nx * gros / 2} ${b[1] + ny * gros / 2}`
                         + `L${b[0] - nx * gros / 2} ${b[1] - ny * gros / 2}`
                         + `L${a[0] - nx * gros / 2} ${a[1] - ny * gros / 2}Z`} />
                  {u ? (() => {
                    // ACELAȘI SEMN ca pe planșă (`simbolUsa`), doar palid:
                    // fantoma arată CE cade, nu doar unde — o ușă dublă se
                    // vede dublă înainte de a fi pusă.
                    const semn = simbolUsa({ a, b, latime: lat, s: 1, grosime: gros,
                                             toc: TOC_IMPLICIT, model: u.model,
                                             intrare: false });
                    return (
                      <>
                        {semn.tocuri.map((d, i) => <path key={`t${i}`} className="toc" d={d} />)}
                        {semn.foi.map((d, i) => <path key={`f${i}`} className="foaie" d={d} />)}
                        {semn.arce.map((d, i) => <path key={`a${i}`} className="arc" d={d} />)}
                        {semn.sina && <path className="arc" d={semn.sina} />}
                      </>
                    );
                  })() : (
                    [0.05, -0.05].map((d, i) => (
                      <path key={i} className="toc"
                            d={`M${a[0] + nx * d} ${a[1] + ny * d}`
                             + `L${b[0] + nx * d} ${b[1] + ny * d}`} />
                    ))
                  )}
                  {/* DE CE NU SE POATE, scris o dată, lângă semn. Fără rândul
                      ăsta roșul spune doar «nu», iar «nu» fără motiv se citește
                      ca o defecțiune. Stă sub gol, la mărimea ecranului (scara
                      inversă), ca să rămână lizibil la orice panoramare. */}
                  {oprit && (
                    <g transform={`translate(${cx + nx * gros} ${cy + ny * gros}) `
                                + `scale(${1 / k})`}>
                      <text className="ob-ed-fantoma-nu" x={0} y={14}>
                        {t("panza.gol_in_beton")}
                      </text>
                    </g>
                  )}
                </g>
              );
            })()}

            {/* COTELE GOLURILOR de pe peretele ales — socoteala e în
                `taieturileZidului`, unde stă și de ce un zid fără goluri nu
                primește niciuna. */}
            {!aratare && zidAles && osia && (() => {
              const taieturi = taieturileZidului(plan.goluri, zidAles.id, osia);
              if (taieturi.length === 0) return null;
              // Pe fața zidului, depărtate cât cotele planșei, ca să stea pe
              // același rând cu ele dacă e camera acolo — doar culoarea le
              // deosebește: astea sunt ale selecției.
              const v = osia.fete[0] - 26 / k;
              const P = (tt: number): Pt => (osia.lungPeX ? [tt, v] : [v, tt]);
              const cote: Cota[] = taieturi
                .map(([a, b]) => ({ a: P(a), b: P(b), valoare: b - a }));
              return <Cote cote={cote} k={k} culoare={SEL} corp={11} />;
            })()}

            {/* GROSIMEA, DIN LATURILE LUNGI — și numai din ele.
                Cadrul avea patru laturi și patru pătrățele în colțuri; cerut
                înapoi cum era: capetele se mută din cele două cercuri (v.
                `manere`), iar «dacă vreau să fac mai gros sau mai subțire trag
                de marginea de sus sau de jos, fără pătrățel». Deci aici rămân
                două linii late și străvezii peste conturul albastru al
                selecției: se apucă, nu se văd.

                ZONA DE APUCAT E ÎMPINSĂ SPRE EXTERIOR: 8 px lățime, centrată la
                2 px în afara conturului. Centrată pe contur, pe un zid de 10 cm
                la mărirea obișnuită (9 px) cele două laturi ar acoperi tot
                corpul, și zidul ales n-ar mai putea fi mutat din corp. */}
            {manaLibera && ales?.fel === "perete" && cadruAles && osia && (() => {
              const [p0, p1, p2, p3] = cadruAles;
              const T = Math.hypot(p3[0] - p0[0], p3[1] - p0[1]) || 1;
              const nx = (p0[0] - p3[0]) / T;          // spre fața +
              const ny = (p0[1] - p3[1]) / T;
              // MÂNERELE DE GROSIME STAU ÎN AFARA ZIDULUI, NU PESTE EL.
              // Erau la 2 px de față, cu o bandă de prindere de 8 px (±4), deci
              // intrau 2 px în corp — de fiecare parte. Pe un zid de 0,30 m,
              // adică 11 px, cele două mâneceau jumătate din el, iar ce mai
              // rămânea îl luau cotele (v. nota lor). Șase pixeli în afară duc
              // banda între 2 și 10 px dincolo de față: tot la îndemână, dar
              // fără să atingă corpul, oricât de subțire ar fi peretele.
              const afara = GROSIME_AFARA_PX / k;
              const dus = (q: Pt, dx: number, dy: number): Pt =>
                [q[0] + dx * afara, q[1] + dy * afara];
              const fete: { a: Pt; b: Pt; fata: 1 | -1 }[] = [
                { a: dus(p0, nx, ny), b: dus(p1, nx, ny), fata: 1 },
                { a: dus(p3, -nx, -ny), b: dus(p2, -nx, -ny), fata: -1 },
              ];
              return (
                <g>
                  {fete.map((l, j) => (
                    <line key={`f${j}`} x1={l.a[0]} y1={l.a[1]}
                          x2={l.b[0]} y2={l.b[1]}
                          stroke="transparent" strokeWidth={BANDA_GROSIME_PX}
                          vectorEffect="non-scaling-stroke" pointerEvents="stroke"
                          style={{ cursor: osia.lungPeX ? "ns-resize" : "ew-resize" }}
                          onPointerDown={(e) => {
                            if (e.button !== 0) return;
                            e.stopPropagation();
                            setGest({ fel: "redimPerete", i: ales.i, doc0: doc,
                                      prindere: { capat: null, fata: l.fata },
                                      pornit: false });
                          }}>
                      <title>{t("panza.trage_grosimea")}</title>
                    </line>
                  ))}
                </g>
              );
            })()}

            {/* CELE DOUĂ CERCURI DE LA CAPETE — v. `manere`. Trase, capătul
                merge oriunde în plan: zidul se rotește și se lungește după
                mână. */}
            {manaLibera && osia && ales?.fel === "perete" && manere.map((m, i) => (
              <circle key={i} cx={m.p[0]} cy={m.p[1]} r={R}
                      fill="#fff" stroke={SEL}
                      strokeWidth={2} vectorEffect="non-scaling-stroke"
                      style={{ cursor: "grab" }}
                      onPointerDown={(e) => {
                        if (e.button !== 0) return;
                        e.stopPropagation();
                        setGest({ fel: "capatPerete", i: ales.i, capat: m.capat,
                                  de: pct(e), doc0: doc, axa: osia.axa,
                                  pornit: false });
                      }}>
                <title>{t("panza.trage_capatul")}</title>
              </circle>
            ))}

            {/* APUCĂTOAREA DE CURBURĂ, pe mijlocul coardei. Trasă perpendicular,
                umflă zidul într-o parte sau în alta; adusă înapoi pe coardă, îl
                face drept la loc. Un romb, ca să nu se confunde cu cercurile
                care mută și lungesc. NUMAI CU BIFA «Perete curbat» din panou
                (sau pe un zid deja curb): cerut anume, ca un zid drept să nu
                poarte o apucătoare pe care o atingi din greșeală. */}
            {manaLibera && axAles && ales?.fel === "perete" && curbabil && (() => {
              const [a, b] = axAles;
              const cx = (a[0] + b[0]) / 2;
              const cy = (a[1] + b[1]) / 2;
              const dx = b[0] - a[0];
              const dy = b[1] - a[1];
              const c = Math.hypot(dx, dy) || 1;
              // DIAMANTUL NU STĂ PESTE CERCUL DIN MIJLOC. Pe un perete drept
              // săgeata e zero, deci amândouă ar cădea în același punct: două
              // apucătoare suprapuse, cu gesturi diferite, iar care dintre ele
              // răspunde ține de ordinea de desenare. Se ridică de pe coardă cu
              // o distanță fixă în PIXELI, ca să rămână despărțite la orice
              // mărire.
              const sagReal = (zidAles!.bulge * c) / 2;
              const sag = sagReal + (sagReal >= 0 ? 1 : -1) * (18 / k);
              // săgeata pozitivă e spre `(dy, −dx)`: acolo e vârful unui arc
              // cu `bulge > 0` (v. `arcDinAx`)
              const px = cx + (dy / c) * sag;
              const py = cy + (-dx / c) * sag;
              return (
                <g>
                  {(
                    <path d={`M${cx} ${cy}L${px} ${py}`} stroke={SEL_MOALE}
                          strokeWidth={1} strokeDasharray="4 3" fill="none"
                          vectorEffect="non-scaling-stroke" pointerEvents="none" />
                  )}
                  <rect x={px - R * 0.8} y={py - R * 0.8} width={R * 1.6}
                        height={R * 1.6} fill="#fff" stroke={SEL} strokeWidth={2}
                        vectorEffect="non-scaling-stroke"
                        transform={`rotate(45 ${px} ${py})`}
                        style={{ cursor: "ns-resize" }}
                        onPointerDown={(e) => {
                          if (e.button !== 0) return;
                          e.stopPropagation();
                          setGest({ fel: "curbeaza", i: ales.i, a, b, pornit: false });
                        }}>
                    <title>{t("panza.trage_curbura")}</title>
                  </rect>
                </g>
              );
            })()}

            {/* peretele care se desenează acum */}
            {gest?.fel === "peretNou" && (
              <path pointerEvents="none" stroke={SEL} strokeWidth={3} fill="none"
                    strokeDasharray="6 4" vectorEffect="non-scaling-stroke"
                    d={`M${gest.de[0]} ${gest.de[1]}L${gest.la[0]} ${gest.la[1]}`} />
            )}

            {/* CONTURUL, DE TRAS DE COLȚ ȘI DE LATURĂ.
                Un pătrat pe fiecare vârf — tras liber în plan — și o bară pe
                mijlocul fiecărei laturi, care o mută perpendicular pe ea. Dublu
                clic pe bară taie latura în două, ca forma să poată căpăta laturi
                noi: un dreptunghi rămâne altfel cu patru, oricât ai trage.

                Nimic dinăuntru nu se mișcă după contur. */}
            {manaLibera && ales?.fel === "anvelopa" && (() => {
              const o = plan.anvelopa;
              return (
                <g>
                  {o.map((a, i) => {
                    const b = o[(i + 1) % o.length];
                    const mx = (a[0] + b[0]) / 2;
                    const my = (a[1] + b[1]) / 2;
                    const dx = b[0] - a[0];
                    const dy = b[1] - a[1];
                    const lungPeX = Math.abs(dx) >= Math.abs(dy);
                    return (
                      <g key={`l${i}`}>
                        {/* bara laturii */}
                        <rect
                          x={mx - (lungPeX ? 9 / k : 3.5 / k)}
                          y={my - (lungPeX ? 3.5 / k : 9 / k)}
                          width={(lungPeX ? 18 : 7) / k}
                          height={(lungPeX ? 7 : 18) / k}
                          rx={2 / k} fill={SEL} stroke="#fff" strokeWidth={2}
                          vectorEffect="non-scaling-stroke"
                          style={{ cursor: lungPeX ? "ns-resize" : "ew-resize" }}
                          onPointerDown={(e) => {
                            if (e.button !== 0) return;
                            e.stopPropagation();
                            setGest({ fel: "laturaContur", i, de: pct(e),
                                      pornit: false });
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            laGestPornit();
                            laSchimbare(taieLaturaConturului(doc, i));
                          }}>
                          <title>trage ca să muți latura; dublu clic o taie în
                                 două</title>
                        </rect>
                        {/* colțul */}
                        <rect x={a[0] - 5 / k} y={a[1] - 5 / k}
                              width={10 / k} height={10 / k} rx={1.5 / k}
                              fill="#fff" stroke={SEL} strokeWidth={2.5}
                              vectorEffect="non-scaling-stroke"
                              style={{ cursor: "move" }}
                              onPointerDown={(e) => {
                                if (e.button !== 0) return;
                                e.stopPropagation();
                                setGest({ fel: "coltContur", i, pornit: false });
                              }}>
                          <title>{t("panza.trage_coltul")}</title>
                        </rect>
                      </g>
                    );
                  })}
                </g>
              );
            })()}

            {/* COLȚURILE CUTIEI NUCLEULUI — golul se strânge și se lărgește de
                aici, iar camerele din jur îl urmează (v. `redimNucleul`). */}
            {manaLibera && ales?.fel === "nucleu" && plan.nucleu && (() => {
              const [x0, y0, x1, y1] = plan.nucleu.rect;
              return (
                <g>
                  {manerele({ x: [x0, x1], y: [y0, y1] }, (apucare) => (e) => {
                    if (e.button !== 0) return;
                    e.stopPropagation();
                    setGest({ fel: "redimNucleu", apucare, pornit: false });
                  }, "trage ca să strângi sau să lărgești golul — camerele din"
                   + " jur îl urmează")}
                </g>
              );
            })()}

            {/* COLȚURILE UNEI PĂRȚI A NUCLEULUI — ghena se croiește de aici.
                Cerut de arhitect: ghena trasă de colțuri, ca orice
                dreptunghi pe care îl croiești pe loc (20.10.2026); până acum
                o parte aleasă se putea doar MUTA, iar mărimea ei se scria
                numai în panou.

                NUMAI PĂRȚILE FĂRĂ CATALOG, și numai COLȚURILE — v.
                `model::parteaSeCroieste` și nota lui `manerele`. Pe o scară
                sau pe un lift nu apare niciun mâner: măsurile lor vin din
                fabrică, iar una ieșită din mână n-ar fi o cabină care există.

                CUTIA ÎL URMEAZĂ dacă partea crește peste ea, cu camerele din
                jur (v. `redimParteaNucleului`). */}
            {manaLibera && ales?.fel === "nucleuParte"
              && plan.nucleu?.parti?.[ales.i]
              && parteaSeCroieste(plan.nucleu.parti[ales.i].kind) && (() => {
              const c = cadrulPartii(plan.nucleu!.parti[ales.i].inel);
              return (
                <g>
                  {manerele({ x: [c.x, c.x + c.w], y: [c.y, c.y + c.h] },
                    (apucare) => (e) => {
                      if (e.button !== 0) return;
                      e.stopPropagation();
                      setGest({ fel: "redimParte", i: ales.i, apucare,
                                pornit: false });
                    }, t("panza.trage_parte"), true)}
                </g>
              );
            })()}

            {/* LATURILE CAMEREI ALESE, de tras. O bară scurtă pe mijlocul
                fiecărei laturi — locul în care se apucă o margine în orice
                editor de plan —, cu zona de apucat lată de 10 px peste toată
                latura, ca să se poată prinde și la mărire mică. Trasă, camera
                își schimbă mărimea împingând zidul de pe latura aia, iar la
                capăt se lipește de fața zidului următor. */}
            {manaLibera && ales?.fel === "camera" && laturiAlese.map((l, j) => {
              const mij = (l.jos + l.sus) / 2;
              const P = (t: number, v: number): Pt =>
                (l.axa === 0 ? [v, t] : [t, v]) as Pt;
              const cursor = l.axa === 0 ? "ew-resize" : "ns-resize";
              const bara = Math.min((l.sus - l.jos) / 3, 18 / k);
              const prinde = (e: React.PointerEvent) => {
                if (e.button !== 0) return;
                e.stopPropagation();
                setGest({ fel: "laturaCamerei", i: ales.i, l, doc0: doc,
                          pornit: false });
              };
              return (
                <g key={`lc${j}`}>
                  <line x1={P(l.jos, l.valoare)[0]} y1={P(l.jos, l.valoare)[1]}
                        x2={P(l.sus, l.valoare)[0]} y2={P(l.sus, l.valoare)[1]}
                        stroke="transparent" strokeWidth={10}
                        vectorEffect="non-scaling-stroke" pointerEvents="stroke"
                        style={{ cursor }} onPointerDown={prinde}>
                    <title>trage ca să schimbi mărimea camerei; latura se
                           lipește de fața zidului de dincolo</title>
                  </line>
                  <line x1={P(mij - bara, l.valoare)[0]}
                        y1={P(mij - bara, l.valoare)[1]}
                        x2={P(mij + bara, l.valoare)[0]}
                        y2={P(mij + bara, l.valoare)[1]}
                        stroke={SEL} strokeWidth={4} strokeLinecap="round"
                        vectorEffect="non-scaling-stroke" pointerEvents="none" />
                </g>
              );
            })}

            {/* PUNCTELE PODESTULUI ALES — pătrate de tras, și cercuri cu +
                pe mijlocul muchiilor.
                Așa a fost arătat și aprobat (prototipul din 13.10.2026):
                pătratul se trage, cercul cu + adaugă un punct pe muchie, iar
                Alt-clic pe un pătrat îl scoate. Sub trei puncte nu mai e o
                formă, deci scoaterea tace.
                Stau în CADRUL PIESEI — mutat în centrul ei și întors cu
                unghiul ei —, ca desenul scării; conturul e scris în
                coordonatele planului, deci se aduce scăzând centrul. */}
            {manaLibera && ales?.fel === "scaraParte" && ales.parte === "podest"
              && (() => {
              const pi = plan.piese[ales.i];
              const f = doc.plan?.furniture?.[ales.i];
              if (!pi || !f?.scara) return null;
              const c = conturulPodestului(f, ales.k);
              if (c.length < 3) return null;
              const A = (q: Pt): Pt => [q[0] - pi.c[0], q[1] - pi.c[1]];
              const h = 5 / k;
              return (
                <g transform={`translate(${pi.c[0]} ${pi.c[1]}) `
                            + `rotate(${pi.deg})`}>
                  {c.map((q0, i) => {
                    const q = A(q0), r = A(c[(i + 1) % c.length]);
                    const m: Pt = [(q[0] + r[0]) / 2, (q[1] + r[1]) / 2];
                    return (
                      <g key={`pm${i}`}>
                        <circle data-maner="podest-plus" data-v={i}
                                cx={m[0]} cy={m[1]} r={6 / k} fill="#fff"
                                stroke={SEL} strokeWidth={1.5}
                                vectorEffect="non-scaling-stroke"
                                style={{ cursor: "copy" }}
                                onPointerDown={(e) => {
                                  if (e.button !== 0) return;
                                  e.stopPropagation();
                                  laGestPornit();
                                  laSchimbare(
                                    adaugaVarfPodest(doc, ales.i, ales.k, i));
                                }}>
                          <title>{t("panza.podest_adauga")}</title>
                        </circle>
                        <path pointerEvents="none" stroke={SEL} strokeWidth={1.5}
                              vectorEffect="non-scaling-stroke"
                              d={`M${m[0] - 3 / k} ${m[1]}h${6 / k}`
                               + `M${m[0]} ${m[1] - 3 / k}v${6 / k}`} />
                      </g>
                    );
                  })}
                  {c.map((q0, i) => {
                    const q = A(q0);
                    return (
                      <rect key={`pv${i}`} data-maner="podest" data-v={i}
                            x={q[0] - h} y={q[1] - h}
                            width={2 * h} height={2 * h} fill="#fff"
                            stroke={SEL} strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            style={{ cursor: "grab" }}
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              e.stopPropagation();
                              if (e.altKey) {
                                // sub trei puncte `model` nu scoate nimic,
                                // deci nu se face nici gest
                                const d = scoateVarfPodest(doc, ales.i,
                                                           ales.k, i);
                                if (d !== doc) { laGestPornit(); laSchimbare(d); }
                                return;
                              }
                              setGest({ fel: "varfPodest", i: ales.i,
                                        k: ales.k, varf: i, doc0: doc,
                                        pornit: false });
                            }}>
                        <title>{t("panza.podest_trage")}</title>
                      </rect>
                    );
                  })}
                </g>
              );
            })()}

            {/* LINIILE PE CARE S-A LIPIT obiectul mutat.
                Punctate, în culoarea selecției, și numai cât ține gestul: ele
                spun de ce s-a oprit obiectul acolo — fără ele, lipirea pare o
                mână care tremură. Linia se întinde de la muchia vecinului până
                la cursor, ca să se vadă amândoi capii alinierii. */}
            {gest && cursor && ghidaje.current.map((m, i) => {
              const alt = (1 - m.axa) as 0 | 1;
              const lo = Math.min(m.span[0], cursor[alt]);
              const hi = Math.max(m.span[1], cursor[alt]);
              const a: Pt = m.axa === 0 ? [m.valoare, lo] : [lo, m.valoare];
              const b: Pt = m.axa === 0 ? [m.valoare, hi] : [hi, m.valoare];
              return (
                <path key={`gh${i}`} pointerEvents="none" fill="none"
                      stroke={SEL} strokeWidth={1} strokeDasharray="4 4"
                      vectorEffect="non-scaling-stroke"
                      d={`M${a[0]} ${a[1]}L${b[0]} ${b[1]}`} />
              );
            })}

            {/* MÂNERUL DE ROTIRE al piesei SAU AL ZIDULUI ales.
                O bilă deasupra mijlocului lui, la capătul unei tije — locul în
                care îl caută oricine a rotit ceva într-un editor de plan. Tras,
                unghiul urmărește cursorul; cu Shift ținut, se lipește din 15 în
                15. Tastele R și «,»/«.» fac același lucru fără mouse.

                Zidul l-a primit ultimul, cerut anume: până atunci se putea
                roti numai trăgând de un capăt, adică schimbându-i și lungimea
                în același gest.

                CAMERA NU MAI ARE BILĂ. O cameră nu se întoarce — v. antetul. */}
            {/* MÂNERELE DE MĂRIME ALE UNEI PIESE DE MOBILIER.
                Cerut pe 19.10.2026: «pull an item (furniture) inward or
                outward it should increase or decrease the size by 5 cm at a
                time». Trasă o muchie, cea opusă rămâne pe loc, iar mărimea se
                lipește din 5 în 5 cm — v. `model::redimMobila`.

                ÎNĂUNTRUL PIESEI, NU PESTE MUCHIA EI, și asta e tot miezul.
                Mânerele au fost scoase pe `daa91840` fiindcă bara de latură (7
                px, adică 19 cm la 37 px/m) se întindea de-o parte și de alta a
                muchiei și acoperea rostul dintre piesă și zidul de lângă ea: pe
                M3, cu liftul ales, nu exista un pixel din care zidul să mai
                poată fi apucat. Împinse ÎNĂUNTRU cu jumătate din grosimea lor,
                ele nu mai ies deloc din piesă, deci nu mai pot fura un clic
                care era al zidului.

                NU ȘI STRUCTURA CU MĂSURI DATE: lifturile și scările rămân cu
                mărimea scrisă în panou, «au dimensiuni fixe, așa vin din
                fabrică», iar o scară are treapta calculată.

                GHENA ÎNSĂ DA, și NUMAI DIN COLȚURI. Ea e `structura: true` în
                catalogul de piese, deci steagul o scotea de sub mână laolaltă
                cu lifturile — de-aia «it still doesn't let me modify the
                ghena» (20.10.2026) chiar după ce părțile de nucleu căpătaseră
                mânere: ghena din paletă nu e o parte de nucleu, e o PIESĂ.
                Întrebarea bună nu e «e structură?», ci «are măsuri din
                fabrică?» — v. `model::parteaSeCroieste`. Fără benzile de
                latură, cerute pe nume: «corners i meant not edges».

                ÎN CADRUL PIESEI: mânerele se rotesc odată cu ea, fiindcă «mai
                lat» e pe axa ei, nu pe est-vest. */}
            {/* TOATE PIESELE ALESE ÎȘI ARATĂ PĂTRĂȚELELE, nu doar ultima
                apăsată. Cerut de arhitect (21.10.2026): «când sunt mai multe
                obiecte selectate (...) să fie vizibile pătrățelele de editare
                din colțuri pentru toate obiectele selectate» — cu cinci piese
                aprinse, un singur set de mânere nu spunea care e grupul.

                ȘI SUNT ADEVĂRATE, fiecare pe piesa lui: un mâner desenat dar
                mort ar fi mai rău decât niciunul, ca o tastă care nu face
                nimic. Redimensionarea rămâne a UNEI piese — ce s-a cerut e să
                se vadă grupul, nu să se întindă tot odată. */}
            {manaLibera && !doarCitire && (() => {
              const indici = mobilaAlese?.length ? mobilaAlese
                : ales?.fel === "mobila" ? [ales.i] : [];
              if (!indici.length) return null;
              // Grosimea barei pe ecran, adusă în metri, ca să se poată împinge
              // înăuntru exact cu jumătate din ea.
              const gros = 7 / k;
              return (
                <>
                  {indici.map((i) => {
                    const pi = plan.piese[i];
                    if (!pi) return null;
                    const kind = pi.kind ?? "";
                    if (felulPiesei(kind)?.structura && !parteaSeCroieste(kind)) {
                      return null;
                    }
                    // Pe structură, numai colțurile: patru pătrățele în loc de
                    // benzi pe toată muchia, lângă un zid de care ghena stă la
                    // câțiva centimetri.
                    const doarColturi = !!felulPiesei(kind)?.structura;
                    const w2 = pi.w / 2, h2 = pi.h / 2;
                    return (
                      <g key={`man${i}`}
                         transform={`translate(${pi.c[0]} ${pi.c[1]}) `
                                   + `rotate(${pi.deg})`}>
                        {manerele(
                          { x: [-w2 + gros / 2, w2 - gros / 2],
                            y: [-h2 + gros / 2, h2 - gros / 2] },
                          (apucare) => (e) => {
                            if (e.button !== 0) return;
                            e.stopPropagation();
                            setGest({ fel: "redimMobila", i, apucare,
                                      pornit: false });
                          },
                          t("panza.trage_marimea"), doarColturi, pi.deg)}
                      </g>
                    );
                  })}
                </>
              );
            })()}

            {/* CAPETELE UNEI FERESTRE ALESE, de tras.
                Cerut pe 19.10.2026, după mobilă: «add this functionality for
                windows as well». Un gol stă ÎN zid, deci singura lui mărime
                care se vede în plan e lățimea — capătul apucat pleacă în lungul
                peretelui, celălalt rămâne pe loc, din 5 în 5 cm
                (v. `model::redimGolul`). Înălțimea și parapetul rămân în panou:
                nu se văd în plan, iar un mâner pentru o cifră nevăzută ar
                minți.

                NUMAI FERESTRE. Ușile au balama, sens și rol, iar capătul lor
                ține de toc — alea se cer anume, nu se adaugă din mers.

                SEMNUL CAPĂTULUI SE MĂSOARĂ, nu se presupune din `a`/`b`:
                `redimGolul` socotește pe axa golului din document (`rot_deg`),
                iar care din cele două tocuri cade pe `+` depinde de cum a fost
                scris golul. Proiectat aici, semnul e același cu al lui. */}
            {manaLibera && !doarCitire && ales?.fel === "gol"
              && ales.fel2 === "windows" && (() => {
                const gd = doc.plan?.windows?.[ales.i];
                const g = plan.goluri.find(
                  (x) => x.fel === "fereastra" && x.idx === ales.i);
                if (!gd || !g) return null;
                const i = ales.i;
                const rad = ((gd.rot_deg ?? 0) * Math.PI) / 180;
                const ux = Math.cos(rad), uy = Math.sin(rad);
                return (
                  <g>
                    {[g.a, g.b].map((q, j) => {
                      const capat: -1 | 1 =
                        (q[0] - g.c[0]) * ux + (q[1] - g.c[1]) * uy >= 0 ? 1 : -1;
                      return (
                        <rect key={j} x={q[0] - 4 / k} y={q[1] - 4 / k}
                              width={8 / k} height={8 / k} rx={1.5 / k}
                              fill="#fff" stroke={SEL} strokeWidth={2}
                              vectorEffect="non-scaling-stroke"
                              style={{ cursor: cursorulPeDirectie(ux, uy) }}
                              onPointerDown={(e) => {
                                if (e.button !== 0) return;
                                e.stopPropagation();
                                setGest({ fel: "redimGol", fel2: "windows", i,
                                          capat, pornit: false });
                              }}>
                          <title>{t("panza.trage_latimea")}</title>
                        </rect>
                      );
                    })}
                  </g>
                );
              })()}

            {(() => {
              const pi = ales?.fel === "mobila" ? plan.piese[ales.i] : null;
              // ȘI ZIDUL SE ROTEȘTE DE AICI. Are acum aceeași bilă pe o tijă,
              // deasupra mijlocului lui — cerut anume, și oricum e locul în
              // care o caută cineva care tocmai a rotit o canapea. Cutia se ia
              // din INELUL zidului, nu din ax: bila trebuie să stea deasupra a
              // ce se vede, iar ce se vede e dreptunghiul cu grosime cu tot.
              const zid = ales?.fel === "perete" ? zidAles?.inel ?? null : null;
              if (!pi && !zid) return null;
              // ȘI PIESA ÎȘI DĂ CONTURUL ROTIT, ca zidul.
              //
              // Erau laturile cutiei NEROTITE (`c ± w/2`, `c ± h/2`), adică o
              // cutie care nu știe de `deg`: bila rămânea deasupra amprentei de
              // dinainte de rotire, iar pe o piesă lungă și subțire — un raft
              // deschis — întoarsă la 90°, ea rămânea de-a dreptul lângă piesă.
              // «Dacă rotesc un raft deschis, grip-ul pentru rotire rămâne pe
              // loc, nu se rotește cu obiectul» (27.10.2026). Zidul n-avea
              // defectul fiindcă el dădea de la bun început inelul lui adevărat.
              const colturiPiesei = (): Pt[] => {
                const r = (pi!.deg * Math.PI) / 180;
                const co = Math.cos(r), si = Math.sin(r);
                const hw = pi!.w / 2, hh = pi!.h / 2;
                return ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as const)
                  .map(([u, v]) => [pi!.c[0] + u * co - v * si,
                                    pi!.c[1] + u * si + v * co] as Pt);
              };
              const contur = zid ?? colturiPiesei();
              const xs = contur.map((q) => q[0]);
              const ys = contur.map((q) => q[1]);
              const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
              const sus = Math.min(...ys);
              const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
              const m: Pt = [cx, sus - 26 / k];
              // CENTRUL ROTIRII. Zidul se întoarce în jurul mijlocului AXULUI
              // lui — acolo îl întoarce și `rotestePeretele` —, nu al cutiei de
              // încadrare: pe un zid deja oblic cele două nu sunt același
              // punct, iar bila ar fi rotit în jurul altui centru decât
              // documentul. Unghiul de pornire se măsoară din ACELAȘI punct,
              // altfel prima mișcare ar sări cu diferența dintre ele.
              const c0: Pt = ales?.fel === "perete" && axAles
                ? [(axAles[0][0] + axAles[1][0]) / 2,
                   (axAles[0][1] + axAles[1][1]) / 2]
                : [cx, cy];
              return (
                <g>
                  <path d={`M${cx} ${sus}L${m[0]} ${m[1]}`} stroke={SEL}
                        strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  <circle cx={m[0]} cy={m[1]} r={7 / k} fill="#fff" stroke={SEL}
                          strokeWidth={2} vectorEffect="non-scaling-stroke"
                          style={{ cursor: "grab" }}
                          onPointerDown={(e) => {
                            if (doarCitire) return;
                            e.stopPropagation();
                            const q = pct(e);
                            setGest({
                              fel: "roteste",
                              ce: ales!.fel === "perete" ? "perete" : "mobila",
                              // Bila se desenează numai pentru mobilă și
                              // perete (v. condiția de deasupra), iar amândouă
                              // au indice; nucleul n-are, dar nu ajunge
                              // niciodată aici.
                              i: "i" in ales! ? ales.i : 0,
                              c: c0,
                              // Unghiul de PORNIRE al obiectului: piesa îl
                              // spune de-a dreptul, zidul îl are în ax.
                              ...(() => {
                                const r0 = zid && axAles
                                  ? (Math.atan2(axAles[1][1] - axAles[0][1],
                                                axAles[1][0] - axAles[0][0])
                                     * 180) / Math.PI
                                  : pi?.deg ?? 0;
                                return { rot0: r0, aplicat: r0 };
                              })(),
                              doc0: doc,
                              unghi0: (Math.atan2(q[1] - c0[1], q[0] - c0[0])
                                       * 180) / Math.PI,
                              pornit: false,
                            });
                          }} />
                </g>
              );
            })()}

            {gest?.fel === "scaraGata" && (() => {
              // the stair as it will be put: along the wall before the hand
              // has chosen a way, then the way it chose
              const dir: Pt = gest.dir ?? directiaScarii(gest.fete, null);
              const pr = rampelePresetului(gest.cheie, gest.de,
                                           doc.storey_height_m ?? 2.8, dir,
                                           gest.oglinda, gest.trepte ?? undefined);
              if (!pr) return null;
              return (
                <g pointerEvents="none" opacity={0.9}
                   data-scara-gata={`${gest.de[0]} ${gest.de[1]} ${dir[0]} ${dir[1]} ${gest.trepte ?? ""}`}
                   style={{ "--mob-gros": 0.8 } as React.CSSProperties}>
                  <ScaraDinRampe rampe={pr.rampe} latime={pr.latime}
                                 hNivel={doc.storey_height_m ?? 2.8}
                                 traseu={pr.rampe.length > 1} />
                </g>
              );
            })()}

            {gest?.fel === "scaraNoua" && (() => {
              const rampe = rampeleScarii(gest);
              if (!rampe.length) return null;
              return (
                <g pointerEvents="none" opacity={0.9}
                   style={{ "--mob-gros": 0.8 } as React.CSSProperties}>
                  <ScaraDinRampe rampe={rampe} latime={gest.latime}
                                 hNivel={doc.storey_height_m ?? 2.8}
                                 treapta={treaptaScarii}
                                 traseu={gest.rampe.length > 1} />
                </g>
              );
            })()}

            {/* ÎNTINDEREA: fereastra cât se trage ea, pe urmă ce s-a prins
                și unde ajunge. Fereastra PIERE după alegere — cât rămâne pe
                ecran, pare că bucata aleasă n-are voie să iasă din ea
                (15.10.2026). */}
            {/* GOLUL ÎN MÂNĂ: dreptunghiul lui sub cursor, oriunde ar fi, cu
                semnul dacă poate sau nu să fie așezat acolo. */}
            {gest?.fel === "mutaGol" && gest.la && (() => {
              const g = doc.plan?.[gest.fel2]?.[gest.i];
              if (!g) return null;
              const lat = g.w_m ?? 0.9;
              // NEROTUNJIT AICI: `la` e deja pe pasul măsurat de la poziția
              // de la apăsare (v. `laMiscare`), iar o a doua rotunjire pe
              // grila absolută ar fi întrebat despre alt punct decât cel
              // desenat — fantoma s-ar fi aprins sau stins pe alt loc.
              const poate = mutaGolulLiber(doc, gest.fel2, gest.i,
                                           gest.la) !== doc;
              const rad = ((g.rot_deg ?? 0) * Math.PI) / 180;
              return (
                <g pointerEvents="none" data-gol-mutat={poate ? "pe-zid" : "afara"}
                   /* UNDE STĂ FANTOMA, pentru probe: pasul de 5 cm e o
                      purtare care se vede, iar din transformarea desenată el
                      nu se poate citi fără să se refacă socoteala. */
                   data-fantoma-gol={`${r3(gest.la[0])} ${r3(gest.la[1])}`}
                   transform={`translate(${gest.la[0]} ${gest.la[1]}) `
                     + `rotate(${((rad * 180) / Math.PI).toFixed(2)})`}>
                  <rect x={-lat / 2} y={-0.15} width={lat} height={0.3}
                        fill={poate ? SEL : HARTIE} fillOpacity={poate ? 0.25 : 0.6}
                        stroke={poate ? SEL : TUS_CULOARE} strokeWidth={2}
                        strokeDasharray={poate ? undefined : "5 4"}
                        vectorEffect="non-scaling-stroke" />
                </g>
              );
            })()}
            {/* CAPĂTUL DINTÂI AL SPĂRTURII, ÎNAINTE SĂ FIE PUS.
                Cât unealta e armată și încă n-a fost nicio apăsare, punctul
                urmează cursorul LIPIT acolo unde chiar va cădea — pe
                încrucișarea de ziduri de lângă, altfel pe pasul de 5 cm (v.
                `capatSparturii`). Fără el nu se vedea de ce se agață spărtura
                decât DUPĂ ce apăsai, adică prea târziu ca să mai alegi
                altceva: «when we create a wall break we should see the initial
                point like in the cote and see it snap to the correct position
                cause now we just see our mouse and it doesn't show the snap»
                (20.10.2026). Același semn ca la cotă, fiindcă e aceeași
                treabă — un capăt de secțiune pus cu mâna. */}
            {unealta === "demoleaza" && !gest && cursor && (() => {
              const c = capatSparturii(cursor);
              if (!c) return null;                 // mâna nu e pe niciun zid
              const o = osiaPeretelui(plan.pereti[c.i].inel);
              const mij = (Math.min(...o.fete) + Math.max(...o.fete)) / 2;
              const q: Pt = c.lung === 0 ? [c.t, mij] : [mij, c.t];
              return (
                <g pointerEvents="none" data-sparge-pornire="1">
                  <circle cx={q[0]} cy={q[1]} r={4 / k} fill={SEL} />
                  {/* Cruciulița spune că punctul S-A LIPIT undeva, nu că
                      plutește: fără ea, un cerc pe o încrucișare arată la fel
                      ca unul la doi centimetri de ea. */}
                  <path d={`M${q[0] - 9 / k} ${q[1]}h${18 / k}`
                           + `M${q[0]} ${q[1] - 9 / k}v${18 / k}`}
                        stroke={SEL} strokeWidth={1} strokeOpacity={0.7}
                        vectorEffect="non-scaling-stroke" fill="none" />
                </g>
              );
            })()}
            {/* SPĂRTURA, cât se trage: banda care va pleca, peste zid. */}
            {gest?.fel === "sparge" && (() => {
              const k2 = k;
              const w = plan.pereti[gest.i];
              if (!w) return null;
              const o = osiaPeretelui(w.inel);
              const [a0, b0] = [Math.min(gest.t0, gest.t1),
                                Math.max(gest.t0, gest.t1)];
              const [f0, f1] = [Math.min(...o.fete), Math.max(...o.fete)];
              const x = gest.lung === 0 ? a0 : f0;
              const y = gest.lung === 0 ? f0 : a0;
              const latime = gest.lung === 0 ? b0 - a0 : f1 - f0;
              const inalt = gest.lung === 0 ? f1 - f0 : b0 - a0;
              return (
                <g pointerEvents="none" data-sparge="1">
                  <rect x={x} y={y} width={latime} height={inalt}
                        fill={HARTIE} fillOpacity={0.85} stroke={SEL}
                        strokeWidth={2} strokeDasharray="6 4"
                        vectorEffect="non-scaling-stroke" />
                  {/* CELE DOUĂ CAPETE, ca bulinele unei cote: ele spun unde
                      s-a pus prima apăsare și unde ar cădea a doua. */}
                  {[gest.t0, gest.t1].map((t, k) => (
                    <circle key={k} r={4 / k2}
                            cx={gest.lung === 0 ? t : (f0 + f1) / 2}
                            cy={gest.lung === 0 ? (f0 + f1) / 2 : t}
                            fill={SEL} />
                  ))}
                </g>
              );
            })()}
            {/* CHENARUL DE ALES, cât ține tragerea. Fără umplutură: el nu
                acoperă nimic, doar arată cât cuprinde — spre deosebire de
                fereastra de ÎNTINS, care e o foaie peste desen. */}
            {gest?.fel === "alegeFer" && (
              <rect pointerEvents="none"
                    x={Math.min(gest.de[0], gest.la[0])}
                    y={Math.min(gest.de[1], gest.la[1])}
                    width={Math.abs(gest.la[0] - gest.de[0])}
                    height={Math.abs(gest.la[1] - gest.de[1])}
                    fill={SEL} fillOpacity={0.08} stroke={SEL}
                    strokeWidth={1.5} strokeDasharray="5 4"
                    vectorEffect="non-scaling-stroke" />
            )}
            {gest?.fel === "intindeFer" && (() => {
              const x0 = Math.min(gest.de[0], gest.la[0]);
              const y0 = Math.min(gest.de[1], gest.la[1]);
              return (
                <rect x={x0} y={y0}
                      width={Math.abs(gest.la[0] - gest.de[0])}
                      height={Math.abs(gest.la[1] - gest.de[1])}
                      fill={SEL} fillOpacity={0.07} stroke={SEL}
                      strokeWidth={1.5} strokeDasharray="6 4"
                      vectorEffect="non-scaling-stroke" pointerEvents="none" />
              );
            })()}
            {/* BUCATA DIN MÂNĂ, desenată acolo unde ar cădea.
                Contur, nu planșă: ea încă nu e în document, iar desenată în
                tușul planșei n-ai mai fi știut ce e pus și ce ții în mână.
                Zidurile umplute stins, camerele conturate — destul cât să
                recunoști bucata și să nimerești unde o vrei. */}
            {gest?.fel === "lipeste" && (() => {
              const cale = (poly: readonly VarfAfin[] | undefined) => {
                const r = (poly ?? []).map(
                  (v) => `${v[0].a} ${v[1].a}`);
                return r.length ? `M${r.join("L")}Z` : "";
              };
              return (
                <g pointerEvents="none"
                   transform={`translate(${gest.la[0]} ${gest.la[1]})`}>
                  {gest.b.rooms.map((c, i) => (
                    <path key={`lr${i}`} d={cale(c.poly)} fill={SEL}
                          fillOpacity={0.05} stroke={SEL} strokeWidth={1}
                          strokeDasharray="4 3" strokeOpacity={0.7}
                          vectorEffect="non-scaling-stroke" />
                  ))}
                  {gest.b.walls.map((w, i) => (
                    <path key={`lw${i}`} d={cale(w.poly)} fill={SEL}
                          fillOpacity={0.18} stroke={SEL} strokeWidth={2}
                          vectorEffect="non-scaling-stroke" />
                  ))}
                  {/* Mobila, ca dreptunghiuri: simbolul fiecărei piese ar fi
                      fost mai frumos, dar fantoma trebuie să se deseneze la
                      fiecare mișcare de mouse, iar un dreptunghi se vede la
                      fel de bine la mărimea la care se nimerește un loc. */}
                  {gest.b.furniture.map((m, i) => (
                    <rect key={`lf${i}`}
                          x={m.x.a - (m.w_m ?? 0.6) / 2}
                          y={m.y.a - (m.h_m ?? 0.6) / 2}
                          width={m.w_m ?? 0.6} height={m.h_m ?? 0.6}
                          transform={`rotate(${m.rot_deg ?? 0} ${m.x.a} ${m.y.a})`}
                          fill={SEL} fillOpacity={0.12} stroke={SEL}
                          strokeWidth={1} vectorEffect="non-scaling-stroke" />
                  ))}
                </g>
              );
            })()}
            {gest?.fel === "intindeTrag" && (() => {
              const ce = ceCuprinde(gest.doc0, gest.f, filtruFerestrei);
              // AXELE LOCULUI DE PORNIRE: prin mijlocul ferestrei, punctate,
              // peste tot planul. Ele spun de unde a plecat bucata și pe ele
              // se lipește tragerea (v. `LIPIRE_AX_PX`).
              const ax = { x: (gest.f.x0 + gest.f.x1) / 2,
                           y: (gest.f.y0 + gest.f.y1) / 2 };
              const c0 = plan.cadru;
              const M = 3;
              const zid = (i: number): Pt[] =>
                (gest.doc0.plan?.walls?.[i]?.poly ?? []).map(
                  (v) => [v[0].a, v[1].a] as Pt);
              const cale2 = (r: Pt[]) =>
                r.length ? `M${r.map((q) => `${q[0]} ${q[1]}`).join("L")}Z` : "";
              const [dx, dy] = gest.dep;
              return (
                <g pointerEvents="none">
                  {/* AXELE DE PORNIRE, punctate. Pe ele se lipește tragerea
                      când bucata ajunge aproape — cerut anume (15.10.2026). */}
                  <path d={`M${c0.x - M} ${ax.y}H${c0.x + c0.w + M}`}
                        stroke={SEL} strokeWidth={1} strokeOpacity={0.6}
                        strokeDasharray="6 5" vectorEffect="non-scaling-stroke"
                        fill="none" data-ax="y" />
                  <path d={`M${ax.x} ${c0.y - M}V${c0.y + c0.h + M}`}
                        stroke={SEL} strokeWidth={1} strokeOpacity={0.6}
                        strokeDasharray="6 5" vectorEffect="non-scaling-stroke"
                        fill="none" data-ax="x" />
                  {/* UNDE ERAU: conturul de pornire, punctat. Albastru ce se
                      mută întreg, chihlimbariu ce se întinde — două feluri de
                      a fi prins, două semne. */}
                  {ce.ziduriIntregi.map((i) => (
                    <path key={`zi${i}`} d={cale2(zid(i))} fill="none"
                          stroke={SEL} strokeWidth={2} strokeDasharray="5 4"
                          vectorEffect="non-scaling-stroke" />
                  ))}
                  {/* ZIDURILE ÎNTINSE SE DESENEAZĂ NORMAL, nu aprinse.
                      Ele nu sunt alese — sunt doar legate de ce e ales, și se
                      lungesc după el. Arătate aprins și umplute, cum au fost o
                      jumătate de oră, umpleau planșa de chihlimbariu: fațadele
                      o traversează toată, deci se aprindeau toate, iar desenul
                      părea stricat. «Keep only the selected portion highlighted
                      and the rest of the attached walls normal» (15.10.2026).
                      Se vede DOAR unde ajung — conturul lor întins, în tușul
                      planșei. */}
                  {ce.ziduriTaiate.map((i) => {
                    const r = zid(i).map((q: Pt) =>
                      (q[0] >= gest.f.x0 && q[0] <= gest.f.x1
                       && q[1] >= gest.f.y0 && q[1] <= gest.f.y1
                        ? [q[0] + dx, q[1] + dy] as Pt : q));
                    return (
                      <path key={`zt${i}`} d={cale2(r)} fill={HARTIE}
                            stroke={TUS_CULOARE} strokeWidth={1}
                            vectorEffect="non-scaling-stroke" />
                    );
                  })}
                  {/* …și unde ajung: aceleași contururi, mutate.
                      ȘI PIESELE, NU DOAR ZIDURILE. `mutaCuprinsul` duce tot ce
                      a prins fereastra — mobilă, aparate, camere —, dar
                      previzualizarea arăta numai pereții, deci mâna nu vedea
                      jumătate din ce mișcă: «in the preview it should show the
                      furniture and other items too, not just the walls»
                      (27.10.2026). */}
                  <g transform={`translate(${dx} ${dy})`}>
                    {ce.ziduriIntregi.map((i) => (
                      <path key={`zf${i}`} d={cale2(zid(i))} fill={SEL}
                            fillOpacity={0.12} stroke={SEL} strokeWidth={2}
                            vectorEffect="non-scaling-stroke" />
                    ))}
                    {ce.piese.map((i) => {
                      const f = plan.piese[i];
                      if (!f) return null;
                      // Cutia piesei, întoarsă cu ea — aceeași socoteală ca a
                      // mânerelor (v. `colturiApropiate`). Pe cutia nerotită, o
                      // piesă lungă la 90° ar fi arătată în altă parte decât
                      // ajunge.
                      const r = (f.deg * Math.PI) / 180;
                      const co = Math.cos(r), si = Math.sin(r);
                      const hw = f.w / 2, hh = f.h / 2;
                      const col = ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as const)
                        .map(([u, v]) => [f.c[0] + u * co - v * si,
                                          f.c[1] + u * si + v * co] as Pt);
                      return (
                        <path key={`pf${i}`} d={cale2(col)} fill={SEL}
                              fillOpacity={0.12} stroke={SEL} strokeWidth={2}
                              vectorEffect="non-scaling-stroke" />
                      );
                    })}
                    {ce.electrice.map((i) => {
                      const a2 = plan.electrice[i];
                      if (!a2) return null;
                      return (
                        <circle key={`af${i}`} cx={a2.c[0]} cy={a2.c[1]}
                                r={7 / k} fill="none" stroke={SEL}
                                strokeWidth={2} vectorEffect="non-scaling-stroke" />
                      );
                    })}
                  </g>

                  {/* CÂT S-A TRAS, scris lângă fereastră. */}
                  {(dx || dy) && (
                    <text x={(gest.f.x0 + gest.f.x1) / 2 + dx}
                          y={gest.f.y1 + dy + 0.5} textAnchor="middle"
                          fontSize={13 / k} fontWeight={700}
                          fill={SEL}>
                      {`${dx ? `${dx > 0 ? "+" : ""}${dx.toFixed(2)} m` : ""}`
                       + `${dx && dy ? " · " : ""}`
                       + `${dy ? `${dy > 0 ? "+" : ""}${dy.toFixed(2)} m` : ""}`}
                    </text>
                  )}
                </g>
              );
            })()}

            {/* COTA ÎN LUCRU: linia de la primul punct la cursor, cu
                măsura pe ea. Fără ea, prima apăsare n-ar schimba nimic pe
                ecran și n-ai ști că unealta te-a auzit. */}
            {/* PRIMUL PUNCT, ÎNAINTE SĂ FIE PUS. Cât unealta e armată și încă
                n-a fost nicio apăsare, punctul de pornire urmează cursorul,
                LIPIT de aceleași muchii ca al doilea (v. `colturiApropiate`).
                Fără el nu se vedea de ce se agață cota decât DUPĂ ce apăsai,
                adică prea târziu ca să mai alegi altceva: «when placing the
                first dot for the cota we should see it before placing it like
                the starting point and it should snap to the walls and
                furniture like we did for the second point» (19.10.2026). */}
            {unealta === "cota" && !gest && cursor && (() => {
              const q = colturiApropiate(cursor);
              return (
                <g pointerEvents="none" data-cota-pornire="1">
                  <circle cx={q[0]} cy={q[1]} r={4 / k} fill={SEL} />
                  {/* Cruciulița spune că punctul S-A LIPIT undeva, nu că
                      plutește: fără ea, un cerc pe o muchie arată la fel ca
                      unul la doi centimetri de ea. */}
                  <path d={`M${q[0] - 9 / k} ${q[1]}h${18 / k}`
                           + `M${q[0]} ${q[1] - 9 / k}v${18 / k}`}
                        stroke={SEL} strokeWidth={1} strokeOpacity={0.7}
                        vectorEffect="non-scaling-stroke" fill="none" />
                </g>
              );
            })()}
            {!doarCitire && ales?.fel === "aparat" && (() => {
              const b = plan.electrice.find((x) => x.idx === ales.i);
              if (b?.kind !== "banda_led" || !b.end) return null;
              const capete = [["start", b.c], ["end", b.end]] as const;
              return (
                <g data-banda-capete="1">
                  {capete.map(([cap, q]) => (
                    <circle key={cap} data-banda="capat" data-capat={cap}
                            cx={q[0]} cy={q[1]} r={6 / k} fill={HARTIE} stroke={SEL}
                            strokeWidth={2} vectorEffect="non-scaling-stroke"
                            style={{ cursor: "grab" }} pointerEvents="all"
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              e.stopPropagation();
                              setGest({ fel: "capatBanda", i: ales.i, capat: cap,
                                        pornit: false });
                            }} />
                  ))}
                </g>
              );
            })()}
            {gest?.fel === "bandaNoua" && (() => {
              const rad = (gest.rot * Math.PI) / 180;
              const b: Pt = [gest.a[0] + Math.sin(rad) * gest.la,
                             gest.a[1] - Math.cos(rad) * gest.la];
              return (
                <g pointerEvents="none" data-banda-noua="1">
                  <path d={`M${gest.a[0]} ${gest.a[1]}L${b[0]} ${b[1]}`} stroke={SEL}
                        strokeWidth={4} strokeDasharray="8 5"
                        vectorEffect="non-scaling-stroke" fill="none" />
                </g>
              );
            })()}
            {gest?.fel === "cotaNoua" && (() => {
              const L = Math.hypot(gest.la[0] - gest.a[0],
                                   gest.la[1] - gest.a[1]);
              const m: Pt = [(gest.a[0] + gest.la[0]) / 2,
                             (gest.a[1] + gest.la[1]) / 2];
              const r = 4 / k;
              return (
                <g pointerEvents="none" data-cota-noua="1">
                  <path d={`M${gest.a[0]} ${gest.a[1]}L${gest.la[0]} ${gest.la[1]}`}
                        stroke={SEL} strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke" fill="none" />
                  <circle cx={gest.a[0]} cy={gest.a[1]} r={r} fill={SEL} />
                  <circle cx={gest.la[0]} cy={gest.la[1]} r={r} fill={SEL} />
                  {L > 0.01 && (
                    <text x={m[0]} y={m[1] - 6 / k} textAnchor="middle"
                          fontSize={13 / k} fontWeight={700} fill={SEL}>
                      {L.toFixed(2)}
                    </text>
                  )}
                </g>
              );
            })()}

            {gest?.fel === "camLibera" && (() => {
              const pts = [...gest.puncte, gest.la];
              return (
                <g pointerEvents="none">
                  <path stroke={SEL} strokeWidth={2} fill={SEL} fillOpacity={0.1}
                        strokeDasharray="6 4" vectorEffect="non-scaling-stroke"
                        d={`M${pts.map((q) => `${q[0]} ${q[1]}`).join("L")}`
                         + (gest.puncte.length >= 3 ? "Z" : "")} />
                  {gest.puncte.map((q, i) => (
                    <circle key={i} cx={q[0]} cy={q[1]} r={5 / k}
                            fill={i === 0 ? "#fff" : SEL} stroke={SEL}
                            strokeWidth={2} vectorEffect="non-scaling-stroke" />
                  ))}
                </g>
              );
            })()}

            {/* CONTURUL CAMEREI care se trage — forma întreagă, nu dreptunghiul
                din care iese: pe un L sau un T, dreptunghiul ar arăta altceva
                decât se va pune, iar cine trage trebuie să vadă chiar forma. */}
            {gest?.fel === "cameraNoua" && (() => {
              const c = conturCamerei(gest.de, gest.la, gest.forma);
              return (
                <path pointerEvents="none" stroke={SEL} strokeWidth={2}
                      fill={SEL} fillOpacity={0.12}
                      strokeDasharray="6 4" vectorEffect="non-scaling-stroke"
                      d={`M${c.map((p) => `${p[0]} ${p[1]}`).join("L")}Z`} />
              );
            })()}

            {cotaVie && !cotaVie.editabil && (() => {
              // ETICHETA SE STRÂNGE PE TEXT. Lățimile erau fixe — 78 și 108 —,
              // iar cifra și numele mărimii nu mai au aceeași lungime de când
              // cadrul scrie când «grosime», când «lungime × grosime»: fixă,
              // banda ori tăia numele, ori lăsa un capăt gol lângă cursor.
              const w1 = cotaVie.text.length * 7.2;
              const xSub = 20 + w1 + 8;
              const lat = (cotaVie.sub ? xSub + cotaVie.sub.length * 6 : 20 + w1)
                + 8 - 12;
              return (
                <g pointerEvents="none" data-cota-vie="1"
                   transform={`translate(${cotaVie.p[0]} ${cotaVie.p[1]}) scale(${1 / k})`}>
                  <rect x={12} y={-30} width={lat} height={22} rx={5} fill={SEL} />
                  <text x={20} y={-14} fontSize={12} fill="#fff" fontWeight={600}>
                    {cotaVie.text}
                  </text>
                  {cotaVie.sub && (
                    <text x={xSub} y={-14} fontSize={11} fill="#cfe0f5">
                      {cotaVie.sub}
                    </text>
                  )}
                </g>
              );
            })()}
          </g>
        )}
      </svg>

      {/* CASETELE DESCHISE LA SCRIS, peste pânză. Sunt HTML, nu desen — v.
          `CasetaCotei` pentru de ce nu mai stau într-un `foreignObject`.
          Locul lor iese din aceeași transformare ca a desenului, deci se mută
          cu el la panoramare și la mărire. */}
      {view && cotaAx && (() => {
        const d = distanteleAxelor(axeleLui)[cotaAx.j];
        if (!d) return null;
        const c = plan.cadru;
        const unde = (d.axa === 0 ? c.y : c.x) - AX_IESIRE - AX_COTA_PX / k;
        const p: Pt = d.axa === 0
          ? [(d.de + d.la) / 2, unde] : [unde, (d.de + d.la) / 2];
        const viu = d.valoare.toFixed(2);
        const inchide = () => setCotaAx(null);
        return (
          <CasetaCotei view={view} p={p} pe>
            <input autoFocus value={cotaAx.text ?? viu}
                   inputMode="decimal"
                   // Cifra stă selectată cât n-a tastat nimeni, ca prima tastă
                   // s-o înlocuiască — v. nota de la cotele obiectului.
                   ref={(el) => { if (el && cotaAx.text === null) el.select(); }}
                   onFocus={(e) => e.currentTarget.select()}
                   onChange={(e) => setCotaAx(
                     { j: cotaAx.j, text: e.target.value })}
                   onPointerDown={(e) => e.stopPropagation()}
                   onBlur={inchide}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") {
                       e.preventDefault();
                       scrieCotaAxului(d, Number((cotaAx.text ?? viu)
                         .replace(",", ".")));
                       return;
                     }
                     if (e.key === "Escape") {
                       e.preventDefault();
                       e.currentTarget.blur();
                       inchide();
                     }
                   }} />
            <i>m</i>
          </CasetaCotei>
        );
      })()}

      {view && cotaScrisa && coteObiect[cotaScrisa.i]?.muta && (() => {
        const c = coteObiect[cotaScrisa.i];
        const viu = c.valoare.toFixed(2);
        const p: Pt = punctulCifrei(c);
        const inchide = () => setCotaScrisa(null);
        return (
          <CasetaCotei view={view} p={p} pe>
            <input autoFocus value={cotaScrisa.text ?? viu}
                   inputMode="decimal"
                   // CIFRA ARĂTATĂ STĂ SELECTATĂ CÂT N-A TASTAT NIMENI, ca
                   // prima tastă s-o înlocuiască: cine apasă pe «1,10» și
                   // scrie 0,5 vrea 0,50, nu 1,100,5. `onFocus` singur
                   // n-ajunge — apăsarea care deschide caseta așază pe urmă
                   // cursorul în ea și strânge selecția —, deci se reface la
                   // fiecare desenare, până la prima tastă (de-atunci `text`
                   // nu mai e `null`).
                   ref={(el) => {
                     if (el && cotaScrisa.text === null) el.select();
                   }}
                   onFocus={(e) => e.currentTarget.select()}
                   onChange={(e) => setCotaScrisa(
                     { i: cotaScrisa.i, text: e.target.value })}
                   // APĂSAREA PE CÂMP NU AJUNGE LA PÂNZĂ: fondul stinge
                   // selecția, iar cu ea ar pleca și cota pe care tocmai o
                   // scrii. Caseta e de-acum ALĂTURI de `<svg>`, nu în el,
                   // deci apăsarea oricum n-ar mai ajunge la pânză; oprirea
                   // rămâne fiindcă ea apără înțelesul, nu un drum anume.
                   onPointerDown={(e) => e.stopPropagation()}
                   onBlur={inchide}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") {
                       e.preventDefault();
                       scrieCota(c, Number((cotaScrisa.text ?? viu)
                         .replace(",", ".")));
                       return;
                     }
                     // Escape se oprește AICI, ca la zidul tras: handlerul de
                     // taste al editorului iese din start pe orice `INPUT`,
                     // deci n-ar ajunge nicăieri.
                     if (e.key === "Escape") {
                       e.preventDefault();
                       e.currentTarget.blur();
                       inchide();
                     }
                   }} />
            <i>m</i>
          </CasetaCotei>
        );
      })()}

      {/* CASETA UNEI COTE DE PLANȘĂ. Aceeași poveste ca a cotei obiectului,
          cu cota ținută întreagă: lista desenului nu e a noastră. */}
      {/* CASETA UNEI COTE: cifra, când se poate scrie, și ȘTERGEREA, mereu.
          Se deschidea numai pe cotele care pot muta ceva (`muta`), deci cele
          venite din desen nu se puteau nici măcar atinge — «există cote
          preluate din desene care nu pot fi șterse, toate ar trebui să aibă
          opțiunea să poată fi șterse» (15.10.2026). */}
      {view && cotaPlan && (() => {
        const c = cotaPlan.c;
        const viu = c.valoare.toFixed(2);
        const la = c.la ?? 0.5;
        const p: Pt = [c.a[0] + (c.b[0] - c.a[0]) * la,
                       c.a[1] + (c.b[1] - c.a[1]) * la];
        const inchide = () => setCotaPlan(null);
        return (
          <CasetaCotei view={view} p={p} pe>
            {c.muta ? <input autoFocus value={cotaPlan.text ?? viu}
                   inputMode="decimal"
                   ref={(el) => { if (el && cotaPlan.text === null) el.select(); }}
                   onFocus={(e) => e.currentTarget.select()}
                   onChange={(e) => setCotaPlan({ c, text: e.target.value })}
                   onPointerDown={(e) => e.stopPropagation()}
                   onBlur={inchide}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") {
                       e.preventDefault();
                       setCotaPlan(null);
                       scrieCota(c, Number((cotaPlan.text ?? viu).replace(",", ".")));
                       return;
                     }
                     if (e.key === "Escape") {
                       e.preventDefault();
                       e.currentTarget.blur();
                       inchide();
                     }
                   }} /> : <i>{viu}</i>}
            <i>m</i>
            {/* ȘTERGE COTA. Pusă de mână, iese din document; socotită din
                desen, se trece pe lista stinselor — altfel s-ar naște din nou
                la următoarea desenare (v. `model::stingeCota`).

                NU ȘI PE COTELE DE AȘEZARE ALE UNUI GOL: ele se nasc din
                selecție și pier cu ea, deci n-au ce stinge. Cu «×» pe ele,
                apăsarea scria o cheie în `dims_stinse` și nu se întâmpla
                NIMIC — «works when I press the „Șterge" button but not when I
                press x» (27.10.2026). */}
            {!c.asezare && <button type="button" className="ob-ed-cota-sterge"
                    title={t("editor.cota_sterge")}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setCotaPlan(null);
                      laGestPornit();
                      import("./model").then(({ stingeCota }) => {
                        laSchimbare(stingeCota(doc, c));
                        laGestTerminat();
                      });
                    }}>×</button>}
          </CasetaCotei>
        );
      })()}

      {/* BUTOANELE BUCĂȚII DIN MÂNĂ — «din butoane ȘI taste», cerute amândouă
          (19.10.2026). Tastele sunt scrise pe butoane, nu ascunse într-un
          ajutor: cine apasă o dată cu mâna vede tasta și a doua oară n-o mai
          caută. Stau DEASUPRA bucății, ca să nu cadă sub cursor — acolo unde
          o apăsare ar pune-o în loc s-o rotească. */}
      {view && gest?.fel === "lipeste" && (() => {
        const buton = (titlu: string, tasta: string, ce: () => Bucata) => (
          <button type="button" className="btn" title={`${titlu} (${tasta})`}
                  onPointerDown={(e) => {
                    // OPRIT AICI, altfel apăsarea ajunge la pânză și PUNE
                    // bucata în loc s-o întoarcă — butonul ar fi fost de
                    // nefolosit chiar pentru ce există.
                    e.stopPropagation();
                    e.preventDefault();
                    setGest({ ...gest, b: ce() });
                  }}>{titlu}</button>
        );
        return (
          /* BARA STĂ PE LOC, SUS PE PÂNZĂ — nu deasupra bucății.
             Legată de bucată, ea URMA CURSORUL: cum mișcai mâna spre un buton,
             butonul fugea de sub ea, iar apăsarea cădea pe pânză și PUNEA
             bucata. «I can't click on the options because if I click down it
             places the tronson portion down» (19.10.2026). O bară care se
             mișcă nu poate fi apăsată niciodată, oricâtă grijă ai avea. */
          <div className="ob-ed-lipire">
            {buton(t("editor.lipire.roteste"), "R",
                   () => rotesteBucata(gest.b, 90))}
            {buton(t("editor.lipire.oglinda_h"), "H",
                   () => oglindesteBucata(gest.b, "h"))}
            {buton(t("editor.lipire.oglinda_v"), "V",
                   () => oglindesteBucata(gest.b, "v"))}
            <i>{t("editor.lipire.pune")}</i>
          </div>
        );
      })()}

      {/* LUNGIMEA SE POATE SCRIE cât timp tragi, nu doar citi.
          În Homestyler cifra de lângă cursor e un câmp: tastezi 3.20 și zidul
          se face de 3.20, în loc să încerci să nimerești cu mâna pe o planșă la
          37 px/m, unde un centimetru e o treime de pixel. */}
      {view && cotaVie?.editabil && (() => {
        // CÂMPUL ARATĂ LUNGIMEA CÂT O TRAGI, nu doar cât o tastezi.
        //
        // A fost gol: `value` era numai ce scrisese omul, deci cât ținea
        // tragerea se vedea o casetă albastră fără nicio cifră în ea — adică
        // fix ce nu-i trebuie cuiva care trage un zid. Acum cifra de sub mână e
        // chiar conținutul câmpului, până când cineva tastează; de-atunci
        // rămâne ce a tastat, ca să nu i se rescrie sub degete. Unghiul stă
        // lângă, ca la celelalte cote.
        const viu = cotaVie.unitate === "trepte"
          ? String(Math.round(cotaVie.valoare ?? 0))
          : (cotaVie.valoare ?? 0).toFixed(2);
        return (
          <CasetaCotei view={view} p={cotaVie.p}>
            <input autoFocus value={lungimeScrisa ?? viu}
                   inputMode="decimal"
                   // Tastarea ÎNLOCUIEȘTE cifra arătată, nu se lipește de ea:
                   // cine scrie 3.20 vrea 3,20, nu 1,853,20.
                   onFocus={(e) => e.currentTarget.select()}
                   onChange={(e) => setLungimeScrisa(e.target.value)}
                   onKeyDown={(e) => {
                     if (e.key === "Enter") {
                       e.preventDefault();
                       // ENTER PE SCARĂ, FĂRĂ SĂ FI TASTAT NIMIC, O ÎNCHEIE.
                       //
                       // Câmpul ăsta se deschide singur lângă cursor și ia
                       // focusul, deci Enter ajungea MEREU în el: handlerul de
                       // pe fereastră, care trebuia să încheie scara, iese din
                       // start pe orice `INPUT`. Omul apăsa Enter, numărul de
                       // trepte se fixa, iar scara rămânea agățată de cursor —
                       // «I still can't stop the stair creation without
                       // deleting it» (07.10.2026). Cifra tastată bate: cine a
                       // scris ceva vrea cifra aia, cine n-a scris nimic a
                       // terminat.
                       if (gest?.fel === "scaraNoua" && lungimeScrisa == null
                           && gest.rampe.length) {
                         incheieScara();
                         return;
                       }
                       const v = Number((lungimeScrisa ?? viu)
                         .replace(",", "."));
                       if (Number.isFinite(v) && v > 0) {
                         if (gest?.fel === "scaraNoua") fixeazaScara(v);
                         else fixeazaLungimea(v);
                       }
                       return;
                     }
                     // ESCAPE ANULEAZĂ ZIDUL ÎN LUCRU, și trebuie făcut CHIAR
                     // AICI: câmpul are focus cât se desenează, iar handlerul
                     // de taste al editorului iese din start pe orice `INPUT`
                     // (`Editor.tsx`), deci Escape nu ajungea nicăieri și zidul
                     // rămânea agățat de cursor.
                     //
                     // Doar zidul, nu și unealta: cine a greșit pornirea vrea
                     // de obicei să reînceapă. A doua apăsare nu mai are câmp
                     // în cale, deci ajunge la editor și face ce scrie acolo —
                     // înapoi la săgeată.
                     if (e.key === "Escape") {
                       e.preventDefault();
                       e.currentTarget.blur();
                       setGest(null);
                       setLungimeScrisa(null);
                     }
                   }} />
            <i>{cotaVie.unitate ?? "m"}</i>
            {cotaVie.sub && <b>{cotaVie.sub}</b>}
          </CasetaCotei>
        );
      })()}

      {/* FĂRĂ BARA DE ZOOM (Stefan, 24.09.2026: «scoate bara aia de zoom și din
          editorul normal de etaj și din editorul de tronsoane»). Mărirea e a
          rotiței și a tastelor (v. `useGesturi`). */}
      <ReperXY />
    </div>
  );
}
